 import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// ─── SHARED DATA ─────────────────────────────────────────────────────────────
const MACHINES = ["Machine 1", "Machine 2", "Machine 3", "Machine 4"];
const CELLS = { "Machine 1": "Cell A", "Machine 2": "Cell A", "Machine 3": "Cell A", "Machine 4": "Cell B" };
// Machines grouped by their cell, preserving MACHINES order.
const MACHINES_BY_CELL = MACHINES.reduce((acc, m) => { (acc[CELLS[m]] = acc[CELLS[m]] || []).push(m); return acc; }, {});

// An operation's assigned machines as an array. Back-compat: an op may still carry the
// legacy single `machine` field, so fall back to it when `machines` is absent.
function opMachines(op) {
  if (Array.isArray(op.machines)) return op.machines;
  return op.machine ? [op.machine] : [];
}
// The machines an op was imported with from the CSV (its default). Absent in the seed
// data, so it defaults to "unassigned" — any machine the planner assigns then counts
// as an override of the CSV value.
function csvMachines(op) {
  return Array.isArray(op.csvMachines) ? op.csvMachines : [];
}
// True when the op's current machine assignment differs from its CSV default.
function machinesOverridden(op) {
  const csv = new Set(csvMachines(op));
  const cur = opMachines(op);
  return cur.length !== csv.size || cur.some(m => !csv.has(m));
}
// True when the op's current est run time differs from its CSV default. The baseline
// (op.csvEstHours) is captured on the first edit, so this is false until then.
function estOverridden(op) {
  return op.csvEstHours != null && Math.abs((op.estHours || 0) - op.csvEstHours) > 1e-9;
}
// Human label for a set of assigned machines — a fully-selected cell collapses to its name
// (e.g. ["Machine 1","Machine 2","Machine 3"] → "Cell A"). Returns null when nothing is assigned.
function machineAssignLabel(machines) {
  if (!machines || machines.length === 0) return null;
  const sel = new Set(machines);
  const parts = [];
  for (const [cell, ms] of Object.entries(MACHINES_BY_CELL)) {
    const inCell = ms.filter(m => sel.has(m));
    if (!inCell.length) continue;
    if (inCell.length === ms.length) parts.push(cell);   // entire cell selected
    else parts.push(...inCell);
  }
  if (!parts.length) return null;
  return parts.length <= 2 ? parts.join(", ") : `${machines.length} machines`;
}

const INITIAL_WORK_ORDERS = [
  {
    id: "A0001", product: "PT-1", description: "Lorem Ipsum product description",
    dueDate: "2026-12-01", targetQty: 100,
    operations: [
      // Q3 flow tracks two independent statuses: `planStatus` (Not planned | Planned) driven by
      // scheduling, and `floorStatus` (Not started | In progress | Done) driven on the shopfloor.
      // The other flows keep using the single `status` field.
      { id: "OP10", label: "OP 10", machine: null, estRunTime: "10h", estHours: 10, plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
      { id: "OP20", label: "OP 20", machine: null, estRunTime: "62h", estHours: 62, plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
      { id: "OP30", label: "OP 30", machine: null, estRunTime: null,  estHours: 8,  plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
    ],
  },
  {
    // Same product line as A0001 but due sooner — its OP 10 should sort ahead of A0001's
    // OP 10 on the tablet (same OP number, earlier due date).
    id: "A0002", product: "PT-1", description: "Lorem Ipsum product description",
    dueDate: "2026-11-15", targetQty: 50,
    operations: [
      { id: "OP10", label: "OP 10", machine: null, estRunTime: "12h", estHours: 12, plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
      { id: "OP20", label: "OP 20", machine: null, estRunTime: "30h", estHours: 30, plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
      { id: "OP30", label: "OP 30", machine: null, estRunTime: "5h",  estHours: 5,  plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned", floorStatus: "Not started", idealCell: "Cell A" },
    ],
  },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    + ", " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function addHours(dateStr, hour, estHours) {
  // Build date in local time to avoid UTC/local mismatch on comparison.
  // `hour` may be fractional (e.g. 6.25 = 06:15) — split into hours + minutes
  // since the Date constructor truncates a fractional hour argument.
  const [y, m, day] = dateStr.split("-").map(Number);
  const wholeH = Math.floor(hour);
  const min = Math.round((hour - wholeH) * 60);
  const d = new Date(y, m - 1, day, wholeH, min, 0, 0);
  return { start: d.toISOString(), end: new Date(d.getTime() + estHours * 3600000).toISOString() };
}
function getWeekDays(startDate) {
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(startDate); d.setDate(d.getDate() + i); return d; });
}
function getMonday(d) {
  const date = new Date(d); const day = date.getDay();
  date.setDate(date.getDate() - day + (day === 0 ? -6 : 1)); date.setHours(0,0,0,0); return date;
}
function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function defaultWindowForDate(date) {
  const dow = date.getDay();
  return (dow === 0 || dow === 6) ? null : { startH: 8, endH: 16 };
}
function genId() { return Math.random().toString(36).slice(2, 10); }
function appendWindow(prev, machine, dateKey, newWin) {
  const existing = prev[machine]?.[dateKey];
  const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
  return [...list, newWin].sort((a, b) => a.startH - b.startH);
}

const STATUS_COLORS = {
  "Not planned": { bg: "#f8c8cc", text: "#000" },
  "In planning": { bg: "#fff0c2", text: "#8a6d00" },
  "Planned":     { bg: "#e4f0ff", text: "#1a6bbf" },
  "Not started": { bg: "#ececec", text: "#555" },
  "In progress": { bg: "#c8d2f4", text: "#1a3098" },
  "Done":        { bg: "#a0e6bb", text: "#146c2e" },
};
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => i);
const HR_OPTS   = Array.from({ length: 25 }, (_, i) => i);
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const DOW_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const DOWNTIME_REASONS = ["Maintenance", "Cleaning", "Handover", "Setup", "Tool change", "Break", "Other"];
const MACHINE_COL_W = 110;
const SHIFT_LABEL_H = 20;
const WIN_LABEL_H = 20;
const WIN_BAR_H   = 8;
const WIN_ROW_H = SHIFT_LABEL_H + WIN_LABEL_H + WIN_BAR_H + 2; // 50
const OPS_ROW_H = 38;
const LANE_H = 34; // height of one stacked-job lane in the planning ops row
const PANEL_W = 248;

const SHIFT_CATEGORIES = ["Day", "Afternoon", "Night", "Overtime"];

function hh(h) { return String(h).padStart(2,"0") + ":00"; }
// Format a fractional hour (e.g. 6.25) as HH:MM (e.g. "06:15").
function hhmm(h) {
  const total = Math.round(h * 60);
  const hr = Math.floor(total / 60) % 24;
  const mn = total % 60;
  return String(hr).padStart(2,"0") + ":" + String(mn).padStart(2,"0");
}
// Drag-snap granularity on the timeline: 15 minutes.
const SNAP_H = 0.25;
// Format a duration in hours (possibly fractional) as "10h 30m" / "8h" / "45m".
// Used for every duration display so minutes are always shown when present.
function fmtDur(h) {
  const totalMin = Math.round((h || 0) * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  if (hh && mm) return `${hh}h ${mm}m`;
  if (hh) return `${hh}h`;
  return `${mm}m`;
}
// Back-compat alias — same minute-aware format, kept for existing callers.
const fmtRunTime = fmtDur;

// Material Symbols Sharp inline icon. Pass the icon name as `name` (e.g. "settings").
function MIcon({ name, size = 20, weight = 500, color, style }) {
  return (
    <span style={{
      fontFamily: '"Material Symbols Sharp"',
      fontWeight: weight,
      fontStyle: "normal",
      fontSize: size,
      lineHeight: 1,
      display: "inline-block",
      fontVariationSettings: `'FILL' 0, 'wght' ${weight}, 'GRAD' 0, 'opsz' ${Math.min(48, Math.max(20, size))}`,
      userSelect: "none",
      color,
      ...style,
    }}>{name}</span>
  );
}

// ISO week number (Mon–Sun weeks; week 1 contains Jan 4).
function getISOWeek(d) {
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const week1 = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// Roll up a work order's status from its operations. Highest stage wins.
function rollupWoStatus(ops) {
  if (!ops.length) return "Not planned";
  if (ops.every(o => o.status === "Done")) return "Done";
  if (ops.some(o => o.status === "In progress")) return "In progress";
  if (ops.some(o => o.status === "Not started")) return "Not started";
  if (ops.some(o => o.status === "Planned")) return "Planned";
  return "Not planned";
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS["Not planned"];
  return (
    <span style={{
      background: c.bg, color: c.text,
      borderRadius: 100,
      padding: small ? "2px 10px" : "3px 12px",
      fontSize: small ? 11 : 12, fontWeight: 600,
      whiteSpace: "nowrap",
      fontFamily: '"Inter", sans-serif',
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      {status === "Done" && <MIcon name="check" size={small ? 12 : 14} color={c.text} weight={600} />}
      {status}
    </span>
  );
}

// Q3 splits an operation's progress into two independent tracks.
// Planning track: dragging onto the calendar marks an op "In planning" (shown as "Planning");
// the planner then confirms it as "Planned" (shown as "Ready"), which prioritises it on the
// operator tablet. The full ordering is reflected in the dropdown and on the tablet.
const PLAN_STATUSES  = ["Not planned", "In planning", "Scheduled", "Planned"];
const FLOOR_STATUSES = ["Not started", "In progress", "Done"];

// Q3 status pill styling (from the Q3 design). Shopfloor statuses are filled with no border;
// planning statuses carry a border. All text is black.
const Q3_STATUS_STYLE = {
  "Not started": { bg: "#d9d9d9", border: "transparent" },
  "In progress": { bg: "#b8cdfd", border: "transparent" },
  "Done":        { bg: "#b2e9d2", border: "transparent" },
  "Not planned": { bg: "#efefef", border: "#d9d9d9" },
  "In planning": { bg: "#f4e89a", border: "#d2ba20" },
  "Scheduled":   { bg: "#e8efff", border: "#195dfa" },
  "Planned":     { bg: "#b2e9d2", border: "#05a764" },
};
// Display labels for Q3 statuses. Internal values stay as-is (used in sorting/rollups/colors);
// only the visible text changes — "Planned" shows as "Ready".
const q3StatusLabel = (s) => s === "In planning" ? "Planning" : s === "Planned" ? "Ready" : s;

// A Q3 status pill. `caret` renders the dropdown arrow inside the pill (right side).
function Q3StatusPill({ status, small, caret }) {
  const s = Q3_STATUS_STYLE[status] || Q3_STATUS_STYLE["Not planned"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: s.bg, border: `1px solid ${s.border}`, color: "#000",
      borderRadius: 60, height: 26, padding: small ? "0 10px" : "0 12px",
      fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 600, letterSpacing: "0.12px",
      whiteSpace: "nowrap", boxSizing: "border-box",
    }}>
      {q3StatusLabel(status)}
      {caret && <MIcon name="expand_more" size={14} color="#000" />}
    </span>
  );
}

// ─── STATUS DROPDOWN (Q3 work order page) ─────────────────────────────────────
// An inline, editable status pill. Click to reveal the option list; pick one to commit.
function StatusDropdown({ value, options, onChange, small }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
          display: "inline-flex", alignItems: "center", fontFamily: "inherit" }}>
        <Q3StatusPill status={value} small={small} caret />
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 60, background: "#fff",
          border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
          padding: 4, minWidth: 150 }}>
          {options.map(opt => (
            <button key={opt}
              onClick={(e) => { e.stopPropagation(); onChange(opt); setOpen(false); }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                background: "transparent", border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer" }}>
              <Q3StatusPill status={opt} small />
              {opt === value && <MIcon name="check" size={16} color="#7053ea" style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// Multi-select machine/cell assignment used on the Work bookings (Q3) page.
// Rules: a machine may be assigned to many ops (no cross-op exclusivity), and a single
// op may take several machines or an entire cell at once. `selected` is an array of
// machine names; `onChange` receives the next array.
// Dark tooltip bubble with a downward-pointing arrow. Render inside a
// position:relative (and NOT overflow:hidden) wrapper; it centers above it.
// `dismiss` adds an underlined Dismiss link (currently non-functional).
function HoverTip({ text, width = 200, dismiss }) {
  return (
    <span style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
      background: "#1c1c1c", color: "#fff", padding: "12px", borderRadius: 8,
      fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", fontWeight: 400,
      whiteSpace: "normal", width: "max-content", maxWidth: width, wordBreak: "break-word",
      textAlign: "left", zIndex: 200, pointerEvents: dismiss ? "auto" : "none", boxShadow: "0 4px 16px rgba(0,0,0,0.22)" }}>
      {text}
      {dismiss && (
        <div style={{ marginTop: 12 }}>
          <span style={{ textDecoration: "underline", cursor: "pointer" }}>Dismiss</span>
        </div>
      )}
      <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
        width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent",
        borderTop: "6px solid #1c1c1c" }} />
    </span>
  );
}

function MachineAssignDropdown({ selected, onChange, woId, opLabel, overridden }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [nameTip, setNameTip] = useState(false);
  const [iconTip, setIconTip] = useState(false);
  const [dotTip, setDotTip] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);          // "Change to" dropdown
  const [picks, setPicks] = useState(selected);          // machines chosen in the modal
  const [query, setQuery] = useState("");                // dropdown search
  const [collapsed, setCollapsed] = useState(new Set()); // collapsed cells
  const [placement, setPlacement] = useState("bottom");  // dropdown flip direction
  const nameRef = useRef(null);
  const ddRef = useRef(null);
  const fieldRef = useRef(null);
  const label = machineAssignLabel(selected);

  // Close the "Change to" dropdown on outside click (but leave the modal open).
  useEffect(() => {
    if (!ddOpen) return;
    const h = e => { if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ddOpen]);

  // Flip the dropdown above / to the side when there isn't room below.
  useLayoutEffect(() => {
    if (!ddOpen) return;
    const place = () => {
      const el = fieldRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const MENU_H = 380, MENU_W = 260;
      const below = window.innerHeight - r.bottom, above = r.top;
      const right = window.innerWidth - r.right, left = r.left;
      if (below >= MENU_H) setPlacement("bottom");
      else if (above >= MENU_H) setPlacement("top");
      else if (right >= MENU_W) setPlacement("right");
      else if (left >= MENU_W) setPlacement("left");
      else setPlacement(below >= above ? "bottom" : "top");
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [ddOpen]);
  const menuPos = {
    bottom: { top: "calc(100% + 4px)", left: 0 },
    top: { bottom: "calc(100% + 4px)", left: 0 },
    right: { left: "calc(100% + 4px)", top: 0 },
    left: { right: "calc(100% + 4px)", top: 0 },
  }[placement];

  function openModal(e) {
    e.stopPropagation();
    setPicks(selected);
    setQuery("");
    setCollapsed(new Set());
    setDdOpen(false);
    setModalOpen(true);
  }
  function confirm() { onChange(picks); setModalOpen(false); }

  const pickSet = new Set(picks);
  const toggleMachine = m => setPicks(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);
  const toggleCell = ms => {
    const all = ms.every(m => pickSet.has(m));
    setPicks(p => all ? p.filter(x => !ms.includes(x)) : [...new Set([...p, ...ms])]);
  };
  const toggleCollapse = cell => setCollapsed(s => { const n = new Set(s); n.has(cell) ? n.delete(cell) : n.add(cell); return n; });

  const opNum = (opLabel || "").replace(/^OP\s*/i, "");
  const currentLabel = machineAssignLabel(selected) || "Unassigned";
  const changeToLabel = machineAssignLabel(picks);
  const sectionLabel = { fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 600, lineHeight: "18px",
    letterSpacing: "0.12px", color: "#000", margin: 0 };
  const q = query.trim().toLowerCase();
  const Check = ({ on }) => (
    <span style={{ width: 20, height: 20, borderRadius: 2, flexShrink: 0,
      border: on ? "none" : "1px solid #767676", background: on ? "#7053ea" : "transparent",
      display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      {on && <MIcon name="check" size={14} color="#fff" />}
    </span>
  );

  return (
    <span style={{ position: "relative", display: "block", width: "100%" }}>
      {/* ── Trigger: machine name + hover edit icon ── */}
      <span onClick={openModal}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        style={{ display: "flex", alignItems: "center", gap: 6, height: 28, width: "100%", cursor: "pointer",
          fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 500,
          color: label ? "#181233" : "#999" }}>
        <span style={{ position: "relative", minWidth: 0, flex: "1 1 auto", display: "inline-flex" }}>
          <span ref={nameRef}
            onMouseEnter={() => { const el = nameRef.current; setNameTip(!!el && el.scrollWidth > el.clientWidth); }}
            onMouseLeave={() => setNameTip(false)}
            style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label || "Assign"}</span>
          {nameTip && label && <HoverTip text={label} />}
        </span>
        {overridden && (
          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
            onMouseEnter={() => setDotTip(true)} onMouseLeave={() => setDotTip(false)}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2f6bff" }} />
            {dotTip && <HoverTip width={260}
              text="This value was changed in FourJaw and no longer matches your CSV. Update your CSV to keep them in sync." />}
          </span>
        )}
        {(hover || modalOpen) && (
          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
            onMouseEnter={() => setIconTip(true)} onMouseLeave={() => setIconTip(false)}>
            <span style={{ width: 24, height: 24, background: "#ececec",
              display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="edit" size={16} color="#000" />
            </span>
            {iconTip && <HoverTip text="Edit" />}
          </span>
        )}
      </span>

      {/* ── Assign-machine modal (clicking outside does NOT close it) ── */}
      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 500,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 570, maxWidth: "calc(100vw - 32px)", background: "#fff", borderRadius: 0,
              display: "flex", flexDirection: "column", overflow: "visible", boxShadow: "0 12px 48px rgba(0,0,0,0.28)",
              fontFamily: '"Inter", sans-serif' }}>
            {/* Header */}
            <div style={{ height: 60, flexShrink: 0, borderBottom: "1px solid #d9d9d9", display: "flex",
              alignItems: "center", justifyContent: "space-between", padding: "0 16px" }}>
              <span style={{ fontSize: 20, fontWeight: 600, lineHeight: "28px", letterSpacing: "-0.2px", color: "#000" }}>
                Assign machine
              </span>
              <button onClick={() => setModalOpen(false)}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "inline-flex" }}>
                <MIcon name="close" size={24} color="#000" />
              </button>
            </div>

            {/* Work-order context row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 32px", flexShrink: 0 }}>
              <MIcon name="description" size={16} color="#000" />
              <span style={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>Work order</span>
              <span style={{ fontSize: 14, fontWeight: 400, lineHeight: "20px", letterSpacing: "0.14px", color: "#767676" }}>{woId}</span>
              {opNum && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2, background: "#ececec",
                  borderRadius: 33, padding: "4px 8px", letterSpacing: "0.12px" }}>
                  <span style={{ fontSize: 12, fontWeight: 400, lineHeight: "18px", color: "#767676" }}>OP</span>
                  <span style={{ fontSize: 12, fontWeight: 600, lineHeight: "18px", color: "#000" }}>{opNum}</span>
                </span>
              )}
            </div>

            {/* Content */}
            <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: "0 32px 24px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <p style={sectionLabel}>Current</p>
                <p style={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: "20px",
                  letterSpacing: "0.14px", color: "#767676", margin: 0 }}>{currentLabel}</p>
              </div>

              <div ref={ddRef} style={{ width: 260, position: "relative", display: "flex", flexDirection: "column", gap: 6 }}>
                <p style={sectionLabel}>Change to</p>
                <button ref={fieldRef} onClick={() => setDdOpen(o => !o)}
                  style={{ height: 40, width: "100%", background: "#efefef", border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 12px",
                    fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: "20px",
                    letterSpacing: "0.14px", color: changeToLabel ? "#000" : "#767676", textAlign: "left" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{changeToLabel || "Select machine"}</span>
                  <MIcon name="expand_more" size={16} color="#000" />
                </button>
                {ddOpen && (
                  <div style={{ position: "absolute", ...menuPos, width: "100%", zIndex: 10,
                    background: "#fff", border: "1px solid #d9d9d9", display: "flex", flexDirection: "column",
                    boxShadow: "0 4px 30px rgba(0,0,0,0.2)" }}>
                    {/* Search */}
                    <div style={{ padding: "16px 16px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, background: "#efefef", padding: "8px 12px" }}>
                        <MIcon name="search" size={20} color="#767676" />
                        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search"
                          style={{ border: "none", outline: "none", background: "transparent", width: "100%",
                            fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px", color: "#000" }} />
                      </div>
                    </div>
                    {/* Clear */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end",
                      borderBottom: "1px solid #d9d9d9", padding: "8px 16px" }}>
                      <button onClick={() => setPicks([])}
                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0,
                          fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px",
                          color: "#000", textDecoration: "underline" }}>
                        Clear
                      </button>
                    </div>
                    {/* Cell groups */}
                    <div style={{ maxHeight: 280, overflowY: "auto", paddingBottom: 8 }}>
                      {Object.entries(MACHINES_BY_CELL).map(([cell, ms]) => {
                        const visible = q ? ms.filter(m => m.toLowerCase().includes(q)) : ms;
                        if (!visible.length) return null;
                        const isCollapsed = collapsed.has(cell);
                        const allOn = visible.every(m => pickSet.has(m));
                        const itemRow = { display: "flex", alignItems: "center", gap: 8, height: 40, width: "100%",
                          textAlign: "left", background: "transparent", border: "none", cursor: "pointer",
                          fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px", color: "#000" };
                        return (
                          <div key={cell}>
                            {/* Parent (cell) header */}
                            <button onClick={() => toggleCollapse(cell)}
                              style={{ ...itemRow, fontWeight: 600, padding: "0 16px", gap: 12 }}>
                              <span>{cell}</span>
                              <MIcon name="expand_more" size={16} color="#000"
                                style={{ transform: isCollapsed ? "none" : "rotate(180deg)" }} />
                            </button>
                            {!isCollapsed && (
                              <>
                                {/* Select all (cell) */}
                                <button onClick={() => toggleCell(visible)} style={{ ...itemRow, padding: "0 16px 0 32px" }}>
                                  <Check on={allOn} />
                                  <span>Select all</span>
                                </button>
                                {/* Machines */}
                                {visible.map(m => (
                                  <button key={m} onClick={() => toggleMachine(m)} style={{ ...itemRow, padding: "0 16px 0 32px" }}>
                                    <Check on={pickSet.has(m)} />
                                    <span>{m}</span>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <p style={{ fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 400, lineHeight: "18px",
                letterSpacing: "0.12px", color: "#767676", margin: 0 }}>
                Once saved, updates will reflect on the tablet automatically. For in-progress work orders, inform the operator before making changes.
              </p>
            </div>

            {/* Footer */}
            <div style={{ height: 60, flexShrink: 0, borderTop: "1px solid #d9d9d9", display: "flex",
              alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "0 16px" }}>
              <button onClick={() => setModalOpen(false)}
                style={{ height: 40, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer",
                  padding: "12px 16px", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
                Cancel
              </button>
              <button onClick={confirm}
                style={{ height: 40, border: "none", background: "#7053ea", cursor: "pointer",
                  padding: "12px 16px", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#fff" }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

// Inline est-run-time editor: plain text + hover edit icon that opens an "Est. run time"
// modal with hours/minutes steppers. Mirrors MachineAssignDropdown's edit pattern.
function EstRunTimeEdit({ hours, onChange, woId, opLabel, overridden }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [iconTip, setIconTip] = useState(false);
  const [dotTip, setDotTip] = useState(false);
  const [h, setH] = useState(0);
  const [m, setM] = useState(0);

  function openModal(e) {
    e.stopPropagation();
    const totalMin = Math.round((hours || 0) * 60);
    setH(Math.floor(totalMin / 60));
    setM(totalMin % 60);
    setModalOpen(true);
  }
  function confirm() {
    const total = Math.max(0, (parseInt(h, 10) || 0) + (parseInt(m, 10) || 0) / 60);
    onChange(Math.round(total * 60) / 60);
    setModalOpen(false);
  }

  const opNum = (opLabel || "").replace(/^OP\s*/i, "");
  const fmtHM = (hrs) => {
    const t = Math.round((hrs || 0) * 60), hh = Math.floor(t / 60), mm = t % 60;
    return mm ? `${hh} h ${mm} m` : `${hh} h`;
  };
  const sectionLabel = { fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 600, lineHeight: "18px",
    letterSpacing: "0.12px", color: "#000", margin: 0 };

  // 72px number field with up/down steppers (h: ≥0, m: 0–59).
  const NumberField = ({ value, setValue, max }) => {
    const step = (d) => setValue(v => {
      let n = (parseInt(v, 10) || 0) + d;
      if (n < 0) n = 0;
      if (max != null && n > max) n = max;
      return n;
    });
    return (
      <div style={{ position: "relative", width: 72, height: 40, background: "#efefef",
        display: "flex", alignItems: "center", padding: "0 12px" }}>
        <input type="text" inputMode="numeric" value={value}
          onChange={e => { let n = parseInt(e.target.value.replace(/\D/g, ""), 10); if (isNaN(n)) n = 0; if (n < 0) n = 0; if (max != null && n > max) n = max; setValue(n); }}
          style={{ border: "none", background: "transparent", outline: "none", width: "100%",
            fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px", color: "#000" }} />
        <span style={{ position: "absolute", right: 4, top: 0, height: "100%", display: "flex",
          flexDirection: "column", justifyContent: "center" }}>
          <button onClick={() => step(1)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, height: 12, display: "flex", alignItems: "center" }}>
            <MIcon name="arrow_drop_up" size={14} color="#000" />
          </button>
          <button onClick={() => step(-1)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, height: 12, display: "flex", alignItems: "center" }}>
            <MIcon name="arrow_drop_down" size={14} color="#000" />
          </button>
        </span>
      </div>
    );
  };

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {/* Trigger: est value + hover edit icon */}
      <span onClick={openModal}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, cursor: "pointer", whiteSpace: "nowrap" }}>
        <span>{fmtDur(hours)}</span>
        {overridden && (
          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
            onMouseEnter={() => setDotTip(true)} onMouseLeave={() => setDotTip(false)}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2f6bff" }} />
            {dotTip && <HoverTip width={260}
              text="This value was changed in FourJaw and no longer matches your CSV. Update your CSV to keep them in sync." />}
          </span>
        )}
        {(hover || modalOpen) && (
          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
            onMouseEnter={() => setIconTip(true)} onMouseLeave={() => setIconTip(false)}>
            <span style={{ width: 24, height: 24, background: "#ececec",
              display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="edit" size={16} color="#000" />
            </span>
            {iconTip && <HoverTip text="Edit" />}
          </span>
        )}
      </span>

      {/* Est. run time modal (clicking outside does NOT close it) */}
      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 500,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 570, maxWidth: "calc(100vw - 32px)", background: "#fff", borderRadius: 0,
              display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.28)",
              fontFamily: '"Inter", sans-serif', textAlign: "left", whiteSpace: "normal" }}>
            {/* Header */}
            <div style={{ height: 60, flexShrink: 0, borderBottom: "1px solid #d9d9d9", display: "flex",
              alignItems: "center", justifyContent: "space-between", padding: "0 16px" }}>
              <span style={{ fontSize: 20, fontWeight: 600, lineHeight: "28px", letterSpacing: "-0.2px", color: "#000" }}>
                Est. run time
              </span>
              <button onClick={() => setModalOpen(false)}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "inline-flex" }}>
                <MIcon name="close" size={24} color="#000" />
              </button>
            </div>

            {/* Work-order context row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 32px", flexShrink: 0 }}>
              <MIcon name="description" size={16} color="#000" />
              <span style={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>Work order</span>
              <span style={{ fontSize: 14, fontWeight: 400, lineHeight: "20px", letterSpacing: "0.14px", color: "#767676" }}>{woId}</span>
              {opNum && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2, background: "#ececec",
                  borderRadius: 33, padding: "4px 8px", letterSpacing: "0.12px" }}>
                  <span style={{ fontSize: 12, fontWeight: 400, lineHeight: "18px", color: "#767676" }}>OP</span>
                  <span style={{ fontSize: 12, fontWeight: 600, lineHeight: "18px", color: "#000" }}>{opNum}</span>
                </span>
              )}
            </div>

            {/* Content */}
            <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: "0 32px 24px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <p style={sectionLabel}>Current</p>
                <p style={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: "20px",
                  letterSpacing: "0.14px", color: "#767676", margin: 0 }}>{fmtHM(hours)}</p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <p style={sectionLabel}>Change to</p>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <NumberField value={h} setValue={setH} />
                    <span style={{ fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px", color: "#000" }}>h</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <NumberField value={m} setValue={setM} max={59} />
                    <span style={{ fontSize: 14, lineHeight: "20px", letterSpacing: "0.14px", color: "#000" }}>m</span>
                  </div>
                </div>
              </div>

              <p style={{ fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 400, lineHeight: "18px",
                letterSpacing: "0.12px", color: "#767676", margin: 0 }}>
                Once confirmed, updates will reflect on the tablet automatically. For in-progress work orders, inform the operator before making changes.
              </p>
            </div>

            {/* Footer */}
            <div style={{ height: 60, flexShrink: 0, borderTop: "1px solid #d9d9d9", display: "flex",
              alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "0 16px" }}>
              <button onClick={() => setModalOpen(false)}
                style={{ height: 40, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer",
                  padding: "12px 16px", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
                Cancel
              </button>
              <button onClick={confirm}
                style={{ height: 40, border: "none", background: "#7053ea", cursor: "pointer",
                  padding: "12px 16px", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#fff" }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

// ─── ADD / EDIT DOWNTIME MODAL ───────────────────────────────────────────────
function AddDowntimeModal({ machines, win, currentNetH, existing, onClose, onSave }) {
  const isEdit = !!existing;
  // Use "Other" as the dropdown key when reasonKey says Other; else the reason itself.
  const initialReasonKey = existing?.reasonKey || (DOWNTIME_REASONS.includes(existing?.reason) ? existing.reason : "");
  const [reasonKey, setReasonKey] = useState(initialReasonKey);
  const [customReason, setCustomReason] = useState(initialReasonKey === "Other" ? existing?.reason || "" : "");
  const [mode, setMode] = useState(existing?.mode || "duration");
  const [durationH, setDurationH] = useState(existing?.durationH ?? 1);
  const [startH, setStartH] = useState(existing?.startH ?? win.startH);
  const [endH, setEndH] = useState(existing?.endH ?? Math.min(win.endH, win.startH + 1));
  const [deduct, setDeduct] = useState(existing?.deduct ?? true);

  function reset() {
    setReasonKey(""); setCustomReason(""); setMode("duration");
    setDurationH(1); setStartH(win.startH); setEndH(Math.min(win.endH, win.startH + 1));
    setDeduct(true);
  }

  const windowSpan = win.endH - win.startH;
  const reasonValid = reasonKey && (reasonKey !== "Other" || customReason.trim().length > 0);
  const timeValid = mode === "duration"
    ? durationH > 0 && durationH <= windowSpan
    : endH > startH && startH >= win.startH && endH <= win.endH;
  const valid = reasonValid && timeValid;

  function build() {
    return {
      id: existing?.id || genId(),
      reasonKey,
      reason: reasonKey === "Other" ? customReason.trim() : reasonKey,
      mode,
      ...(mode === "exact" ? { startH, endH } : { durationH: Number(durationH) }),
      deduct,
    };
  }
  function handleSave(andAddNew) {
    onSave(build(), andAddNew);
    if (andAddNew) reset();
  }

  const S = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" },
    modal: { background: "#fff", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", width: 620, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", fontFamily: "sans-serif" },
    label: { fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6, display: "block" },
    select: { fontSize: 13, padding: "9px 10px", borderRadius: 6, border: "1px solid #e0e0e0", background: "#f5f5f5", width: "100%" },
    btnPrimary: { padding: "10px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", background: "#7c3aed", color: "#fff" },
    btnSecondary: { padding: "10px 18px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer", color: "#222", fontWeight: 600 },
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px 14px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{isEdit ? "Edit downtime" : "Add downtime"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#999", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "18px 24px 24px" }}>
          <div style={{ fontSize: 13, color: "#444", marginBottom: 22, lineHeight: 1.5 }}>
            Add planned downtime activities like handovers, cleaning, and breaks to keep your schedule visible and accurate.
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={S.label}>Machines</div>
            <div style={{ fontSize: 13, color: "#333" }}>{machines.join(", ")}</div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={S.label}>Downtime reason</label>
            <select value={reasonKey} onChange={e => setReasonKey(e.target.value)} style={S.select}>
              <option value="">Select</option>
              {DOWNTIME_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {reasonKey === "Other" && (
              <input type="text" placeholder="Describe the downtime" value={customReason} onChange={e => setCustomReason(e.target.value)}
                style={{ ...S.select, marginTop: 8, background: "#fff" }} />
            )}
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={S.label}>Time</label>
            <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: "1px solid #eee", cursor: "pointer" }}>
                <input type="radio" checked={mode === "duration"} onChange={() => setMode("duration")} style={{ accentColor: "#7c3aed" }} />
                <span style={{ flex: 1, fontSize: 13, color: "#222" }}>I don't know exact time, set by Est. duration</span>
                {mode === "duration" && (
                  <>
                    <input type="number" min={0.5} step={0.5} max={windowSpan} value={durationH}
                      onChange={e => setDurationH(Number(e.target.value))}
                      style={{ width: 80, padding: "6px 8px", borderRadius: 4, border: "1px solid #ddd", fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: "#555" }}>h</span>
                  </>
                )}
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer" }}>
                <input type="radio" checked={mode === "exact"} onChange={() => setMode("exact")} style={{ accentColor: "#7c3aed" }} />
                <span style={{ flex: 1, fontSize: 13, color: "#222" }}>Set by exact start and end time</span>
                {mode === "exact" && (
                  <>
                    <select value={startH} onChange={e => setStartH(Number(e.target.value))}
                      style={{ width: 90, padding: "6px 8px", borderRadius: 4, border: "1px solid #ddd", fontSize: 13, background: "#fff" }}>
                      {HR_OPTS.filter(h => h >= win.startH && h < win.endH).map(h => <option key={h} value={h}>{hh(h)}</option>)}
                    </select>
                    <span style={{ color: "#999" }}>→</span>
                    <select value={endH} onChange={e => setEndH(Number(e.target.value))}
                      style={{ width: 90, padding: "6px 8px", borderRadius: 4, border: "1px solid #ddd", fontSize: 13, background: "#fff" }}>
                      {HR_OPTS.filter(h => h > win.startH && h <= win.endH).map(h => <option key={h} value={h}>{hh(h)}</option>)}
                    </select>
                  </>
                )}
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={deduct} onChange={e => setDeduct(e.target.checked)}
                style={{ marginTop: 3, accentColor: "#7c3aed", width: 16, height: 16 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>Deduct from planned production time</div>
                <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>Current planned production time: {fmtDur(currentNetH)}</div>
              </div>
            </label>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px 18px", borderTop: "1px solid #eee", gap: 8 }}>
          <button onClick={onClose} style={S.btnSecondary}>Cancel</button>
          <div style={{ display: "flex", gap: 8 }}>
            {!isEdit && (
              <button onClick={() => handleSave(true)} disabled={!valid}
                style={{ ...S.btnSecondary, opacity: valid ? 1 : 0.5, cursor: valid ? "pointer" : "default" }}>
                Save and add new downtime event
              </button>
            )}
            <button onClick={() => handleSave(false)} disabled={!valid}
              style={{ ...S.btnPrimary, opacity: valid ? 1 : 0.5, cursor: valid ? "pointer" : "default" }}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SHIFT POPOVER (view + edit + delete a shift) ───────────────────────────
function ShiftPopover({ date, shift, anchorRect, onClose, onSave, onDelete }) {
  const popRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [category, setCategory] = useState(shift.category);
  const [startH, setStartH] = useState(shift.startH);
  const [endH, setEndH] = useState(shift.endH);

  useEffect(() => {
    function down(e) { if (popRef.current && !popRef.current.contains(e.target)) onClose(); }
    function scroll() { onClose(); }
    document.addEventListener("mousedown", down);
    window.addEventListener("scroll", scroll, true);
    return () => { document.removeEventListener("mousedown", down); window.removeEventListener("scroll", scroll, true); };
  }, [onClose]);

  const totalH = endH - startH;
  const dayLabel = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  const recurringLabel = shift.recurring ? `Weekly, ${shift.recurring.days.map(d => DOW_LABELS[d]).join(", ")}` : null;

  const top  = Math.min((anchorRect?.bottom ?? 0) + 6, (window.innerHeight || 800) - 260);
  const left = Math.min(Math.max(8, anchorRect?.left ?? 0), (window.innerWidth || 1200) - 320);

  function handleSave() { if (endH > startH) onSave({ category, startH, endH }); }

  const labelStyle = { fontSize: 10, fontWeight: 600, color: "#555", display: "block", marginBottom: 3 };

  return (
    <div ref={popRef}
      style={{ position: "fixed", top, left, minWidth: 300, background: "#fff", border: "1px solid #e5e5e5",
        borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.18)", zIndex: 401, padding: 14, fontFamily: '"Inter", sans-serif' }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editing ? 10 : 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>Shift</span>
          <span style={{ background: "#f5f5f5", color: "#181233", border: "1px solid #ececec",
            borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>
            {editing ? category : shift.category}
          </span>
          {!editing && (
            <button onClick={() => setEditing(true)} title="Edit"
              style={{ background: "none", border: "1px solid transparent", cursor: "pointer", fontSize: 13,
                padding: "2px 6px", color: "#666", borderRadius: 4 }}>✏️</button>
          )}
          {!editing && onDelete && (
            <button onClick={() => { if (window.confirm(shift.recurring ? "Delete this shift from all recurring days?" : "Delete this shift?")) onDelete(); }}
              title="Delete"
              style={{ background: "none", border: "1px solid transparent", cursor: "pointer", fontSize: 13,
                padding: "2px 6px", color: "#666", borderRadius: 4 }}>🗑️</button>
          )}
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
      </div>

      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#555" }}>
          <span style={{ fontSize: 14 }}>{recurringLabel ? "🔁" : "📅"}</span>
          <span>{recurringLabel || dayLabel}, {hh(shift.startH)} – {hh(shift.endH)}</span>
        </div>
      ) : (
        <>
          {shift.recurring && (
            <div style={{ fontSize: 11, color: "#555", marginBottom: 10, lineHeight: 1.4,
              background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, padding: "6px 9px" }}>
              Recurring shift — changes apply to <strong>all</strong> {shift.recurring.days.map(d => DOW_LABELS[d]).join(", ")}.
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ddd", width: "100%", fontFamily: '"Inter", sans-serif' }}>
              {SHIFT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start</label>
              <select value={startH} onChange={e => setStartH(Number(e.target.value))}
                style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ddd", width: "100%" }}>
                {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
              </select>
            </div>
            <div style={{ paddingBottom: 6, color: "#bbb" }}>→</div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>End</label>
              <select value={endH} onChange={e => setEndH(Number(e.target.value))}
                style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ddd", width: "100%" }}>
                {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button onClick={() => { setEditing(false); setCategory(shift.category); setStartH(shift.startH); setEndH(shift.endH); }}
              style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid #ddd", background: "#fff", fontSize: 12, cursor: "pointer", color: "#555" }}>Cancel</button>
            <button onClick={handleSave} disabled={totalH <= 0}
              style={{ padding: "5px 14px", borderRadius: 5, border: "none", fontSize: 12, fontWeight: 600,
                cursor: totalH > 0 ? "pointer" : "default", background: totalH > 0 ? "#7053ea" : "#ccc", color: "#fff" }}>Save</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── WINDOW POPOVER (view + edit a planned production time) ─────────────────
function WindowPopover({ date, win, anchorRect, downtimes = [], netH, onClose, onSave, onDelete, onAddDowntime, onEditDowntime, onDeleteDowntime }) {
  const popRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [startH, setStartH] = useState(win.startH);
  const [endH, setEndH] = useState(win.endH);

  useEffect(() => {
    function down(e) { if (popRef.current && !popRef.current.contains(e.target)) onClose(); }
    function scroll() { onClose(); }
    document.addEventListener("mousedown", down);
    window.addEventListener("scroll", scroll, true);
    return () => { document.removeEventListener("mousedown", down); window.removeEventListener("scroll", scroll, true); };
  }, [onClose]);

  const totalH = endH - startH;
  const origH = win.endH - win.startH;
  const headerH = netH ?? origH;
  const dayLabel = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  const recurringLabel = win.recurring ? `Weekly, ${win.recurring.days.map(d => DOW_LABELS[d]).join(", ")}` : null;

  const top  = Math.min((anchorRect?.bottom ?? 0) + 6, (window.innerHeight || 800) - 260);
  const left = Math.min(Math.max(8, anchorRect?.left ?? 0), (window.innerWidth || 1200) - 320);

  function handleSave() { if (endH > startH) onSave(startH, endH); }

  const labelStyle = { fontSize: 10, fontWeight: 600, color: "#555", display: "block", marginBottom: 3 };

  return (
    <div ref={popRef}
      style={{ position: "fixed", top, left, minWidth: 300, background: "#fff", border: "1px solid #e5e5e5",
        borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.18)", zIndex: 401, padding: 14, fontFamily: "sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editing ? 10 : 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>Total production time</span>
          <span style={{ background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9", borderRadius: 4,
            padding: "2px 8px", fontSize: 12, fontWeight: 700, minWidth: 32, textAlign: "center" }}>
            {editing ? Math.max(0, totalH) : headerH}h
          </span>
          {!editing && (
            <button onClick={() => setEditing(true)} title="Edit"
              style={{ background: "none", border: "1px solid transparent", cursor: "pointer", fontSize: 13,
                padding: "2px 6px", color: "#666", borderRadius: 4 }}>✏️</button>
          )}
          {!editing && onDelete && (
            <button onClick={() => { if (window.confirm(win.recurring && !win.isDefault ? "Delete this ppt from all recurring days?" : "Delete this ppt?")) onDelete(); }} title="Delete"
              style={{ background: "none", border: "1px solid transparent", cursor: "pointer", fontSize: 13,
                padding: "2px 6px", color: "#666", borderRadius: 4 }}>🗑️</button>
          )}
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
      </div>

      {!editing ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#555" }}>
            <span style={{ fontSize: 14 }}>{recurringLabel ? "🔁" : "📅"}</span>
            <span>{recurringLabel || dayLabel}, {hh(win.startH)} – {hh(win.endH)}</span>
            {win.isDefault && <span style={{ fontSize: 10, color: "#999", fontStyle: "italic", marginLeft: 4 }}>(default)</span>}
          </div>

          {/* Planned downtime section */}
          <div style={{ borderTop: "1px solid #eee", margin: "12px 0 8px" }} />
          <div style={{ fontSize: 12, fontWeight: 700, color: "#222", marginBottom: 6 }}>Planned downtime</div>
          {downtimes.length === 0 ? (
            <div style={{ fontSize: 11, color: "#999", marginBottom: 4 }}>No planned downtime</div>
          ) : (
            <div style={{ marginBottom: 4 }}>
              {downtimes.map(dt => {
                const timeLabel = dt.mode === "exact"
                  ? `${hh(dt.startH)} – ${hh(dt.endH)}`
                  : `~${dt.durationH}h`;
                return (
                  <div key={dt.id} onClick={() => onEditDowntime && onEditDowntime(dt)}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 4, fontSize: 11, color: "#444", cursor: "pointer", marginBottom: 1 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#e53935", flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 600 }}>{dt.reason}</span>
                    <span style={{ color: "#888" }}>{timeLabel}</span>
                    {!dt.deduct && <span style={{ fontSize: 9, color: "#aaa", fontStyle: "italic" }}>no deduct</span>}
                    <button onClick={e => { e.stopPropagation(); onDeleteDowntime && onDeleteDowntime(dt.id); }}
                      title="Delete"
                      style={{ background: "none", border: "none", color: "#bbb", cursor: "pointer", padding: "0 4px", fontSize: 12 }}>
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 6 }}>
            <button onClick={() => onAddDowntime && onAddDowntime()}
              style={{ background: "none", border: "none", color: "#222", fontSize: 12, fontWeight: 600, cursor: "pointer",
                padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add downtime
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "#555", marginBottom: 10, lineHeight: 1.4,
            background: win.recurring ? "#fff8e1" : "#f5f5f5",
            border: `1px solid ${win.recurring ? "#ffe082" : "#e5e5e5"}`,
            borderRadius: 4, padding: "6px 9px" }}>
            {win.isDefault
              ? <>Editing the default for <strong>{dayLabel}</strong> only — other days keep their {hh(win.startH)}–{hh(win.endH)} default.</>
              : win.recurring
              ? <>Recurring slot — changes apply to <strong>all</strong> {win.recurring.days.map(d => DOW_LABELS[d]).join(", ")}.</>
              : <>Editing <strong>{dayLabel}</strong> only.</>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start</label>
              <select value={startH} onChange={e => setStartH(Number(e.target.value))}
                style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ddd", width: "100%" }}>
                {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
              </select>
            </div>
            <div style={{ paddingBottom: 6, color: "#bbb" }}>→</div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>End</label>
              <select value={endH} onChange={e => setEndH(Number(e.target.value))}
                style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ddd", width: "100%" }}>
                {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button onClick={() => { setEditing(false); setStartH(win.startH); setEndH(win.endH); }}
              style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid #ddd", background: "#fff", fontSize: 12, cursor: "pointer", color: "#555" }}>Cancel</button>
            <button onClick={handleSave} disabled={totalH <= 0}
              style={{ padding: "5px 14px", borderRadius: 5, border: "none", fontSize: 12, fontWeight: 600,
                cursor: totalH > 0 ? "pointer" : "default", background: totalH > 0 ? "#4caf50" : "#ccc", color: "#fff" }}>Save</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── WORK ORDERS PAGE ────────────────────────────────────────────────────────
// Mock actual-run-hours from op status (the prototype doesn't track real-time progress yet).
function mockActualHours(op) {
  if (op.status === "Done") return op.estHours || 0;
  if (op.status === "In progress") return Math.round((op.estHours || 0) * 0.5);
  return 0;
}

// Real actual-run-hours once a job has been started on a tablet (Q3): the live elapsed time
// since `actualStart`. Falls back to the status-based mock for ops never started on a tablet.
function actualHours(op, nowMs) {
  if (op.actualStart) {
    const start = new Date(op.actualStart).getTime();
    if (op.floorStatus === "In progress")
      return Math.max(0, (nowMs - start) / 3600000);
    if (op.floorStatus === "Done")
      return op.actualEnd ? Math.max(0, (new Date(op.actualEnd).getTime() - start) / 3600000) : (op.estHours || 0);
  }
  return mockActualHours(op);
}

function ProgressBar({ pct, width = 90 }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div style={{ width, height: 6, background: "#ececec", borderRadius: 3, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ width: `${clamped}%`, height: "100%", background: "#888" }} />
    </div>
  );
}

function formatDateShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

// "Mon, 1 Jan 2026, 00:00 - 12:00" when same date,
// "Mon, 1 Jan 2026, 12:00 - Tue, 2 Jan 2026, 00:00" when crossing days.
function formatPlannedRange(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const s = new Date(startIso);
  const e = new Date(endIso);
  const sameDay = s.getFullYear() === e.getFullYear()
    && s.getMonth() === e.getMonth()
    && s.getDate() === e.getDate();
  const dateOpts = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
  const timeOpts = { hour: "2-digit", minute: "2-digit", hour12: false };
  const sDate = s.toLocaleDateString("en-GB", dateOpts);
  const sTime = s.toLocaleTimeString("en-GB", timeOpts);
  const eTime = e.toLocaleTimeString("en-GB", timeOpts);
  if (sameDay) return `${sDate}, ${sTime} - ${eTime}`;
  const eDate = e.toLocaleDateString("en-GB", dateOpts);
  return `${sDate}, ${sTime} - ${eDate}, ${eTime}`;
}

// Actual production window for a started op: the compact start–end range once complete (Done),
// or just the start time while it's still running.
function formatActualProd(start, end) {
  if (!start) return "—";
  return end ? formatPlannedRange(start, end) : formatDateTime(start);
}

// Smallest width a column can take without clipping its header: the label text plus the sort
// icon, framed by the same 16px padding on the left and right (matching SortHeader's padding).
function minColWidth(label) {
  const PAD = 16;          // left & right padding — kept equal so both gaps match
  const ICON = 20;         // sort icon (16) + gap (4) to its left
  const charPx = 7.7;      // ~width of one bold 13px Inter character
  return Math.ceil(label.length * charPx) + ICON + PAD * 2;
}

function SortHeader({ label, width, onResizeStart }) {
  return (
    <th style={{ width, minWidth: width, maxWidth: width,
      padding: "12px 16px", textAlign: "left",
      background: "#fff", borderBottom: "1px solid #ececec",
      fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 600, color: "#181233",
      position: "sticky", top: 0, zIndex: 1, whiteSpace: "nowrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        <MIcon name="unfold_more" size={16} color="#888" />
      </span>
      {/* Column resize handle — drag the right edge to resize. The divider line hints it's draggable. */}
      {onResizeStart && (
        <span onMouseDown={onResizeStart} onClick={e => e.stopPropagation()}
          title="Drag to resize column"
          style={{ position: "absolute", top: 0, right: 0, width: 9, height: "100%",
            cursor: "col-resize", userSelect: "none", zIndex: 2,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ width: 1, height: "60%", background: "#d9d9d9" }} />
        </span>
      )}
    </th>
  );
}

// ─── CSV IMPORT ──────────────────────────────────────────────────────────────
// Tokenise CSV text into rows of string cells. Handles quoted fields containing
// commas, escaped quotes (""), and CR/LF/CRLF line breaks (incl. inside quotes).
function tokenizeCsv(text) {
  const rows = [];
  let row = [], cell = "", inQuotes = false;
  // Strip a leading UTF-8 BOM if present.
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }   // escaped quote
        else inQuotes = false;
      } else cell += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(cell); cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;        // swallow CRLF pair
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += c;
  }
  // Flush the trailing cell/row if the file didn't end with a newline.
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

// Map a raw header cell to one of our canonical field keys (case/space/underscore-insensitive).
const CSV_HEADER_ALIASES = {
  woid: "woId", workorderid: "woId", workorder: "woId", wo: "woId",
  product: "product", part: "product",
  description: "description", desc: "description",
  duedate: "dueDate", due: "dueDate",
  targetqty: "targetQty", targetquantity: "targetQty", quantity: "targetQty", qty: "targetQty",
  opid: "opId", operationid: "opId", operation: "opId",
  oplabel: "opLabel", operationlabel: "opLabel", label: "opLabel",
  esthours: "estHours", estimatedhours: "estHours", estruntime: "estHours", hours: "estHours",
  idealcell: "idealCell", cell: "idealCell",
  machines: "machines", machine: "machines", assignedmachines: "machines", assignedmachine: "machines",
  scheduledproductiontime: "scheduled", scheduledtime: "scheduled", scheduled: "scheduled",
  plannedwindow: "scheduled", scheduledwindow: "scheduled",
};
function canonicalHeader(raw) {
  return CSV_HEADER_ALIASES[String(raw).trim().toLowerCase().replace(/[^a-z0-9]/g, "")] || null;
}

const CSV_MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
// Parse one side of a scheduled range, e.g. "Wed, 24 Jun 2026, 08:00" or "24 Jun 2026, 08:00".
// `base` (the already-parsed start) lets the end side be a bare time like "17:00" — the
// same-day shorthand the Work Orders page prints. Builds a local Date (matching addHours)
// and returns { y, mo, d, iso }. Returns null if it can't be parsed.
function parseScheduledSide(s, base) {
  s = s.trim();
  const full = s.match(/(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})\D+(\d{1,2}):(\d{2})/);
  if (full) {
    const mo = CSV_MONTHS[full[2].slice(0, 3).toLowerCase()];
    if (mo == null) return null;
    const [y, d, hh, mm] = [+full[3], +full[1], +full[4], +full[5]];
    return { y, mo, d, iso: new Date(y, mo, d, hh, mm, 0, 0).toISOString() };
  }
  const time = s.match(/^(\d{1,2}):(\d{2})$/);
  if (time && base) {
    const { y, mo, d } = base;
    return { y, mo, d, iso: new Date(y, mo, d, +time[1], +time[2], 0, 0).toISOString() };
  }
  return null;
}
// Parse a "Scheduled production time" cell (the format the Work Orders page prints) into
// { start, end } ISO strings. Accepts the same-day form ("… 08:00 - 17:00") and the
// cross-day form ("… 08:00 - Wed, 1 Jul 2026, 09:30"). Returns null when absent/unparseable.
function parseScheduledRange(cell) {
  cell = (cell || "").trim();
  if (!cell || cell === "—" || cell === "-") return null;
  const parts = cell.split(/\s+-\s+/);
  if (parts.length !== 2) return null;
  const start = parseScheduledSide(parts[0], null);
  if (!start) return null;
  const end = parseScheduledSide(parts[1], start);
  if (!end) return null;
  return { start: start.iso, end: end.iso };
}

// The fields an imported CSV row contributes to an operation. Used both when creating a new op
// (spread over the defaults below) and when updating an existing one — so re-importing a row
// only touches what the CSV actually carried, leaving untouched planning state intact. Assigning
// machines and/or a scheduled window places the op on the Planning calendar (which needs a single
// `machine` + non-empty `segments`). csvMachines/csvEstHours capture the baseline for override flags.
function csvOpFields(csvOp) {
  const f = {
    label: csvOp.label, estHours: csvOp.estHours, estRunTime: csvOp.estRunTime, csvEstHours: csvOp.estHours,
  };
  if (csvOp.idealCell !== undefined) f.idealCell = csvOp.idealCell;
  if (csvOp.machines && csvOp.machines.length) {
    f.machine = csvOp.machines[0];   // calendar lanes key off the single `machine`
    f.machines = csvOp.machines;
    f.csvMachines = csvOp.machines;
  }
  if (csvOp.plannedStart && csvOp.plannedEnd) {
    f.segments = [{ start: csvOp.plannedStart, end: csvOp.plannedEnd }];
    f.plannedStart = csvOp.plannedStart;
    f.plannedEnd = csvOp.plannedEnd;
    f.status = "Planned";
    f.planStatus = "Scheduled";
  }
  return f;
}

// Build a fresh operation from its CSV-sourced fields, filling the planning/shopfloor defaults
// a new op needs, then layering on whatever the CSV carried (est time, machines, schedule).
function newOperationFromCsv(csvOp) {
  return {
    id: csvOp.id,
    machine: null, plannedStart: null, plannedEnd: null, segments: [],
    status: "Not planned", planStatus: "Not planned", floorStatus: "Not started",
    idealCell: csvOp.idealCell,
    ...csvOpFields(csvOp),
  };
}

// Parse CSV text (one row per operation) into work orders with nested operations.
// Rows sharing a woId are grouped; insertion order is preserved. Throws on bad input.
function parseWorkOrdersCsv(text) {
  const rows = tokenizeCsv(text).filter(r => r.some(c => c.trim() !== ""));   // drop blank rows
  if (rows.length < 2) throw new Error("CSV needs a header row and at least one data row.");

  const headers = rows[0].map(canonicalHeader);
  const idx = {};
  headers.forEach((h, i) => { if (h && !(h in idx)) idx[h] = i; });
  if (idx.woId == null || idx.opId == null)
    throw new Error("CSV must include columns for woId and opId.");

  const woPresent = ["product", "description", "dueDate", "targetQty"].filter(f => idx[f] != null);
  const get = (row, key) => idx[key] != null ? (row[idx[key]] ?? "").trim() : "";

  const map = new Map();   // woId → work order accumulator (preserves first-seen order)
  let opCount = 0;
  rows.slice(1).forEach((row, n) => {
    const woId = get(row, "woId");
    const opId = get(row, "opId");
    if (!woId) return;
    if (!opId) throw new Error(`Row ${n + 2}: missing opId for work order "${woId}".`);

    if (!map.has(woId)) {
      const wo = { id: woId, operations: [] };
      if (idx.product != null)     wo.product = get(row, "product");
      if (idx.description != null) wo.description = get(row, "description");
      if (idx.dueDate != null)     wo.dueDate = get(row, "dueDate");
      if (idx.targetQty != null)   { const q = parseInt(get(row, "targetQty"), 10); wo.targetQty = Number.isNaN(q) ? 0 : q; }
      map.set(woId, wo);
    }
    const hoursRaw = get(row, "estHours");
    const estHours = hoursRaw === "" || Number.isNaN(parseFloat(hoursRaw)) ? null : parseFloat(hoursRaw);
    const machines = get(row, "machines").split(/[,;]/).map(s => s.trim()).filter(Boolean);
    const sched = parseScheduledRange(get(row, "scheduled"));
    map.get(woId).operations.push({
      id: opId,
      label: get(row, "opLabel") || opId,
      estHours,
      estRunTime: estHours != null ? `${estHours}h` : null,
      idealCell: get(row, "idealCell") || undefined,
      machines: machines.length ? machines : undefined,
      plannedStart: sched?.start,
      plannedEnd: sched?.end,
    });
    opCount++;
  });

  return { workOrders: [...map.values()], woPresent, opCount };
}

// The styled "Import CSV" button: opens a file picker, parses the chosen CSV, hands the
// parsed work orders to onImport, and shows a transient success/error toast.
function ImportCsvButton({ onImport }) {
  const inputRef = useRef(null);
  const [toast, setToast] = useState(null);   // { ok: boolean, text: string }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";   // reset so picking the same file again re-fires onChange
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { workOrders, woPresent, opCount } = parseWorkOrdersCsv(String(reader.result));
        onImport(workOrders, woPresent);
        const nWo = workOrders.length;
        setToast({ ok: true, text: `Imported ${nWo} work order${nWo === 1 ? "" : "s"} (${opCount} operation${opCount === 1 ? "" : "s"}).` });
      } catch (err) {
        setToast({ ok: false, text: err.message || "Could not parse the CSV file." });
      }
    };
    reader.onerror = () => setToast({ ok: false, text: "Could not read the file." });
    reader.readAsText(file);
  }

  return (
    <div style={{ position: "relative" }}>
      <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: "none" }} />
      <button onClick={() => inputRef.current?.click()}
        style={{ height: 40, padding: "0 16px", background: "#7053ea", color: "#fff", border: "none",
          cursor: "pointer", fontSize: 14, fontWeight: 600, lineHeight: "20px",
          display: "flex", alignItems: "center", gap: 8, fontFamily: '"Inter", sans-serif' }}>
        <MIcon name="add" size={20} color="#fff" />
        Import CSV
      </button>
      {toast && (
        <div style={{ position: "absolute", top: 48, right: 0, zIndex: 50, maxWidth: 320,
          padding: "10px 14px", borderRadius: 4, fontSize: 13, lineHeight: "18px", fontFamily: '"Inter", sans-serif',
          background: toast.ok ? "#e9f7ef" : "#fdecea", color: toast.ok ? "#1e7e45" : "#b3261e",
          border: `1px solid ${toast.ok ? "#9bd9b4" : "#f3b6b0"}`, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

function WorkOrdersPage({ workOrders, onUpdateWo, onImport }) {
  const [expanded, setExpanded] = useState(new Set(["A0001"]));
  const [search, setSearch] = useState("");
  const [editingDueDateFor, setEditingDueDateFor] = useState(null); // wo.id while inline-editing the due date
  const toggle = id => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const woStatuses = workOrders.map(w => rollupWoStatus(w.operations));
  const countOf = s => woStatuses.filter(x => x === s).length;
  const chips = [
    ["Not started", countOf("Not started") + countOf("Not planned") + countOf("Planned")],
    ["In progress", countOf("In progress")],
    ["Done",        countOf("Done")],
  ];

  const columns = [
    ["Work order",         180],
    ["Machine",            130],
    ["Operation status",   200],
    ["Actual / Est. run time", 200],
    ["Target Qty.",        110],
    ["Product No.",        110],
    ["Product description", 200],
    ["Due date",           170],
    ["Planned production time", 320],
  ];

  const pillBtn = {
    height: 36, padding: "0 14px", background: "#fff", border: "1px solid #d9d9d9", borderRadius: 100,
    fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, color: "#181233",
    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff", fontFamily: '"Inter", sans-serif', overflow: "hidden" }}>
      {/* Main header */}
      <div style={{ height: 60, padding: "0 16px", background: "#fff", borderBottom: "1px solid #ececec",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.18px", color: "#181233" }}>Work orders</h1>
        <div style={{ display: "flex", gap: 16, alignItems: "center", height: 40 }}>
          <button title="Settings"
            style={{ width: 40, height: 40, background: "#fff", border: "1px solid #bebfd0", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <MIcon name="settings" size={20} color="#181233" />
          </button>
          <ImportCsvButton onImport={onImport} />
        </div>
      </div>

      {/* Filter pills + search */}
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={pillBtn}>Planned start date <MIcon name="expand_more" size={18} color="#181233" /></button>
          <button style={pillBtn}>Assets <MIcon name="expand_more" size={18} color="#181233" /></button>
        </div>
        <div style={{ position: "relative", width: 260 }}>
          <MIcon name="search" size={20} color="#888" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search"
            style={{ width: "100%", height: 40, padding: "0 12px 0 40px", border: "1px solid #d9d9d9", background: "#fff",
              boxSizing: "border-box", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#000", outline: "none" }} />
        </div>
      </div>

      {/* Status chips + export */}
      <div style={{ padding: "0 16px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {chips.map(([label, count]) => (
            <button key={label} style={{
              padding: "6px 16px", borderRadius: 100, border: "1px solid #d9d9d9", background: "#fff",
              cursor: "pointer", fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, color: "#181233",
              display: "inline-flex", alignItems: "center", gap: 8 }}>
              {label}
              <span style={{ color: "#888", fontWeight: 400 }}>{count}</span>
            </button>
          ))}
        </div>
        <button title="Export"
          style={{ width: 40, height: 40, background: "#fff", border: "1px solid #d9d9d9", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <MIcon name="download" size={20} color="#181233" />
        </button>
      </div>

      {/* Table */}
      <div style={{ margin: "0 16px 16px", border: "1px solid #ececec", borderRadius: 8, overflow: "auto", flex: 1, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr>
              {columns.map(([label, w]) => <SortHeader key={label} label={label} width={Math.max(w, minColWidth(label))} />)}
            </tr>
          </thead>
          <tbody>
            {workOrders.map(wo => {
              const isOpen = expanded.has(wo.id);
              const ops = wo.operations;
              const done = ops.filter(o => o.status === "Done").length;
              const woStatus = rollupWoStatus(ops);
              const woActual = ops.reduce((s, o) => s + mockActualHours(o), 0);
              const woEst    = ops.reduce((s, o) => s + (o.estHours || 0), 0);
              const machines = new Set(ops.map(o => o.machine).filter(Boolean));
              const machineLabel = machines.size === 0 ? "—"
                : machines.size === 1 ? [...machines][0]
                : `${machines.size} machines`;
              const td = (content, extra = {}) => (
                <td style={{ padding: "12px 16px", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#181233",
                  borderBottom: "1px solid #f0f0f0", verticalAlign: "middle", ...extra }}>{content}</td>
              );
              return (
                <React.Fragment key={wo.id}>
                  <tr onClick={() => toggle(wo.id)} style={{ cursor: "pointer", background: "#fff" }}>
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                        <MIcon name={isOpen ? "arrow_drop_up" : "arrow_drop_down"} size={20} color="#181233" />
                        <MIcon name="description" size={18} color="#181233" />
                        {wo.id}
                      </span>
                    )}
                    {td(machineLabel)}
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <StatusBadge status={woStatus} />
                        <span style={{ color: "#181233", fontSize: 14 }}>
                          <strong>{done}</strong> / {ops.length} Done
                        </span>
                      </span>
                    )}
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <ProgressBar pct={woEst ? (woActual / woEst) * 100 : 0} width={90} />
                        <span><strong>{woActual ? fmtDur(woActual) : "-"}</strong> / {fmtDur(woEst)}</span>
                      </span>
                    )}
                    {td(wo.targetQty ?? "—")}
                    {td(wo.product)}
                    {td(<span style={{ color: "#555", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: "100%" }}>{wo.description || "—"}</span>)}
                    {td(
                      editingDueDateFor === wo.id ? (
                        <input
                          type="date"
                          autoFocus
                          value={wo.dueDate || ""}
                          onChange={(e) => onUpdateWo && onUpdateWo(wo.id, { dueDate: e.target.value })}
                          onBlur={() => setEditingDueDateFor(null)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === "Enter") setEditingDueDateFor(null); }}
                          style={{ font: "inherit", padding: "4px 6px", border: "1px solid #7053ea", background: "#fff", borderRadius: 4, outline: "none" }} />
                      ) : (
                        <span onClick={(e) => { e.stopPropagation(); setEditingDueDateFor(wo.id); }}
                          title="Click to edit"
                          style={{ cursor: "pointer", borderBottom: "1px dashed #d9d9d9", paddingBottom: 1 }}>
                          {wo.dueDate ? formatDateShort(wo.dueDate) : "—"}
                        </span>
                      )
                    )}
                    {(() => {
                      const allSegs = ops.flatMap(o => o.segments || []);
                      if (!allSegs.length) return td("—");
                      const starts = allSegs.map(s => s.start).filter(Boolean).sort();
                      const ends   = allSegs.map(s => s.end).filter(Boolean).sort();
                      return td(formatPlannedRange(starts[0], ends[ends.length - 1]));
                    })()}
                  </tr>
                  {isOpen && ops.map(op => {
                    const opActual = mockActualHours(op);
                    const opEst = op.estHours || 0;
                    const opTd = (content, extra = {}) => (
                      <td style={{ padding: "10px 16px", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#555",
                        borderBottom: "1px solid #f0f0f0", verticalAlign: "middle", background: "#fafafa", ...extra }}>{content}</td>
                    );
                    return (
                      <tr key={op.id}>
                        {opTd(
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 28 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#ececec", color: "#181233", borderRadius: 100, height: 26, padding: "0 10px", fontSize: 12, fontWeight: 600, boxSizing: "border-box" }}>
                              OP <strong style={{ fontWeight: 700 }}>{op.label.replace(/^OP\s*/i, "")}</strong>
                            </span>
                          </span>
                        )}
                        {opTd(op.machine || <span style={{ color: "#bbb" }}>Not assigned</span>)}
                        {opTd(<StatusBadge status={op.status} small />)}
                        {opTd(
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                            <ProgressBar pct={opEst ? (opActual / opEst) * 100 : 0} width={90} />
                            <span><strong>{opActual ? fmtDur(opActual) : "-"}</strong> / {fmtDur(opEst)}</span>
                          </span>
                        )}
                        {opTd("")}
                        {opTd("")}
                        {opTd("")}
                        {opTd("")}
                        {opTd(formatPlannedRange(op.plannedStart, op.plannedEnd))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── WORK ORDERS PAGE (Q3) ────────────────────────────────────────────────────
// Q3 variant: each operation carries TWO independent statuses — a shopfloor status
// (Not started / In progress / Done) and a planning status (Not planned / Planned) —
// both editable inline. The rest mirrors the standard work orders table.
function WorkOrdersPageQ3({ workOrders, onUpdateWo, onUpdateOp, onImport }) {
  // Default to every work order expanded so the operator sees all OPs up front.
  const [expanded, setExpanded] = useState(() => new Set(workOrders.map(w => w.id)));
  const [search, setSearch] = useState("");
  const [editingDueDateFor, setEditingDueDateFor] = useState(null);
  const toggle = id => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Once a job is running (started on a tablet, actualStart set) its actual run time ticks live.
  const anyRunning = workOrders.some(wo => (wo.operations || []).some(o => o.actualStart && o.floorStatus === "In progress"));
  const [, tickActual] = useState(0);
  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(() => tickActual(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [anyRunning]);
  const nowMs = Date.now();

  const rollupFloor = ops => ops.every(o => o.floorStatus === "Done") ? "Done"
    : ops.some(o => o.floorStatus === "In progress") ? "In progress" : "Not started";
  // Earliest actual start / latest actual end among a WO's ops — the rolled-up production window.
  const rollupActualStart = ops => {
    const starts = ops.map(o => o.actualStart).filter(Boolean).sort();
    return starts[0] || null;
  };
  const rollupActualEnd = ops => {
    const ends = ops.map(o => o.actualEnd).filter(Boolean).sort();
    return ends[ends.length - 1] || null;
  };
  const rollupPlan = ops => !ops.length ? "Not planned"
    : ops.every(o => o.planStatus === "Planned") ? "Planned"
    : ops.every(o => o.planStatus === "Scheduled") ? "Scheduled"
    : ops.some(o => ["Planned", "Scheduled", "In planning"].includes(o.planStatus)) ? "In planning" : "Not planned";

  const LAST_MODIFIED = "Mon, 1 Jan 2026, 12:00";
  const fmtLastModified = (iso) => iso ? formatDateTime(iso) : LAST_MODIFIED;

  const columns = [
    ["Work order",              180],
    ["Machine",                 120],
    ["Shopfloor status",        220],
    ["Planning status",         160],
    ["Actual / Est. run time",  190],
    ["Target Qty.",             100],
    ["Product No.",             100],
    ["Product description",     190],
    ["Due date",                150],
    ["Actual production time",  180],
    ["Scheduled production time", 240],
    ["Last modified",           200],
  ];

  // Resizable columns — widths live in state; dragging a header's right edge updates them.
  const [colWidths, setColWidths] = useState(columns.map(c => Math.max(c[1], minColWidth(c[0]))));
  const resizeRef = useRef(null);
  function startResize(e, idx) {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { idx, startX: e.clientX, startW: colWidths[idx] };
    const onMove = ev => {
      const r = resizeRef.current; if (!r) return;
      const next = Math.max(minColWidth(columns[r.idx][0]), r.startW + (ev.clientX - r.startX));
      setColWidths(prev => { const n = [...prev]; n[r.idx] = next; return n; });
    };
    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }
  const tableWidth = colWidths.reduce((s, w) => s + w, 0);

  // Pagination (static, prototype) — mirrors the Figma "Showing 1-20 of 1,023" strip.
  const [page, setPage] = useState(1);

  // Grey "global filter" pill (matches Figma: #ececec fill, no border, h32, pl16/pr12).
  const pillBtn = {
    height: 32, padding: "0 12px 0 16px", background: "#ececec", border: "none", borderRadius: 100,
    fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, color: "#181233",
    display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
  };
  const pageBtn = {
    width: 32, height: 32, background: "transparent", border: "none", borderRadius: 6,
    display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff", fontFamily: '"Inter", sans-serif', overflow: "hidden" }}>
      {/* Main header */}
      <div style={{ height: 60, padding: "0 16px", background: "#fff", borderBottom: "1px solid #ececec",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.18px", color: "#181233" }}>Work orders</h1>
        <div style={{ display: "flex", gap: 16, alignItems: "center", height: 40 }}>
          <button title="Settings"
            style={{ width: 40, height: 40, background: "#fff", border: "1px solid #bebfd0", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <MIcon name="settings" size={20} color="#181233" />
          </button>
          <ImportCsvButton onImport={onImport} />
        </div>
      </div>

      {/* Filter pills + search — Figma "Work orders filters": h46, 7px pad, border-b #ececec */}
      <div style={{ height: 46, padding: "0 8px 0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        flexShrink: 0, borderBottom: "1px solid #ececec", boxSizing: "border-box" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={pillBtn}>Planned start date <MIcon name="expand_more" size={18} color="#181233" /></button>
          <button style={pillBtn}>Assets <MIcon name="expand_more" size={18} color="#181233" /></button>
        </div>
        <div style={{ position: "relative", width: 250 }}>
          <MIcon name="search" size={20} color="#888" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search"
            style={{ width: "100%", height: 32, padding: "0 12px 0 40px", border: "none", background: "#efefef",
              boxSizing: "border-box", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#000", outline: "none" }} />
        </div>
      </div>

      {/* Status filter chips + export — Figma "container header_v2": h52, 8px pad, border-b #d9d9d9 */}
      <div style={{ height: 52, padding: "0 8px 0 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, borderBottom: "1px solid #d9d9d9", boxSizing: "border-box" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {["Not planned", "In planning", "Scheduled", "Planned", "Not started", "In progress", "Done"].map(label => (
            <button key={label} style={{
              height: 36, padding: "0 16px", borderRadius: 100,
              border: "1px solid #d9d9d9", background: "#fff",
              cursor: "pointer", fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, color: "#181233",
              display: "inline-flex", alignItems: "center", gap: 8, minWidth: 50, justifyContent: "center" }}>
              {q3StatusLabel(label)}
              <span style={{ color: "#181233", fontWeight: 700 }}>
                {workOrders.filter(w =>
                  PLAN_STATUSES.includes(label) ? rollupPlan(w.operations) === label : rollupFloor(w.operations) === label).length}
              </span>
            </button>
          ))}
        </div>
        <button title="Export"
          style={{ width: 40, height: 36, background: "#fff", border: "none", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <MIcon name="download" size={20} color="#181233" />
        </button>
      </div>

      {/* Grey content area holding the white table card */}
      <div style={{ flex: 1, background: "#ececec", padding: 16, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 0, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ overflow: "auto", flex: 1 }}>
        <table style={{ width: tableWidth, minWidth: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>{columns.map(([label], i) => <col key={label} style={{ width: colWidths[i] }} />)}</colgroup>
          <thead>
            <tr>{columns.map(([label], i) => <SortHeader key={label} label={label} width={colWidths[i]} onResizeStart={e => startResize(e, i)} />)}</tr>
          </thead>
          <tbody>
            {workOrders.map((wo, woIdx) => {
              const isOpen = expanded.has(wo.id);
              const ops = wo.operations;
              const done = ops.filter(o => o.floorStatus === "Done").length;
              const floorStatus = rollupFloor(ops);
              const planStatus = rollupPlan(ops);
              const woActual = ops.reduce((s, o) => s + actualHours(o, nowMs), 0);
              const woEst = ops.reduce((s, o) => s + (o.estHours || 0), 0);
              const woActualStart = rollupActualStart(ops);
              const woActualEnd = rollupActualEnd(ops);
              const allMachines = [...new Set(ops.flatMap(o => opMachines(o)))];
              const machineLabel = machineAssignLabel(allMachines) || "—";
              const td = (content, extra = {}) => (
                <td style={{ padding: "12px 16px", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#181233",
                  borderBottom: "1px solid #f0f0f0", verticalAlign: "middle", ...extra }}>{content}</td>
              );
              return (
                <React.Fragment key={wo.id}>
                  {/* 8px background-coloured gap between work orders so each parent is distinct. */}
                  {woIdx > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={columns.length} style={{ height: 8, padding: 0, background: "#ececec", border: "none" }} />
                    </tr>
                  )}
                  <tr onClick={() => toggle(wo.id)} style={{ cursor: "pointer", background: "#fff" }}>
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                        <MIcon name={isOpen ? "arrow_drop_up" : "arrow_drop_down"} size={20} color="#181233" />
                        <MIcon name="description" size={18} color="#181233" />
                        {wo.id}
                      </span>
                    )}
                    {td(machineLabel)}
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                        <Q3StatusPill status={floorStatus} />
                        <span style={{ color: "#181233", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}><strong>{done}</strong> / {ops.length} Done</span>
                      </span>
                    )}
                    {td(<Q3StatusPill status={planStatus} />)}
                    {td(
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <ProgressBar pct={woEst ? (woActual / woEst) * 100 : 0} width={90} />
                        <span><strong>{woActual ? fmtDur(woActual) : "-"}</strong> / {fmtDur(woEst)}</span>
                      </span>
                    )}
                    {td(wo.targetQty ?? "—")}
                    {td(wo.product)}
                    {td(<span style={{ color: "#555", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: "100%" }}>{wo.description || "—"}</span>)}
                    {td(
                      editingDueDateFor === wo.id ? (
                        <input type="date" autoFocus value={wo.dueDate || ""}
                          onChange={(e) => onUpdateWo && onUpdateWo(wo.id, { dueDate: e.target.value })}
                          onBlur={() => setEditingDueDateFor(null)} onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === "Enter") setEditingDueDateFor(null); }}
                          style={{ font: "inherit", padding: "4px 6px", border: "1px solid #7053ea", background: "#fff", borderRadius: 4, outline: "none" }} />
                      ) : (
                        <span onClick={(e) => { e.stopPropagation(); setEditingDueDateFor(wo.id); }} title="Click to edit"
                          style={{ cursor: "pointer", borderBottom: "1px dashed #d9d9d9", paddingBottom: 1 }}>
                          {wo.dueDate ? formatDateShort(wo.dueDate) : "—"}
                        </span>
                      )
                    )}
                    {td(formatActualProd(woActualStart, floorStatus === "Done" ? woActualEnd : null))}
                    {(() => {
                      const allSegs = ops.flatMap(o => o.segments || []);
                      if (!allSegs.length) return td("—");
                      const starts = allSegs.map(s => s.start).filter(Boolean).sort();
                      const ends = allSegs.map(s => s.end).filter(Boolean).sort();
                      return td(formatPlannedRange(starts[0], ends[ends.length - 1]));
                    })()}
                    {td(<span style={{ color: "#888", whiteSpace: "nowrap" }}>{fmtLastModified(wo.lastModified)}</span>)}
                  </tr>
                  {isOpen && ops.map(op => {
                    const opActual = actualHours(op, nowMs);
                    const opEst = op.estHours || 0;
                    const opTd = (content, extra = {}) => (
                      <td style={{ padding: "10px 16px", fontFamily: '"Inter", sans-serif', fontSize: 14, color: "#555",
                        borderBottom: "1px solid #f0f0f0", verticalAlign: "middle", background: "#fff", ...extra }}>{content}</td>
                    );
                    return (
                      <tr key={op.id}>
                        {opTd(
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 28 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#ececec", color: "#181233", borderRadius: 100, height: 26, padding: "0 10px", fontSize: 12, fontWeight: 600, boxSizing: "border-box" }}>
                              OP <strong style={{ fontWeight: 700 }}>{op.label.replace(/^OP\s*/i, "")}</strong>
                            </span>
                          </span>
                        )}
                        {opTd(<MachineAssignDropdown selected={opMachines(op)} woId={wo.id} opLabel={op.label}
                          overridden={machinesOverridden(op)}
                          onChange={(ms) => onUpdateOp && onUpdateOp(wo.id, op.id, { machines: ms, machine: ms[0] || null })} />)}
                        {opTd(<StatusDropdown value={op.floorStatus} options={FLOOR_STATUSES} small
                          onChange={(v) => onUpdateOp && onUpdateOp(wo.id, op.id, { floorStatus: v })} />)}
                        {opTd(<StatusDropdown value={op.planStatus} options={PLAN_STATUSES} small
                          onChange={(v) => onUpdateOp && onUpdateOp(wo.id, op.id, { planStatus: v })} />)}
                        {opTd(
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                            <ProgressBar pct={opEst ? (opActual / opEst) * 100 : 0} width={90} />
                            <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                              <strong>{opActual ? fmtDur(opActual) : "-"}</strong>&nbsp;/&nbsp;
                              <EstRunTimeEdit hours={opEst} woId={wo.id} opLabel={op.label} overridden={estOverridden(op)}
                                onChange={(hrs) => onUpdateOp && onUpdateOp(wo.id, op.id, { estHours: hrs, estRunTime: fmtDur(hrs) })} />
                            </span>
                          </span>
                        )}
                        {opTd("")}
                        {opTd("")}
                        {opTd("")}
                        {opTd("")}
                        {opTd(formatActualProd(op.actualStart, op.actualEnd))}
                        {opTd(formatPlannedRange(op.plannedStart, op.plannedEnd))}
                        {opTd(<span style={{ color: "#888", whiteSpace: "nowrap" }}>{fmtLastModified(op.lastModified)}</span>)}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
          </div>
          {/* Pagination strip */}
          <div style={{ flexShrink: 0, borderTop: "1px solid #ececec", padding: "10px 16px",
            display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff" }}>
            <span style={{ fontSize: 13, color: "#888", fontFamily: '"Inter", sans-serif' }}>Showing 1-{workOrders.length} of {workOrders.length} work orders</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {[
                { icon: "keyboard_double_arrow_left" },
                { icon: "chevron_left" },
              ].map(b => (
                <button key={b.icon} disabled title="" style={{ ...pageBtn, cursor: "default", opacity: 0.3 }}>
                  <MIcon name={b.icon} size={18} color="#181233" />
                </button>
              ))}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 8px 0 12px",
                border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600, color: "#181233", background: "#f2f2f2" }}>
                {page} <MIcon name="expand_more" size={16} color="#181233" />
              </span>
              {[
                { icon: "chevron_right" },
                { icon: "keyboard_double_arrow_right" },
              ].map(b => (
                <button key={b.icon} disabled title="" style={{ ...pageBtn, cursor: "default", opacity: 0.3 }}>
                  <MIcon name={b.icon} size={18} color="#181233" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ADD SHIFT MODAL ─────────────────────────────────────────────────────────
// `initial`: optional prefill from a calendar drag — { machines: [...], startH, endH, dateKey }.
// When provided, defaults flip to "Specific dates" with the single dragged day pre-selected.
function AddShiftModal({ onClose, onSave, initial }) {
  const [selMachines, setSelMachines] = useState(
    initial?.machines ? new Set(initial.machines) : new Set(MACHINES)
  );
  const [category, setCategory] = useState("Day");
  const [startH, setStartH] = useState(initial?.startH ?? 8);
  const [endH, setEndH]     = useState(initial?.endH ?? 16);
  const [recurring, setRecurring] = useState(initial ? false : true);
  const [selDays, setSelDays] = useState(new Set([0,1,2,3,4]));
  const [rangeStart, setRangeStart] = useState(initial?.dateKey || isoDate(new Date()));
  const [rangeEnd, setRangeEnd]     = useState(initial?.dateKey || isoDate(new Date()));

  const totalH = endH - startH;
  const valid = selMachines.size > 0 && totalH > 0 && (recurring ? selDays.size > 0 : rangeStart <= rangeEnd);

  function toggleMachine(m) {
    setSelMachines(prev => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });
  }
  function toggleDay(d) {
    setSelDays(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n; });
  }

  function handleSave() {
    const entries = [];
    if (recurring) {
      const from = new Date(); from.setHours(0,0,0,0);
      const to   = new Date(); to.setMonth(to.getMonth() + 6);
      for (const cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
        const dow = (cur.getDay() + 6) % 7;
        if (selDays.has(dow)) {
          [...selMachines].forEach(m => entries.push({ machine: m, key: isoDate(new Date(cur)) }));
        }
      }
    } else {
      const from = new Date(rangeStart); const to = new Date(rangeEnd);
      for (const cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
        [...selMachines].forEach(m => entries.push({ machine: m, key: isoDate(new Date(cur)) }));
      }
    }
    onSave(entries, startH, endH, category, recurring ? { days: [...selDays].sort((a, b) => a - b) } : null);
    onClose();
  }

  const S = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" },
    modal:   { background: "#fff", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 24, width: 460, maxWidth: "95vw", fontFamily: '"Inter", sans-serif' },
    label:   { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 4, display: "block" },
    select:  { fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%", fontFamily: '"Inter", sans-serif' },
    chip:    (active) => ({ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer", border: "1px solid",
      background: active ? "#7053ea" : "#fff", color: active ? "#fff" : "#666", borderColor: active ? "#7053ea" : "#ddd", fontFamily: '"Inter", sans-serif' }),
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Add shift</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Assign a shift category to one or more machines</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Machines</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button onClick={() => setSelMachines(selMachines.size === MACHINES.length ? new Set() : new Set(MACHINES))}
              style={{ ...S.chip(selMachines.size === MACHINES.length), fontSize: 10 }}>All</button>
            {MACHINES.map(m => (
              <button key={m} onClick={() => toggleMachine(m)} style={S.chip(selMachines.has(m))}>{m}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={S.select}>
            {SHIFT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Start time</label>
            <select value={startH} onChange={e => setStartH(Number(e.target.value))} style={S.select}>
              {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, color: "#bbb", fontSize: 16 }}>→</div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>End time</label>
            <select value={endH} onChange={e => setEndH(Number(e.target.value))} style={S.select}>
              {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
          </div>
          {totalH > 0
            ? <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, fontSize: 11, color: "#7053ea", fontWeight: 600, whiteSpace: "nowrap" }}>= {totalH}h</div>
            : <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, fontSize: 11, color: "#e53935", whiteSpace: "nowrap" }}>invalid</div>}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[["Recurring", true], ["Specific dates", false]].map(([label, val]) => (
            <button key={label} onClick={() => setRecurring(val)}
              style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid",
                background: recurring === val ? "#1a1a2e" : "#fff",
                color: recurring === val ? "#fff" : "#555",
                borderColor: recurring === val ? "#1a1a2e" : "#ddd", fontWeight: recurring === val ? 600 : 400, fontFamily: '"Inter", sans-serif' }}>
              {label}
            </button>
          ))}
        </div>

        {recurring ? (
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Repeat on</label>
            <div style={{ display: "flex", gap: 6 }}>
              {DOW_LABELS.map((d, i) => (
                <button key={i} onClick={() => toggleDay(i)} style={{ ...S.chip(selDays.has(i)), width: 40, textAlign: "center", padding: "4px 0" }}>{d.slice(0,2)}</button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>From</label>
              <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)}
                style={{ fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%", fontFamily: '"Inter", sans-serif' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>To</label>
              <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}
                style={{ fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%", fontFamily: '"Inter", sans-serif' }} />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer", color: "#555", fontFamily: '"Inter", sans-serif' }}>Cancel</button>
          <button onClick={handleSave} disabled={!valid}
            style={{ padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: valid ? "pointer" : "default",
              background: valid ? "#7053ea" : "#ccc", color: "#fff", fontFamily: '"Inter", sans-serif' }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD PRODUCTION TIME MODAL ───────────────────────────────────────────────
function AddProductionTimeModal({ onClose, onSave }) {
  const [selMachines, setSelMachines] = useState(new Set(MACHINES));
  const [startH, setStartH]   = useState(8);
  const [endH, setEndH]       = useState(16);
  const [recurring, setRecurring] = useState(true);
  // recurring = apply to selected days of week indefinitely
  const [selDays, setSelDays] = useState(new Set([0,1,2,3,4])); // Mon–Fri by default (0=Mon)
  // non-recurring = specific date range
  const [rangeStart, setRangeStart] = useState(isoDate(new Date()));
  const [rangeEnd,   setRangeEnd]   = useState(isoDate(new Date()));

  const totalH = endH - startH;
  const valid = selMachines.size > 0 && totalH > 0 && (recurring ? selDays.size > 0 : rangeStart <= rangeEnd);

  function toggleMachine(m) {
    setSelMachines(prev => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });
  }
  function toggleDay(d) {
    setSelDays(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n; });
  }

  function handleSave() {
    // Build list of { machine, dateKey } pairs to apply
    const entries = [];
    if (recurring) {
      // Apply to a rolling 6-month window for days matching selDays
      const from = new Date(); from.setHours(0,0,0,0);
      const to   = new Date(); to.setMonth(to.getMonth() + 6);
      for (const cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
        const dow = (cur.getDay() + 6) % 7; // convert Sun=0 to Mon=0
        if (selDays.has(dow)) {
          [...selMachines].forEach(m => entries.push({ machine: m, key: isoDate(new Date(cur)) }));
        }
      }
    } else {
      const from = new Date(rangeStart); const to = new Date(rangeEnd);
      for (const cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
        [...selMachines].forEach(m => entries.push({ machine: m, key: isoDate(new Date(cur)) }));
      }
    }
    onSave(entries, startH, endH, recurring ? { days: [...selDays].sort((a, b) => a - b) } : null);
    onClose();
  }

  const S = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" },
    modal:   { background: "#fff", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 24, width: 460, maxWidth: "95vw" },
    label:   { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 4, display: "block" },
    select:  { fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%" },
    chip:    (active) => ({ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer", border: "1px solid",
      background: active ? "#4caf50" : "#fff", color: active ? "#fff" : "#666", borderColor: active ? "#4caf50" : "#ddd" }),
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Add production time</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Set when machines are available to run operations</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa", lineHeight: 1 }}>✕</button>
        </div>

        {/* Machine selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Machines</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button onClick={() => setSelMachines(selMachines.size === MACHINES.length ? new Set() : new Set(MACHINES))}
              style={{ ...S.chip(selMachines.size === MACHINES.length), fontSize: 10 }}>All</button>
            {MACHINES.map(m => (
              <button key={m} onClick={() => toggleMachine(m)} style={S.chip(selMachines.has(m))}>{m}</button>
            ))}
          </div>
        </div>

        {/* Time range */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Start time</label>
            <select value={startH} onChange={e => setStartH(Number(e.target.value))} style={S.select}>
              {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, color: "#bbb", fontSize: 16 }}>→</div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>End time</label>
            <select value={endH} onChange={e => setEndH(Number(e.target.value))} style={S.select}>
              {HR_OPTS.map(h => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
          </div>
          {totalH > 0
            ? <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, fontSize: 11, color: "#4caf50", fontWeight: 600, whiteSpace: "nowrap" }}>= {totalH}h</div>
            : <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8, fontSize: 11, color: "#e53935", whiteSpace: "nowrap" }}>invalid</div>}
        </div>

        {/* Recurring toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[["Recurring", true], ["Specific dates", false]].map(([label, val]) => (
            <button key={label} onClick={() => setRecurring(val)}
              style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid",
                background: recurring === val ? "#1a1a2e" : "#fff",
                color: recurring === val ? "#fff" : "#555",
                borderColor: recurring === val ? "#1a1a2e" : "#ddd", fontWeight: recurring === val ? 600 : 400 }}>
              {label}
            </button>
          ))}
        </div>

        {recurring ? (
          /* Day-of-week picker */
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Repeat on</label>
            <div style={{ display: "flex", gap: 6 }}>
              {DOW_LABELS.map((d, i) => (
                <button key={i} onClick={() => toggleDay(i)} style={{ ...S.chip(selDays.has(i)), width: 40, textAlign: "center", padding: "4px 0" }}>{d.slice(0,2)}</button>
              ))}
            </div>
          </div>
        ) : (
          /* Date range picker */
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>From</label>
              <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)}
                style={{ fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>To</label>
              <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}
                style={{ fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #ddd", width: "100%" }} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer", color: "#555" }}>Cancel</button>
          <button onClick={handleSave} disabled={!valid}
            style={{ padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: valid ? "pointer" : "default",
              background: valid ? "#4caf50" : "#ccc", color: "#fff" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── RECURRING RESIZE PROMPT ────────────────────────────────────────────────
// Shown after a recurring planned-production-time is resized by dragging a handle.
// Lets the planner choose how far the change reaches (just this date, or this and
// every following date) and which machines it applies to.
function RecurringResizeModal({ machine, date, win, newStartH, newEndH, machineOptions, onClose, onApply }) {
  const [scope, setScope] = useState("this");           // "this" | "following"
  const [selMachines, setSelMachines] = useState(new Set([machine]));
  const dayLabel = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  const valid = selMachines.size > 0;

  function toggleMachine(m) {
    setSelMachines(prev => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });
  }

  const S = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 410, display: "flex", alignItems: "center", justifyContent: "center" },
    modal:   { background: "#fff", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 24, width: 420, maxWidth: "95vw", fontFamily: '"Inter", sans-serif' },
    label:   { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 6, display: "block" },
    chip:    (active) => ({ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer", border: "1px solid",
      background: active ? "#4caf50" : "#fff", color: active ? "#fff" : "#666", borderColor: active ? "#4caf50" : "#ddd" }),
    option:  (active) => ({ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 8, cursor: "pointer",
      border: `1px solid ${active ? "#4caf50" : "#ddd"}`, background: active ? "#f1f8f2" : "#fff", marginBottom: 8 }),
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Edit recurring production time</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
          Change to <strong>{hh(newStartH)} – {hh(newEndH)}</strong> ({Math.max(0, newEndH - newStartH)}h). This is a recurring slot —
          choose how far the change should apply.
        </div>

        {/* Scope */}
        <div style={{ marginBottom: 16 }}>
          <div style={S.option(scope === "this")} onClick={() => setScope("this")}>
            <input type="radio" checked={scope === "this"} readOnly style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>This date only</div>
              <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>Only {dayLabel} changes; other days keep the recurring time.</div>
            </div>
          </div>
          <div style={S.option(scope === "following")} onClick={() => setScope("following")}>
            <input type="radio" checked={scope === "following"} readOnly style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>This and following dates</div>
              <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>{dayLabel} and every later recurring day change.</div>
            </div>
          </div>
        </div>

        {/* Machines */}
        <div style={{ marginBottom: 20 }}>
          <label style={S.label}>Apply to machines</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {machineOptions.map(m => (
              <button key={m} onClick={() => toggleMachine(m)} style={S.chip(selMachines.has(m))}>{m}</button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer", color: "#555" }}>Cancel</button>
          <button onClick={() => valid && onApply(scope, [...selMachines])} disabled={!valid}
            style={{ padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: valid ? "pointer" : "default",
              background: valid ? "#4caf50" : "#ccc", color: "#fff" }}>Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── PLANNING PAGE ────────────────────────────────────────────────────────────
function PlanningPage({ flow = "vision", workOrders, onAssignOp, onUnassignOp, onReleaseToShopfloor, onUpdateOp, prodWindows, setProdWindows, downtimes, setDowntimes, shifts, setShifts }) {
  // Q3 flow: shifts come pre-created for Cell A and the production time (PPT) always mirrors
  // the shift — the user manages only shifts, never PPT separately.
  const isQ3 = flow === "q3";
  const [viewMode, setViewMode]   = useState("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dragging, setDragging]   = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  // Mouse-based drag of an already-placed op (move or edge-resize). Native HTML5 DnD is
  // unreliable for this (pointer-events on the source cancels the drag), so placed jobs
  // use raw mouse events instead. Shape: { woId, opId, mode, estHours, grabOffsetH,
  // fixedStartMs, fixedEndMs }.
  const [opDrag, setOpDrag] = useState(null);
  // True while a placed job is being dragged over the Plan OPs panel — releasing there
  // cancels the planning (unassigns the op back to the panel) instead of moving it.
  const [opDragOverPanel, setOpDragOverPanel] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  // Plan OPs panel is user-resizable by dragging its left edge. PANEL_W is the default.
  const [panelW, setPanelW] = useState(PANEL_W);
  const [panelResizing, setPanelResizing] = useState(false);
  const startPanelResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = panelW;
    setPanelResizing(true);
    const onMove = (ev) => {
      // Dragging left (smaller clientX) widens the panel; clamp to a sensible range.
      const next = Math.min(560, Math.max(220, startW + (startX - ev.clientX)));
      setPanelW(next);
    };
    const onUp = () => {
      setPanelResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const [hourColPx, setHourColPx] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  // prodWindows now lives on App so it survives page navigation. See props above.
  // drag-to-draw state: { machine, date, anchorH, currentH }
  const [drawing, setDrawing] = useState(null);
  // drag-to-draw shift state (top band): same shape as `drawing` but opens AddShiftModal on release
  const [drawingShift, setDrawingShift] = useState(null);
  // window popover state: { machine, date, win, anchorRect }
  const [winPopover, setWinPopover] = useState(null);
  // active window resize-by-handle drag: { machine, date, win, edge:"start"|"end", startH, endH }
  const [winResize, setWinResize] = useState(null);
  // recurring-resize prompt shown on release: { machine, date, win, newStartH, newEndH }
  const [recurResize, setRecurResize] = useState(null);
  // add/edit downtime modal state: { machine, date, win, existing? }
  const [addDowntimeFor, setAddDowntimeFor] = useState(null);
  // shift modal + Add-button dropdown
  const [showAddShiftModal, setShowAddShiftModal] = useState(false);
  // optional prefill for the shift modal when opened via drag — { machine, date, startH, endH }
  const [pendingShift, setPendingShift] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);
  useEffect(() => {
    if (!addMenuOpen) return;
    function handler(e) { if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setAddMenuOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [addMenuOpen]);

  function getShifts(machine, date) {
    const key = isoDate(date);
    const stored = shifts?.[machine]?.[key];
    const list = Array.isArray(stored) ? stored : (stored && typeof stored === "object" && stored !== null) ? [stored] : [];
    if (isQ3) {
      // Cell A machines get a virtual weekday Day shift (08:00–16:00) out of the box.
      // Editing/deleting it stores a concrete entry flagged `suppressDefault`, which hides the
      // virtual default for that day. An edit override carries real times (renders); a delete
      // override is zero-width (renders as nothing). Both stay in the list, mirroring getWindows.
      const suppress = list.some(s => s.suppressDefault);
      const def = (!suppress && CELLS[machine] === "Cell A") ? defaultWindowForDate(date) : null;
      const withDef = def
        ? [{ id: `default-shift-${machine}`, startH: def.startH, endH: def.endH, category: "Day", isDefault: true }, ...list]
        : list;
      return withDef.sort((a, b) => a.startH - b.startH);
    }
    return list;
  }

  function handleShiftSave(entries, startH, endH, category, recurring) {
    const sharedId = recurring ? genId() : null;
    setShifts(prev => {
      const next = { ...prev };
      entries.forEach(({ machine, key }) => {
        const id = sharedId || genId();
        const shift = { id, startH, endH, category, ...(recurring ? { recurring } : {}) };
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: [...list, shift].sort((a, b) => a.startH - b.startH) };
      });
      return next;
    });
  }

  // Shift popover + edit/delete
  const [shiftPopover, setShiftPopover] = useState(null);
  function openShiftPopover(e, machine, date, shift) {
    const r = e.currentTarget.getBoundingClientRect();
    setShiftPopover({ machine, date, shift, anchorRect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right } });
  }

  function updateShift(shift, machine, date, changes) {
    setShifts(prev => {
      const next = { ...prev };
      if (shift.isDefault) {
        // Materialise the virtual Cell A default shift into a concrete per-day override.
        const key = isoDate(date);
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        const override = { id: genId(), startH: shift.startH, endH: shift.endH, category: shift.category, ...changes, suppressDefault: true };
        next[machine] = { ...next[machine], [key]: [...list, override].sort((a, b) => a.startH - b.startH) };
        return next;
      }
      if (shift.recurring) {
        Object.keys(next).forEach(m => {
          const dayMap = next[m] || {};
          const updated = {};
          Object.keys(dayMap).forEach(k => {
            const v = dayMap[k];
            if (v === null || v === undefined) { updated[k] = v; return; }
            const arr = Array.isArray(v) ? v : [v];
            updated[k] = arr.map(s => s.id === shift.id ? { ...s, ...changes } : s);
          });
          next[m] = updated;
        });
      } else {
        const key = isoDate(date);
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: list.map(s => s.id === shift.id ? { ...s, ...changes } : s) };
      }
      return next;
    });
  }
  function deleteShift(shift, machine, date) {
    setShifts(prev => {
      const next = { ...prev };
      if (shift.isDefault) {
        // Drop a zero-width suppressDefault marker so the virtual default stops showing this day.
        const key = isoDate(date);
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: [...list, { id: genId(), startH: shift.startH, endH: shift.startH, suppressDefault: true }] };
        return next;
      }
      if (shift.recurring) {
        Object.keys(next).forEach(m => {
          const dayMap = next[m] || {};
          const updated = {};
          Object.keys(dayMap).forEach(k => {
            const v = dayMap[k];
            if (v === null || v === undefined) { updated[k] = v; return; }
            const arr = Array.isArray(v) ? v : [v];
            updated[k] = arr.filter(s => s.id !== shift.id);
          });
          next[m] = updated;
        });
      } else {
        const key = isoDate(date);
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: list.filter(s => s.id !== shift.id) };
      }
      return next;
    });
  }

  function openWinPopover(e, machine, date, win) {
    const r = e.currentTarget.getBoundingClientRect();
    setWinPopover({ machine, date, win, anchorRect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right } });
  }

  function saveDowntime(machine, date, win, dt) {
    setDowntimes(prev => {
      const list = prev[win.id] || [];
      const idx = list.findIndex(x => x.id === dt.id);
      const next = idx >= 0 ? list.map(x => x.id === dt.id ? dt : x) : [...list, dt];
      return { ...prev, [win.id]: next };
    });
  }
  function deleteDowntime(win, dtId) {
    setDowntimes(prev => {
      const list = (prev[win.id] || []).filter(x => x.id !== dtId);
      return { ...prev, [win.id]: list };
    });
  }

  // ── Window helpers ──────────────────────────────────────────────────────────
  // Windows are layered: a virtual "default" weekday 8–16 window is overlaid on top of
  // user-added windows (unless an explicit override carries suppressDefault).
  function getWindows(machine, date) {
    // Q3: production time is not managed independently — it always mirrors the shift.
    if (isQ3) {
      return getShifts(machine, date).map(s => ({ id: `ppt-${s.id}`, startH: s.startH, endH: s.endH, mirrorsShift: true }));
    }
    const key = isoDate(date);
    const stored = prodWindows[machine]?.[key];
    const list = Array.isArray(stored) ? stored : (stored && typeof stored === "object" && stored !== null) ? [stored] : [];
    const suppressDefault = list.some(w => w.suppressDefault);
    const def = !suppressDefault ? defaultWindowForDate(date) : null;
    const result = def
      ? [{ id: `default-${machine}`, startH: def.startH, endH: def.endH, isDefault: true, recurring: { days: [0, 1, 2, 3, 4] } }, ...list]
      : list;
    return result.sort((a, b) => a.startH - b.startH);
  }

  // Method 1: bulk save from Add modal — appends; recurring entries share an id so edits propagate.
  function handleModalSave(entries, startH, endH, recurring) {
    const sharedId = recurring ? genId() : null;
    setProdWindows(prev => {
      const next = { ...prev };
      entries.forEach(({ machine, key }) => {
        const id = sharedId || genId();
        const win = { id, startH, endH, ...(recurring ? { recurring } : {}) };
        next[machine] = { ...next[machine], [key]: appendWindow(next, machine, key, win) };
      });
      return next;
    });
  }

  // Update a window. If recurring, propagate to all windows that share the same id.
  // If it's a virtual default, materialize a per-day override with suppressDefault.
  function updateWindow(machine, date, win, newStartH, newEndH) {
    const key = isoDate(date);
    setProdWindows(prev => {
      const next = { ...prev };
      if (win.isDefault) {
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        const override = { id: genId(), startH: newStartH, endH: newEndH, suppressDefault: true };
        next[machine] = { ...next[machine], [key]: [...list, override].sort((a, b) => a.startH - b.startH) };
      } else if (win.recurring) {
        Object.keys(next).forEach(m => {
          const dayMap = next[m] || {};
          const updated = {};
          Object.keys(dayMap).forEach(k => {
            const v = dayMap[k];
            if (v === null || v === undefined) { updated[k] = v; return; }
            const arr = Array.isArray(v) ? v : [v];
            updated[k] = arr.map(w => w.id === win.id ? { ...w, startH: newStartH, endH: newEndH } : w);
          });
          next[m] = updated;
        });
      } else {
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: list.map(w => w.id === win.id ? { ...w, startH: newStartH, endH: newEndH } : w) };
      }
      return next;
    });
  }

  // Remove a planned production time. For defaults we drop in a zero-width
  // `suppressDefault` placeholder so the day stops rendering the default bar.
  // For recurring user-added windows, every instance sharing the id is removed.
  // Associated downtimes are also cleaned up.
  function deleteWindow(machine, date, win) {
    const key = isoDate(date);
    setProdWindows(prev => {
      const next = { ...prev };
      if (win.isDefault) {
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        const placeholder = { id: genId(), startH: 0, endH: 0, suppressDefault: true };
        next[machine] = { ...next[machine], [key]: [...list, placeholder] };
      } else if (win.recurring) {
        Object.keys(next).forEach(m => {
          const dayMap = next[m] || {};
          const updated = {};
          Object.keys(dayMap).forEach(k => {
            const v = dayMap[k];
            if (v === null || v === undefined) { updated[k] = v; return; }
            const arr = Array.isArray(v) ? v : [v];
            updated[k] = arr.filter(w => w.id !== win.id);
          });
          next[m] = updated;
        });
      } else {
        const existing = next[machine]?.[key];
        const list = Array.isArray(existing) ? existing : (existing && typeof existing === "object") ? [existing] : [];
        next[machine] = { ...next[machine], [key]: list.filter(w => w.id !== win.id) };
      }
      return next;
    });
    setDowntimes(prev => {
      const next = { ...prev };
      delete next[win.id];
      return next;
    });
  }

  // Downtime helpers ─────────────────────────────────────────────────────────
  function getWinDowntimes(win) {
    return downtimes[win.id] || [];
  }
  function getDeductibleH(win) {
    return getWinDowntimes(win).reduce((s, dt) => {
      if (!dt.deduct) return s;
      return s + (dt.mode === "exact" ? (dt.endH - dt.startH) : (dt.durationH || 0));
    }, 0);
  }
  // For a PPT win, list every machine it currently applies to (derived from prodWindows by id).
  function getMachinesForPpt(win, clickedMachine) {
    if (win.isDefault || !win.recurring) return [clickedMachine];
    const set = new Set();
    Object.entries(prodWindows).forEach(([m, dayMap]) => {
      Object.values(dayMap || {}).forEach(v => {
        const arr = Array.isArray(v) ? v : (v && typeof v === "object") ? [v] : [];
        if (arr.some(w => w.id === win.id)) set.add(m);
      });
    });
    return [...set];
  }

  // Walk forward through windows, splitting an op across them.
  function computeSegments(machine, dayDate, dropHour, estHours) {
    const segments = [];
    let remaining = estHours;
    const curDate = new Date(dayDate); curDate.setHours(0, 0, 0, 0);
    let curHour = dropHour;
    for (let i = 0; i < 180 && remaining > 0; i++) {
      const windows = getWindows(machine, curDate);
      for (const win of windows) {
        if (win.endH <= curHour) continue;
        const segStartH = Math.max(curHour, win.startH);
        if (segStartH >= win.endH) continue;
        const segHours = Math.min(remaining, win.endH - segStartH);
        const { start, end } = addHours(isoDate(curDate), segStartH, segHours);
        segments.push({ start, end });
        remaining -= segHours;
        curHour = segStartH + segHours;
        if (remaining <= 0) break;
      }
      if (remaining > 0) {
        curDate.setDate(curDate.getDate() + 1);
        curHour = 0;
      }
    }
    if (segments.length === 0) {
      const { start, end } = addHours(isoDate(dayDate), dropHour, estHours);
      segments.push({ start, end });
    }
    return segments;
  }

  // Measure hourColPx via a callback ref on the first ops-row td
  const roRef = useRef(null);
  const firstTdRef = useCallback(node => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) return;
    const measure = () => setHourColPx(node.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    roRef.current = ro;
  }, [viewMode, panelOpen]);

  const weekStart   = getMonday(currentDate);
  const days        = getWeekDays(weekStart);
  const displayDays = viewMode === "day" ? [currentDate] : days;
  const totalHourCols = displayDays.length * 24;

  const unplanned = workOrders.flatMap(wo => wo.operations.filter(op => !op.plannedStart).map(op => ({ ...op, woId: wo.id })));
  // Q3 lists every operation in the Plan OPs panel (any status) so the planner can change
  // status at any point; other flows list only the not-yet-planned ops.
  const panelOps = isQ3 ? workOrders.flatMap(wo => wo.operations.map(op => ({ ...op, woId: wo.id }))) : unplanned;
  // One entry per planned op (not per segment) — the bar renders as a single block
  // spanning first segment start → last segment end, so multi-window ops aren't cut up.
  // We also clip to the visible view, so an op that started before the view (or extends
  // past it) renders a continuation chunk anchored to the view edge.
  const viewStartDate = displayDays[0];
  const viewEndDate = new Date(displayDays[displayDays.length - 1]);
  viewEndDate.setHours(0, 0, 0, 0); viewEndDate.setDate(viewEndDate.getDate() + 1);
  const viewStartT = new Date(viewStartDate); viewStartT.setHours(0, 0, 0, 0);

  const visibleOps = workOrders.flatMap(wo =>
    wo.operations.filter(op => op.machine && op.segments?.length).map(op => {
      // Q3: production time can change after an op is planned (e.g. a shift added over the
      // job's time), so re-lay the run time from the original start across the CURRENT windows.
      // The bar then shrinks to fit once the added shift makes the run time contiguous.
      const anchor = new Date(op.segments[0].start);
      const segs = isQ3 ? computeSegments(op.machine, anchor, anchor.getHours() + anchor.getMinutes() / 60, op.estHours) : op.segments;
      const opStart = new Date(segs[0].start);
      const opEnd   = new Date(segs[segs.length - 1].end);
      if (opEnd <= viewStartT || opStart >= viewEndDate) return null;
      const visStart = opStart >= viewStartT ? opStart : viewStartT;
      const visEnd   = opEnd   <= viewEndDate ? opEnd   : viewEndDate;
      return {
        woId: wo.id, opId: op.id, label: op.label, estRunTime: op.estRunTime,
        estHours: op.estHours, machine: op.machine, status: op.status, planStatus: op.planStatus,
        priority: op.priority,
        segments: segs,
        visStart, visEnd,
        visDate: `${visStart.getFullYear()}-${String(visStart.getMonth() + 1).padStart(2, "0")}-${String(visStart.getDate()).padStart(2, "0")}`,
        visHour: visStart.getHours(),
        visFrac: visStart.getMinutes() / 60,
        visHours: (visEnd - visStart) / 3600000,
        clippedLeft:  opStart < viewStartT,
        clippedRight: opEnd   > viewEndDate,
      };
    }).filter(Boolean)
  );

  // Order jobs within a machine's stack: explicit priority first, then start time, then OP number.
  const opNumOf = (label) => { const n = parseInt(String(label).replace(/\D/g, ""), 10); return Number.isFinite(n) ? n : 0; };
  const opLaneCompare = (a, b) =>
    ((a.priority ?? Infinity) - (b.priority ?? Infinity))
    || (a.visStart - b.visStart)
    || (opNumOf(a.label) - opNumOf(b.label));

  function prev() { const d = new Date(currentDate); d.setDate(d.getDate() - (viewMode === "week" ? 7 : 1)); setCurrentDate(d); }
  function next() { const d = new Date(currentDate); d.setDate(d.getDate() + (viewMode === "week" ? 7 : 1)); setCurrentDate(d); }
  // When drag starts on an on-grid op card, remember the offset (in hours) from the
  // card's left edge to the cursor. The card's *left edge* becomes the drop anchor —
  // not the cursor position — so what you see is what gets placed.
  // Side-panel cards don't have an hour-width, so offset stays 0.
  function onDragStartOp(op, e) {
    let dragOffsetH = 0;
    if (e && hourColPx > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      dragOffsetH = (e.clientX - rect.left) / hourColPx;
    }
    setDragging({ ...op, dragOffsetH });
  }

  // Map a cursor (day, hour + fraction-within-hour) back to the snapped position where
  // the card's left edge will land. The result `startH` is a fractional hour snapped to
  // 15-minute units (e.g. 6.25 = 06:15), relative to the returned `dayDate`'s midnight.
  function snapFromCursor(dayDate, hour, fracH = 0) {
    const offset = (dragging?.dragOffsetH) || 0;
    const base = new Date(dayDate); base.setHours(0, 0, 0, 0);
    // hours from midnight where the card's left edge sits, then snap to the 15-min grid
    const snappedRel = Math.round((hour + fracH - offset) / SNAP_H) * SNAP_H;
    const startMs = base.getTime() + snappedRel * 3600000;
    const effDay = new Date(startMs); effDay.setHours(0, 0, 0, 0);
    const startH = (startMs - effDay.getTime()) / 3600000;
    return { dayDate: effDay, startH };
  }

  // Fraction (0..1) of the cursor's position within an hour cell.
  function fracInCellFromEvent(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return 0;
    return Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width));
  }

  function onDropSlot(machine, dayDate, hour, fracH = 0) {
    if (!dragging) return;
    const snap = snapFromCursor(dayDate, hour, fracH);
    const segments = computeSegments(machine, snap.dayDate, snap.startH, dragging.estHours || 8);
    onAssignOp(dragging.woId, dragging.id, machine, segments);
    setDragging(null); setHoveredSlot(null);
  }

  // ── Placed-op mouse drag (move + restack) ──────────────────────────────────
  // Jobs are a fixed size on the calendar (run time is set on the Work orders page).
  // Dragging sideways moves the job in time; dragging up/down restacks it into another
  // lane, which is the priority order shown on the operator tablet.
  // Write a placed op's new position to shared state (reflects to the Work orders page).
  function commitOp(woId, opId, machine, dayDate, startH, estHours) {
    const est = Math.max(SNAP_H, Math.round(estHours * 4) / 4);
    const segments = computeSegments(machine, dayDate, startH, est);
    const plannedStart = segments[0]?.start ?? null;
    const plannedEnd = segments[segments.length - 1]?.end ?? null;
    // Re-scheduling an op (moving it earlier/later) invalidates a prior confirmation: if it was
    // already "Scheduled" or "Planned" (Ready), drop it back to "In planning" (Planning) so the
    // planner re-confirms the new slot.
    const current = workOrders.find(wo => wo.id === woId)?.operations.find(o => o.id === opId);
    const revert = current && (current.planStatus === "Scheduled" || current.planStatus === "Planned");
    onUpdateOp && onUpdateOp(woId, opId, { machine, segments, plannedStart, plannedEnd,
      estHours: est, estRunTime: fmtRunTime(est), status: "Planned",
      ...(revert ? { planStatus: "In planning" } : {}) });
  }
  function startOpMove(e, p) {
    e.stopPropagation(); e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const grabOffsetH = hourColPx > 0 ? (e.clientX - rect.left) / hourColPx : 0;
    // Offset (px) from the card's top to the cursor, so vertical dragging feels anchored.
    const grabOffsetY = e.clientY - rect.top;
    setOpDrag({ woId: p.woId, opId: p.opId, estHours: p.estHours || 8, grabOffsetH, grabOffsetY,
      machine: p.machine, topPx: null });
  }
  // Cursor moved over an ops-row cell while dragging a placed op. Nothing is committed yet —
  // we just track where it would land (X = time, Y = lane) so a ghost + start-time tooltip can
  // be drawn while the original bar stays put in the background. Applied on release.
  function onOpDragMove(machine, day, hour, e) {
    if (!opDrag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = rect.width ? Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
    const base = new Date(day); base.setHours(0, 0, 0, 0);
    const snappedRel = Math.round((hour + frac - opDrag.grabOffsetH) / SNAP_H) * SNAP_H;
    const startMs = base.getTime() + snappedRel * 3600000;
    const eff = new Date(startMs); eff.setHours(0, 0, 0, 0);
    const topPx = Math.max(0, e.clientY - rect.top - (opDrag.grabOffsetY || 0));
    setOpDrag(prev => prev && { ...prev, previewMachine: machine, previewDay: eff,
      previewStartH: (startMs - eff.getTime()) / 3600000, topPx });
  }
  function onOpDragMouseUp() {
    if (!opDrag) return;
    const d = opDrag;
    setOpDrag(null);
    // Released over the Plan OPs panel → cancel the planning (unassign back to the panel).
    if (opDragOverPanel) { setOpDragOverPanel(false); onUnassignOp && onUnassignOp(d.woId, d.opId); return; }
    if (d.previewStartH == null || !d.previewMachine) return;
    // Commit the new time/machine.
    commitOp(d.woId, d.opId, d.previewMachine, d.previewDay, d.previewStartH, d.estHours);
    // Restack: which lane did it land in? Reassign priority for every job on that machine.
    const targetLane = Math.max(0, Math.round(((d.topPx ?? 0) - 3) / LANE_H));
    const list = visibleOps.filter(o => o.machine === d.previewMachine).sort(opLaneCompare);
    const without = list.filter(o => !(o.woId === d.woId && o.opId === d.opId));
    const dragged = list.find(o => o.woId === d.woId && o.opId === d.opId) || { woId: d.woId, opId: d.opId };
    const at = Math.min(targetLane, without.length);
    without.splice(at, 0, dragged);
    without.forEach((o, i) => onUpdateOp && onUpdateOp(o.woId, o.opId, { priority: i }));
  }

  // Method 2: drag-to-draw on window bar
  // We capture mousedown/mousemove/mouseup on the window row cells.
  // Mousedown on an existing bar or label is captured by those elements (stopPropagation),
  // so a drag can only start in a truly empty area of the row. We also reject ranges that
  // overlap an existing ppt window — no stacking new ppt on top of an old one.
  function rangeOverlapsAnyWindow(machine, date, startH, endH) {
    return getWindows(machine, date).some(w => w.endH > w.startH && !(w.endH <= startH || w.startH >= endH));
  }
  function onWinMouseDown(machine, date, hour) {
    if (isQ3) return; // PPT is shift-driven in Q3 — no manual drawing.
    setDrawing({ machine, date, anchorH: hour, currentH: hour });
  }
  // Drag a start/end handle on an existing ppt across hour cells.
  function rangeOverlapsOthers(machine, date, startH, endH, exceptId) {
    return getWindows(machine, date).some(w => w.id !== exceptId && w.endH > w.startH && !(w.endH <= startH || w.startH >= endH));
  }
  function startWinResize(machine, date, win, edge) {
    setWinPopover(null);
    setWinResize({ machine, date, win, edge, startH: win.startH, endH: win.endH });
  }
  function onWinMouseEnter(machine, date, hour) {
    if (winResize) {
      if (winResize.machine !== machine || isoDate(winResize.date) !== isoDate(date)) return;
      setWinResize(prev => {
        if (!prev) return prev;
        let { startH, endH } = prev;
        if (prev.edge === "start") startH = Math.max(0, Math.min(hour, endH - 1));
        else                        endH = Math.min(24, Math.max(hour + 1, startH + 1));
        if (rangeOverlapsOthers(machine, date, startH, endH, prev.win.id)) return prev;
        return { ...prev, startH, endH };
      });
      return;
    }
    if (!drawing || drawing.machine !== machine || isoDate(drawing.date) !== isoDate(date)) return;
    const nextStartH = Math.min(drawing.anchorH, hour);
    const nextEndH   = Math.max(drawing.anchorH, hour) + 1;
    // Block preview expansion into any existing ppt — preview clamps at the bar edge.
    if (rangeOverlapsAnyWindow(machine, date, nextStartH, nextEndH)) return;
    setDrawing(prev => ({ ...prev, currentH: hour }));
  }
  function onWinMouseUp() {
    if (!drawing) return;
    // A bare click (no movement to another cell) shouldn't create a new ppt — drag required.
    const moved = drawing.currentH !== drawing.anchorH;
    const startH = Math.min(drawing.anchorH, drawing.currentH);
    const endH   = Math.max(drawing.anchorH, drawing.currentH) + 1;
    if (moved && endH > startH && !rangeOverlapsAnyWindow(drawing.machine, drawing.date, startH, endH)) {
      const key = isoDate(drawing.date);
      const win = { id: genId(), startH, endH };
      setProdWindows(prev => ({ ...prev, [drawing.machine]: { ...prev[drawing.machine], [key]: appendWindow(prev, drawing.machine, key, win) } }));
    }
    setDrawing(null);
  }

  // Release a resize-by-handle drag. No change → no-op. Recurring (non-default)
  // windows open a prompt to pick scope + machines; everything else applies directly.
  function onWinResizeMouseUp() {
    if (!winResize) return;
    const { machine, date, win, startH, endH } = winResize;
    setWinResize(null);
    if (endH <= startH || (startH === win.startH && endH === win.endH)) return;
    if (win.recurring && !win.isDefault) {
      setRecurResize({ machine, date, win, newStartH: startH, newEndH: endH });
    } else {
      updateWindow(machine, date, win, startH, endH);
    }
  }

  // Apply a recurring-resize after the planner picks scope + machines.
  // "this": detach just this date (per machine) into a one-off override.
  // "following": retime every instance sharing the id on/after this date (per machine).
  function applyRecurringResize(scope, machines, newStartH, newEndH) {
    const { date, win } = recurResize;
    const fromKey = isoDate(date);
    const mset = new Set(machines);
    setProdWindows(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(m => {
        if (!mset.has(m)) return;
        const dayMap = next[m] || {};
        const updated = {};
        Object.keys(dayMap).forEach(k => {
          const v = dayMap[k];
          if (v === null || v === undefined) { updated[k] = v; return; }
          const arr = Array.isArray(v) ? v : [v];
          updated[k] = arr.map(w => {
            if (w.id !== win.id) return w;
            if (scope === "this") {
              if (k !== fromKey) return w;
              const { recurring, ...rest } = w; // detach this date from the series
              return { ...rest, id: genId(), startH: newStartH, endH: newEndH };
            }
            // "following": retime this date and every later one
            if (k < fromKey) return w;
            return { ...w, startH: newStartH, endH: newEndH };
          });
        });
        next[m] = updated;
      });
      return next;
    });
    setRecurResize(null);
  }

  // Compute drawing preview for a given machine+date cell.
  // Suppress the preview until the user has actually moved to a different cell —
  // a plain click without a drag shouldn't flash a 1-hour ghost.
  function getDrawingPreview(machine, date) {
    if (!drawing) return null;
    if (drawing.machine !== machine || isoDate(drawing.date) !== isoDate(date)) return null;
    if (drawing.currentH === drawing.anchorH) return null;
    const startH = Math.min(drawing.anchorH, drawing.currentH);
    const endH   = Math.max(drawing.anchorH, drawing.currentH) + 1;
    return { startH, endH };
  }

  // Shift drag-to-draw on the top band. Shifts may overlap each other, so no
  // overlap check — the released drag just opens AddShiftModal pre-filled.
  function onShiftMouseDown(machine, date, hour) {
    setDrawingShift({ machine, date, anchorH: hour, currentH: hour });
  }
  function onShiftMouseEnter(machine, date, hour) {
    if (!drawingShift || drawingShift.machine !== machine || isoDate(drawingShift.date) !== isoDate(date)) return;
    setDrawingShift(prev => ({ ...prev, currentH: hour }));
  }
  function onShiftMouseUp() {
    if (!drawingShift) return;
    const moved = drawingShift.currentH !== drawingShift.anchorH;
    const startH = Math.min(drawingShift.anchorH, drawingShift.currentH);
    const endH   = Math.max(drawingShift.anchorH, drawingShift.currentH) + 1;
    if (moved && endH > startH) {
      setPendingShift({ machine: drawingShift.machine, date: drawingShift.date, startH, endH });
      setShowAddShiftModal(true);
    }
    setDrawingShift(null);
  }
  function getShiftDrawingPreview(machine, date) {
    if (!drawingShift) return null;
    if (drawingShift.machine !== machine || isoDate(drawingShift.date) !== isoDate(date)) return null;
    if (drawingShift.currentH === drawingShift.anchorH) return null;
    const startH = Math.min(drawingShift.anchorH, drawingShift.currentH);
    const endH   = Math.max(drawingShift.anchorH, drawingShift.currentH) + 1;
    return { startH, endH };
  }

  const headerLabel = viewMode === "day"
    ? currentDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    : `Week ${getISOWeek(days[0])},  ${days[0].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} - ${days[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

  const cellGroups = {};
  MACHINES.forEach(m => { const c = CELLS[m]; if (!cellGroups[c]) cellGroups[c] = []; cellGroups[c].push(m); });
  const showHourLabel = viewMode === "day";
  // Q3 week view drops per-hour timeslots — it shows only faint gridlines every 6h.
  const q3Week = isQ3 && viewMode === "week";
  // Q3 shift box is taller (light purple, shows the start time under the category) — derive the
  // row geometry locally so the other flows keep their original constants.
  const shiftBoxH = isQ3 ? 34 : SHIFT_LABEL_H;
  const winRowH = isQ3 ? (shiftBoxH + WIN_LABEL_H) : WIN_ROW_H;

  return (
    <div style={{ display: "flex", height: "100%", fontFamily: '"Inter", sans-serif', overflow: "hidden", flexDirection: "row" }}
      onMouseUp={() => { onWinMouseUp(); onShiftMouseUp(); onWinResizeMouseUp(); onOpDragMouseUp(); }}
      onMouseLeave={() => { onWinMouseUp(); onShiftMouseUp(); onWinResizeMouseUp(); onOpDragMouseUp(); }}>

      {/* Page content column — flexes to fill the space left by the Plan OPs panel, so
          opening/closing the panel responsively resizes the headers + calendar beside it. */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── Main header (60px) ─────────────────────────────────────────────── */}
      <div style={{ height: 60, padding: "0 16px", background: "#fff", borderBottom: "1px solid #ececec",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontFamily: '"Inter", sans-serif', fontSize: 18, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.18px", color: "#181233" }}>
          Production Planning
        </h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center", height: 40 }}>
          {/* Settings (icon-only) */}
          <button title="Settings"
            style={{ width: 40, height: 40, background: "#fff", border: "1px solid #bebfd0", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <MIcon name="settings" size={20} color="#181233" />
          </button>
          {/* + Add dropdown menu */}
          <div ref={addMenuRef} style={{ position: "relative" }}>
            <button onClick={() => setAddMenuOpen(o => !o)}
              style={{ height: 40, padding: "12px 16px", background: addMenuOpen ? "#f5f5f5" : "#fff", border: "1px solid #d9d9d9",
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
              <MIcon name="add" size={20} color="#181233" />
              Add
            </button>
            {addMenuOpen && (
              <div style={{ position: "absolute", top: 44, right: 0, background: "#fff",
                boxShadow: "0px 4px 30px 0px rgba(0,0,0,0.2)", zIndex: 50, width: 380,
                display: "flex", flexDirection: "column", gap: 16, padding: 16,
                fontFamily: '"Inter", sans-serif' }}>
                <div style={{ fontSize: 16, fontWeight: 600, lineHeight: "24px", color: "#000" }}>
                  What would you like to add?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    // Q3 manages production time via shifts only, so the standalone option is hidden.
                    ...(isQ3 ? [] : [{ icon: "bolt", title: "Production time",
                      desc: "Set the time machines are planned to run.",
                      action: () => setShowAddModal(true) }]),
                    { icon: "groups", title: "Shifts",
                      desc: "Set the time people are planned to work.",
                      action: () => setShowAddShiftModal(true) },
                  ].map(({ icon, title, desc, action }) => (
                    <button key={title} onClick={() => { setAddMenuOpen(false); action(); }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "#000"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "#d9d9d9"}
                      style={{ display: "flex", alignItems: "center", gap: 16, width: "100%",
                        padding: "16px 24px", background: "#fff", border: "1px solid #d9d9d9",
                        textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                      <MIcon name={icon} size={24} color="#000" />
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
                          {title}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 400, lineHeight: "20px", color: "#767676" }}>
                          {desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Plan OPs toggle with count pill */}
          <button onClick={() => setPanelOpen(p => !p)}
            style={{ height: 40, padding: "12px 16px", background: panelOpen ? "#ecdef9" : "#fff",
              border: panelOpen ? "1px solid #7053ea" : "1px solid #e0e0e0",
              display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
              fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
            Plan OPs
            {unplanned.length > 0 && (
              <span style={{ background: "#000", color: "#fff", borderRadius: 40, padding: "0 10px",
                fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px" }}>
                {unplanned.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Pending-release banner ─────────────────────────────────────────── */}
      {/* Q3 has no release-to-shopfloor step — ops go straight to the operator tablet. */}
      {!isQ3 && (() => {
        const plannedCount = workOrders.flatMap(wo => wo.operations).filter(op => op.status === "Planned").length;
        if (plannedCount === 0) return null;
        return (
          <div style={{ padding: "7px 16px", background: "#fff", borderBottom: "1px solid #ececec",
            display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#f4e89a", flexShrink: 0 }} />
              <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", color: "#000", letterSpacing: "0.14px" }}>
                You have <strong>{plannedCount}</strong> planned OP{plannedCount === 1 ? "" : "s"} that {plannedCount === 1 ? "is" : "are"} <strong>pending release</strong>
              </span>
            </div>
            <button onClick={onReleaseToShopfloor}
              style={{ height: 32, padding: "12px 16px", background: "#7053ea", color: "#fff", border: "none",
                cursor: "pointer", fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              Release to shopfloor
            </button>
          </div>
        );
      })()}

      {/* ── Confirm-planning banner (Q3) ───────────────────────────────────── */}
      {/* Q3: bulk-confirm every op still in "Planning" or "Scheduled" to "Ready" in one click. */}
      {isQ3 && (() => {
        const planningOps = workOrders.flatMap(wo => wo.operations.map(op => ({ woId: wo.id, op })))
          .filter(({ op }) => op.planStatus === "In planning" || op.planStatus === "Scheduled");
        if (planningOps.length === 0) return null;
        return (
          <div style={{ padding: "7px 16px", background: "#fff", borderBottom: "1px solid #ececec",
            display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#f4e89a", flexShrink: 0 }} />
              <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", color: "#000", letterSpacing: "0.14px" }}>
                You have <strong>{planningOps.length}</strong> OP{planningOps.length === 1 ? "" : "s"} to mark as <strong>Ready</strong>
              </span>
            </div>
            <button onClick={() => planningOps.forEach(({ woId, op }) => onUpdateOp && onUpdateOp(woId, op.id, { planStatus: "Planned" }))}
              style={{ height: 32, padding: "12px 16px", background: "#7053ea", color: "#fff", border: "none",
                cursor: "pointer", fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              Mark all as Ready
            </button>
          </div>
        );
      })()}

      {/* ── Date bar ───────────────────────────────────────────────────────── */}
      <div style={{ padding: "8px 16px", background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => { const t = new Date(); t.setHours(12, 0, 0, 0); setCurrentDate(t); }}
            style={{ height: 40, padding: "12px 16px", background: "#fff", border: "1px solid #d9d9d9",
              cursor: "pointer", fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>
            Today
          </button>
          <div style={{ display: "flex", gap: 8, alignItems: "center", padding: 4 }}>
            <button onClick={prev} title="Previous"
              style={{ width: 20, height: 20, background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="chevron_left" size={20} color="#181233" />
            </button>
            <button onClick={next} title="Next"
              style={{ width: 20, height: 20, background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="chevron_right" size={20} color="#181233" />
            </button>
          </div>
          <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 16, fontWeight: 600, lineHeight: "24px", color: "#000", whiteSpace: "pre" }}>
            {headerLabel}
          </span>
          <MIcon name="expand_more" size={20} color="#181233" style={{ fontSize: 12 }} />
        </div>
        {/* Day / Week dropdown */}
        <div style={{ position: "relative", width: 114, height: 40 }}>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)}
            style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
              width: "100%", height: "100%", padding: "0 32px 0 12px",
              background: "#efefef", border: "none", cursor: "pointer",
              fontFamily: '"Inter", sans-serif', fontSize: 14, lineHeight: "20px", color: "#000", letterSpacing: "0.14px" }}>
            <option value="day">Day</option>
            <option value="week">Week</option>
          </select>
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <MIcon name="expand_more" size={20} color="#181233" style={{ fontSize: 12 }} />
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Grid */}
        <div style={{ flex: 1, overflowX: "hidden", overflowY: "auto" }}>
          <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
            <colgroup>
              <col style={{ width: MACHINE_COL_W }} />
              {displayDays.flatMap((_, dIdx) =>
                ALL_HOURS.map((_, hIdx) => (
                  <col key={`${dIdx}-${hIdx}`} />
                ))
              )}
            </colgroup>

            {/* Header */}
            <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>
              {viewMode === "week" && (
                <tr style={{ background: q3Week ? "#fff" : "#fafafa" }}>
                  <th style={{ borderRight: "1px solid #e5e5e5", borderBottom: q3Week ? "none" : "1px solid #eee", background: q3Week ? "#fff" : undefined }} />
                  {displayDays.map((day, dIdx) => {
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                    <th key={dIdx} colSpan={24} style={{ textAlign: q3Week ? "left" : "center", fontSize: 11, fontWeight: 700, color: "#444",
                      padding: q3Week ? "6px 0 6px 12px" : "3px 4px", borderRight: dIdx === displayDays.length - 1 ? "none" : "1px solid #ddd", borderBottom: q3Week ? "none" : "1px solid #eee", background: q3Week ? "#fff" : "#fafafa", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {q3Week ? (
                        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#888" }}>{DAY_LABELS[dIdx]}</span>
                          {isToday ? (
                            <span style={{ marginTop: 2, width: 28, height: 28, borderRadius: "50%", background: "#000", color: "#fff",
                              display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700 }}>{day.getDate()}</span>
                          ) : (
                            <span style={{ fontSize: 20, fontWeight: 700, color: "#181233" }}>{day.getDate()}</span>
                          )}
                        </span>
                      ) : <>{DAY_LABELS[dIdx]} {day.getDate()}</>}
                    </th>
                    );
                  })}
                </tr>
              )}
              <tr style={{ background: q3Week ? "#fff" : "#fafafa", borderBottom: "2px solid #e5e5e5" }}>
                <th style={{ borderRight: "1px solid #e5e5e5", background: q3Week ? "#fff" : "#fafafa", padding: "4px 8px", fontSize: 11, color: "#999", textAlign: "left", fontWeight: 500 }}>{q3Week ? "" : "Machine"}</th>
                {displayDays.flatMap((_, dIdx) =>
                  ALL_HOURS.map(h => (
                    <th key={`${dIdx}-${h}`}
                      style={{ padding: q3Week ? 0 : "3px 0 3px 2px", height: q3Week ? 4 : undefined, fontSize: 9, color: "#bbb", fontWeight: 400, textAlign: "left",
                      background: q3Week ? "#fff" : "#fafafa", borderRight: h===23 ? (dIdx === displayDays.length - 1 ? "none" : "1px solid #ddd") : h%6===5 ? "1px solid #e0e0e0" : (q3Week ? "none" : "1px solid #f0f0f0"),
                      overflow: "hidden", whiteSpace: "nowrap", borderBottom: "2px solid #e5e5e5" }}>
                      {showHourLabel ? hh(h) : q3Week ? "" : (h%6===0 ? String(h).padStart(2,"0") : "")}
                    </th>
                  ))
                )}
              </tr>
            </thead>

            <tbody>
              {Object.entries(cellGroups).map(([cell, machines]) => [
                <tr key={`cell-${cell}`} style={{ background: "#f5f5f5" }}>
                  <td colSpan={totalHourCols + 1} style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, color: "#666", borderTop: "1px solid #e5e5e5", borderBottom: "1px solid #e5e5e5" }}>
                    {isQ3
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>{cell}<MIcon name="arrow_drop_up" size={16} color="#999" /></span>
                      : <>▲ {cell}</>}
                  </td>
                </tr>,
                ...machines.flatMap(machine => {
                  let totalPPT = 0;
                  for (const d of displayDays) {
                    for (const w of getWindows(machine, d)) totalPPT += (w.endH - w.startH) - getDeductibleH(w);
                  }
                  // Booked time is the UNION of every job's intervals — when jobs run at the
                  // same time the machine is busy once, not twice, so overlaps count only once.
                  const vStart = viewStartT.getTime(), vEnd = viewEndDate.getTime();
                  const busy = [];
                  for (const wo of workOrders) {
                    for (const op of wo.operations) {
                      if (op.machine !== machine || !op.segments?.length) continue;
                      for (const seg of op.segments) {
                        const vs = Math.max(new Date(seg.start).getTime(), vStart);
                        const ve = Math.min(new Date(seg.end).getTime(), vEnd);
                        if (ve > vs) busy.push([vs, ve]);
                      }
                    }
                  }
                  busy.sort((a, b) => a[0] - b[0]);
                  let usedPPT = 0, curS = null, curE = null;
                  for (const [s, e] of busy) {
                    if (curE === null) { curS = s; curE = e; }
                    else if (s > curE) { usedPPT += (curE - curS) / 3600000; curS = s; curE = e; }
                    else if (e > curE) { curE = e; }
                  }
                  if (curE !== null) usedPPT += (curE - curS) / 3600000;
                  // Cap at total — OPs placed during downtime don't count beyond available PPT.
                  usedPPT = Math.min(usedPPT, totalPPT);
                  // Pack this machine's placed jobs into vertical lanes by TIME: jobs whose
                  // intervals don't overlap share a lane (sit side by side), and a job only drops
                  // to a new lane when it conflicts in time with one already placed above it.
                  // Greedy first-fit over start-sorted intervals keeps the lane count minimal.
                  //
                  // Drag-aware: while a placed job is being dragged onto this machine, it's packed
                  // at its PREVIEW time (not its current one) so the layout shown is the real
                  // result of dropping — a conflicting job gets pushed to a new lane, and the
                  // dragged job's ghost lands on the lane it will actually occupy.
                  const draggedKey = opDrag ? `${opDrag.woId}:${opDrag.opId}` : null;
                  const dragHere = opDrag && opDrag.previewMachine === machine && opDrag.previewStartH != null;
                  let dragPreviewStart = 0;
                  if (dragHere) {
                    const base = new Date(opDrag.previewDay); base.setHours(0, 0, 0, 0);
                    dragPreviewStart = base.getTime() + opDrag.previewStartH * 3600000;
                  }
                  const packItems = visibleOps
                    .filter(o => o.machine === machine && !(dragHere && `${o.woId}:${o.opId}` === draggedKey))
                    .map(o => ({ key: `${o.woId}:${o.opId}`, s: o.visStart.getTime(), e: o.visEnd.getTime(), num: opNumOf(o.label) }));
                  if (dragHere) packItems.push({ key: draggedKey, s: dragPreviewStart,
                    e: dragPreviewStart + (opDrag.estHours || 8) * 3600000, num: 0, dragged: true });
                  packItems.sort((a, b) => (a.s - b.s) || (a.e - b.e) || (a.num - b.num));
                  const laneEnds = []; // last occupied end time (ms) for each lane
                  const laneOf = {};
                  let draggedLane = 0;
                  packItems.forEach(it => {
                    // Reuse the first lane whose last job ends at/before this job starts.
                    let lane = laneEnds.findIndex(end => end <= it.s);
                    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.e); }
                    else laneEnds[lane] = it.e;
                    laneOf[it.key] = lane;
                    if (it.dragged) draggedLane = lane;
                  });
                  // The panel native-drag (HTML5 drag from Plan OPs) isn't part of packItems, so
                  // reserve a trailing lane for its snap-preview ghost.
                  const dragExtra = (dragging && hoveredSlot?.machine === machine) ? 1 : 0;
                  const numLanes = Math.max(1, laneEnds.length + dragExtra);
                  const opsRowH = numLanes * LANE_H + 4;
                  // Ghost preview for a placed job being moved onto this machine — lands on the
                  // lane the packing assigned it, matching exactly where it'll sit after the drop.
                  const movePreviewHere = dragHere
                    ? { lane: draggedLane, startH: opDrag.previewStartH, day: opDrag.previewDay, hours: opDrag.estHours }
                    : null;
                  return [

                  // ── Window row ────────────────────────────────────────────────
                  <tr key={`${machine}-win`} style={{ background: "#fff" }}>
                    <td style={{ width: MACHINE_COL_W, padding: "6px 12px", borderRight: "1px solid #e5e5e5", background: "#fafafa", verticalAlign: "middle" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{machine}</div>
                      <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                        {isQ3
                          // Q3: show how much of the machine's time is still available (total minus booked).
                          ? <>Available:<br /><strong>{fmtDur(totalPPT - usedPPT)}</strong> / {fmtDur(totalPPT)}</>
                          : <>P: <strong>{fmtDur(usedPPT)}</strong> / {fmtDur(totalPPT)}</>}
                      </div>
                    </td>
                    {/* One <td colSpan=24> per day — contains the window bars + Method 2 drag */}
                    {displayDays.map((day, dIdx) => {
                      const windows = getWindows(machine, day);
                      const preview = getDrawingPreview(machine, day);
                      const shiftPreview = getShiftDrawingPreview(machine, day);
                      const hasAny  = windows.length > 0 || !!preview;
                      const dayShifts = getShifts(machine, day);
                      return (
                        <td key={dIdx} colSpan={24}
                          style={{ padding: 0, borderRight: dIdx === displayDays.length - 1 ? "none" : "1px solid #ddd", position: "relative", height: winRowH,
                            background: "#fff", cursor: "crosshair", userSelect: "none" }}
                          title={isQ3 ? "drag to set time" : "Drag the top band to add a shift, or drag below to add production time"}>
                          {/* Top-band hour cells — drag to add a shift */}
                          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: shiftBoxH, display: "flex" }}>
                            {ALL_HOURS.map(h => (
                              <div key={h} style={{ flex: 1, height: "100%" }}
                                onMouseDown={() => onShiftMouseDown(machine, day, h)}
                                onMouseEnter={() => onShiftMouseEnter(machine, day, h)} />
                            ))}
                          </div>
                          {/* Bottom hour cells — drag to add planned production time */}
                          <div style={{ position: "absolute", top: shiftBoxH, left: 0, right: 0, bottom: 0, display: "flex" }}>
                            {ALL_HOURS.map(h => (
                              <div key={h} style={{ flex: 1, height: "100%" }}
                                onMouseDown={() => onWinMouseDown(machine, day, h)}
                                onMouseEnter={() => onWinMouseEnter(machine, day, h)} />
                            ))}
                          </div>
                          {/* Divider between shift band (top) and PPT area (bottom).
                              Hidden in Q3 — shift and PPT are functionally one unit there. */}
                          {!isQ3 && (
                            <div style={{ position: "absolute", top: shiftBoxH, left: 0, right: 0, height: 1,
                              background: "#e5e5e5", pointerEvents: "none", zIndex: 1 }} />
                          )}
                          {/* Shift labels — top band, one per shift on this (machine, day) */}
                          {dayShifts.map((shift, si) => {
                            const sTotal = shift.endH - shift.startH;
                            if (sTotal <= 0) return null;
                            const sLeftPct = (shift.startH / 24) * 100;
                            const sWidthPct = (sTotal / 24) * 100;
                            const isSelected = shiftPopover && shiftPopover.shift.id === shift.id
                              && shiftPopover.machine === machine && isoDate(shiftPopover.date) === isoDate(day);
                            return (
                              <div key={shift.id || si}
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => { e.stopPropagation(); openShiftPopover(e, machine, day, shift); }}
                                style={{ position: "absolute", top: isQ3 ? 0 : 1, left: `${sLeftPct}%`, width: `${sWidthPct}%`, height: shiftBoxH,
                                  background: isQ3 ? "#ece7fb" : "#fff", borderTop: "1px solid #d8d8d8",
                                  borderLeft: "1px solid #000", borderRight: "1px solid #000",
                                  display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 1,
                                  paddingLeft: 6, overflow: "hidden",
                                  pointerEvents: (drawing || drawingShift) ? "none" : "auto", zIndex: 2, boxSizing: "border-box",
                                  cursor: "pointer",
                                  outline: isSelected ? "2px solid #1b5e20" : "none", outlineOffset: isSelected ? 1 : 0 }}
                                title={`${shift.category} shift · ${hh(shift.startH)} – ${hh(shift.endH)} · click for details`}>
                                <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, color: "#000", fontWeight: 600, whiteSpace: "nowrap", pointerEvents: "none", lineHeight: 1.1 }}>
                                  {shift.category}
                                </span>
                                {isQ3 && (
                                  <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 10, color: "#000", fontWeight: 500, whiteSpace: "nowrap", pointerEvents: "none", lineHeight: 1.1 }}>
                                    {hh(shift.startH)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {/* Per-window: white "net hours" label box on top, thin green/red bar below.
                              Both surfaces open the same popover. Red overlays inside the bar represent
                              planned downtime activities (none yet — data shape: win.downtimes = []). */}
                          {windows.map((win, i) => {
                            // While a handle is being dragged, render the live (in-progress) extent.
                            const isResizingThis = winResize && winResize.win.id === win.id
                              && winResize.machine === machine && isoDate(winResize.date) === isoDate(day);
                            const effStartH = isResizingThis ? winResize.startH : win.startH;
                            const effEndH   = isResizingThis ? winResize.endH   : win.endH;
                            const totalH = effEndH - effStartH;
                            const winDts = getWinDowntimes(win);
                            const netH = totalH - getDeductibleH(win);
                            const leftPct = (effStartH / 24) * 100;
                            const widthPct = (totalH / 24) * 100;
                            const isSelected = winPopover && winPopover.win.id === win.id
                              && winPopover.machine === machine && isoDate(winPopover.date) === isoDate(day);
                            // Show drag handles when the ppt is selected (popover open) or being resized.
                            const showHandles = isSelected || isResizingThis;
                            // During any drag, the bars must be click-through so the hour cells beneath
                            // still receive mouse-enter events that drive the drawing/resize tracking.
                            // Q3: the PPT box mirrors the shift and is read-only (manage the shift instead).
                            const barPointer = (drawing || drawingShift || winResize || isQ3) ? "none" : "auto";
                            // Stack: shift band (top) → ppt label → activity bar. No gaps between layers.
                            const shiftBandTop = isQ3 ? 0 : 1;
                            const labelBoxTop = shiftBandTop + shiftBoxH;
                            const barTop = labelBoxTop + WIN_LABEL_H;
                            const handleSpanH = WIN_LABEL_H + WIN_BAR_H;
                            const commonHandlers = {
                              onMouseDown: e => e.stopPropagation(),
                              onClick: e => { e.stopPropagation(); openWinPopover(e, machine, day, win); },
                            };
                            const Handle = ({ edge }) => (
                              <div onMouseDown={e => { e.stopPropagation(); startWinResize(machine, day, win, edge); }}
                                style={{ position: "absolute", top: labelBoxTop, height: handleSpanH,
                                  left: `${edge === "start" ? leftPct : leftPct + widthPct}%`,
                                  width: 12, marginLeft: -6, zIndex: 4, cursor: "ew-resize",
                                  pointerEvents: winResize ? "none" : "auto",
                                  display: "flex", alignItems: "center", justifyContent: "center" }}
                                title={`Drag to adjust ${edge === "start" ? "start" : "end"} time`}>
                                <div style={{ width: 3, height: "100%", background: "#000", borderRadius: 2 }} />
                              </div>
                            );
                            // Black 1px lines at the ppt's start and end markers — applied as
                            // left/right borders on both label box and bar so they form one
                            // continuous tick from top of label to bottom of bar.
                            const tickBorder = { borderLeft: "1px solid #000", borderRight: "1px solid #000" };
                            return (
                              <div key={win.id || i} style={{ display: "contents" }}>
                                {/* Top: white label box showing net production hours */}
                                <div {...commonHandlers}
                                  style={{ position: "absolute", top: labelBoxTop, left: `${leftPct}%`, width: `${widthPct}%`, height: WIN_LABEL_H,
                                    background: "#fff",
                                    borderTop: isQ3 ? "none" : "1px solid #d8d8d8",
                                    ...tickBorder,
                                    borderRadius: 0,
                                    display: "flex", alignItems: "center", paddingLeft: 6,
                                    overflow: "hidden", pointerEvents: barPointer, zIndex: 2, cursor: "pointer",
                                    outline: isSelected ? "2px solid #1b5e20" : "none", outlineOffset: isSelected ? 1 : 0,
                                    boxSizing: "border-box" }}
                                  title={`${hh(effStartH)} – ${hh(effEndH)} · ${netH}h net${netH !== totalH ? ` (${totalH}h ppt)` : ""}`}>
                                  <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, color: "#000", fontWeight: isQ3 ? 500 : 700, whiteSpace: "nowrap", pointerEvents: "none" }}>{netH}h</span>
                                </div>
                                {/* Bottom: thin colored activity bar with red downtime overlays.
                                    Hidden in Q3 — that flow shows only the white PPT box. */}
                                {!isQ3 && (
                                  <div {...commonHandlers}
                                    style={{ position: "absolute", top: barTop, left: `${leftPct}%`, width: `${widthPct}%`, height: WIN_BAR_H,
                                      background: "#37C58B",
                                      ...tickBorder,
                                      borderRadius: 0,
                                      pointerEvents: barPointer, zIndex: 2, cursor: "pointer",
                                      outline: isSelected ? "2px solid #1b5e20" : "none", outlineOffset: isSelected ? 1 : 0,
                                      boxSizing: "border-box" }}
                                    title={`${hh(effStartH)} – ${hh(effEndH)} · click for details`}>
                                    {winDts.filter(dt => dt.mode === "exact").map(dt => {
                                      const dtLeftPct = ((dt.startH - effStartH) / totalH) * 100;
                                      const dtWidthPct = ((dt.endH - dt.startH) / totalH) * 100;
                                      return (
                                        <div key={dt.id}
                                          style={{ position: "absolute", top: 0, left: `${dtLeftPct}%`, width: `${dtWidthPct}%`, height: "100%",
                                            background: "#CE464F", borderRadius: 0, pointerEvents: "none" }}
                                          title={`${dt.reason} · ${hh(dt.startH)}–${hh(dt.endH)}`} />
                                      );
                                    })}
                                  </div>
                                )}
                                {/* Start/end drag handles — appear when selected or resizing */}
                                {showHandles && <Handle edge="start" />}
                                {showHandles && <Handle edge="end" />}
                              </div>
                            );
                          })}
                          {/* Shift drag-to-draw preview (top band) */}
                          {shiftPreview && (() => {
                            const totalH = shiftPreview.endH - shiftPreview.startH;
                            const leftPct = (shiftPreview.startH / 24) * 100;
                            const widthPct = (totalH / 24) * 100;
                            return (
                              <div style={{ position: "absolute", top: 1, left: `${leftPct}%`, width: `${widthPct}%`,
                                height: isQ3 ? shiftBoxH + WIN_LABEL_H : SHIFT_LABEL_H,
                                background: "rgba(112,83,234,0.18)", border: "1px dashed #7053ea",
                                borderRadius: 0, display: "flex", alignItems: "center", paddingLeft: 5,
                                overflow: "hidden", pointerEvents: "none", zIndex: 3, boxSizing: "border-box" }}>
                                <span style={{ fontSize: 9, color: "#1a1a2e", fontWeight: 700, whiteSpace: "nowrap" }}>+{totalH}h shift</span>
                              </div>
                            );
                          })()}
                          {/* PPT drag-to-draw preview (bottom area) */}
                          {preview && (() => {
                            const totalH = preview.endH - preview.startH;
                            const leftPct = (preview.startH / 24) * 100;
                            const widthPct = (totalH / 24) * 100;
                            const previewTop = shiftBoxH + 1;
                            const previewH = winRowH - shiftBoxH - 3;
                            return (
                              <div style={{ position: "absolute", top: previewTop, left: `${leftPct}%`, width: `${widthPct}%`, height: previewH,
                                background: "rgba(76,175,80,0.35)", border: "1px dashed #2e7d32",
                                borderRadius: 3, display: "flex", alignItems: "center", paddingLeft: 5,
                                overflow: "hidden", pointerEvents: "none", zIndex: 3 }}>
                                <span style={{ fontSize: 9, color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>+{totalH}h</span>
                              </div>
                            );
                          })()}
                          {/* Hint when empty — Q3 shows it only as the cell tooltip, not inline text */}
                          {!hasAny && !isQ3 && (
                            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 8,
                              fontSize: 9, color: "#ccc", pointerEvents: "none" }}>drag to set time</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>,

                  // ── Ops row ───────────────────────────────────────────────────
                  <tr key={`${machine}-ops`} style={{ borderBottom: "2px solid #e8e8e8" }}>
                    <td style={{ width: MACHINE_COL_W, padding: "4px 12px 6px", borderRight: "1px solid #e5e5e5", background: "#fafafa", verticalAlign: "top" }} />
                    {displayDays.flatMap((day, dIdx) =>
                      ALL_HOURS.map(hour => {
                        const windows = getWindows(machine, day);
                        const inWindow = windows.some(w => hour >= w.startH && hour < w.endH);
                        const isHovered = hoveredSlot?.machine === machine
                          && hoveredSlot?.dayDate?.toDateString() === day.toDateString()
                          && hoveredSlot?.hour === hour;
                        const opsHere = visibleOps.filter(p => p.machine === machine && p.visDate === isoDate(day) && p.visHour === hour);
                        // Snap-preview ghost: where the dragged card's *left edge* will land.
                        // `startH` is the snapped fractional start; it renders in the cell whose
                        // integer hour contains it, offset by the minutes-within-the-hour.
                        let snapPreview = null;
                        if (dragging && hoveredSlot && hoveredSlot.machine === machine) {
                          const snap = snapFromCursor(hoveredSlot.dayDate, hoveredSlot.hour, hoveredSlot.fracH || 0);
                          if (isoDate(snap.dayDate) === isoDate(day) && Math.floor(snap.startH) === hour) {
                            snapPreview = { hours: dragging.estHours || 8, fracInCell: snap.startH - hour, startH: snap.startH };
                          }
                        }
                        // Ghost for a placed job being moved here — renders in the cell that holds its
                        // snapped start, at its target lane, with a start-time tooltip.
                        let movePreview = null;
                        if (movePreviewHere && isoDate(movePreviewHere.day) === isoDate(day)
                            && Math.floor(movePreviewHere.startH) === hour) {
                          movePreview = { fracInCell: movePreviewHere.startH - hour, startH: movePreviewHere.startH,
                            hours: movePreviewHere.hours, lane: movePreviewHere.lane };
                        }
                        return (
                          <td key={`${dIdx}-${hour}`} ref={dIdx === 0 && hour === 0 ? firstTdRef : undefined} style={{ height: opsRowH, padding: 0, position: "relative", verticalAlign: "top",
                            background: isHovered && dragging ? "#f0e8ff" : "transparent",
                            transition: "background 0.1s",
                            borderRight: hour===23 ? (dIdx === displayDays.length - 1 ? "none" : "1px solid #ddd") : hour%6===5 ? "1px solid #e0e0e0" : (q3Week ? "none" : "1px solid #f5f5f5") }}
                            onDragOver={e => { e.preventDefault(); setHoveredSlot({ machine, dayDate: day, hour, fracH: fracInCellFromEvent(e) }); }}
                            onDragLeave={() => setHoveredSlot(null)}
                            onDrop={e => onDropSlot(machine, day, hour, fracInCellFromEvent(e))}
                            onMouseMove={e => onOpDragMove(machine, day, hour, e)}>
                            {snapPreview && (
                              <div style={{ position: "absolute", top: 3 + (numLanes - 1) * LANE_H, left: 1 + snapPreview.fracInCell * hourColPx,
                                width: hourColPx > 0 ? snapPreview.hours * hourColPx - 2 : 0,
                                height: LANE_H - 6,
                                background: "rgba(245, 237, 202, 0.7)",
                                border: "1px dashed #8a7a00",
                                pointerEvents: "none", zIndex: 4, boxSizing: "border-box" }}>
                                {/* Start-time tooltip pinned to the left (drop) edge */}
                                <div style={{ position: "absolute", top: -27, left: 0, transform: "translateX(-50%)",
                                  background: "#000", color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1,
                                  padding: "5px 8px", borderRadius: 4, whiteSpace: "nowrap",
                                  fontFamily: '"Inter", sans-serif', pointerEvents: "none" }}>
                                  {hhmm(snapPreview.startH)}
                                  <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                                    borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #000" }} />
                                </div>
                              </div>
                            )}
                            {movePreview && (
                              <div style={{ position: "absolute", top: 3 + movePreview.lane * LANE_H, left: 1 + movePreview.fracInCell * hourColPx,
                                width: hourColPx > 0 ? movePreview.hours * hourColPx - 2 : 0,
                                height: LANE_H - 6,
                                background: "rgba(245, 237, 202, 0.7)",
                                border: "1px dashed #8a7a00",
                                pointerEvents: "none", zIndex: 6, boxSizing: "border-box" }}>
                                {/* Start-time tooltip pinned to the left (drop) edge */}
                                <div style={{ position: "absolute", top: -27, left: 0, transform: "translateX(-50%)",
                                  background: "#000", color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1,
                                  padding: "5px 8px", borderRadius: 4, whiteSpace: "nowrap",
                                  fontFamily: '"Inter", sans-serif', pointerEvents: "none" }}>
                                  {hhmm(movePreview.startH)}
                                  <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                                    borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #000" }} />
                                </div>
                              </div>
                            )}
                            {opsHere.map(p => {
                              const wPx = hourColPx > 0 ? p.visHours * hourColPx - 2 : 0;
                              const split = p.segments.length > 1;
                              const released = p.status === "Not started";
                              // Q3: once the planner confirms an op as "Planned", its calendar bar turns green;
                              // a "Scheduled" op turns blue.
                              const q3Planned = isQ3 && p.planStatus === "Planned";
                              const q3Scheduled = isQ3 && p.planStatus === "Scheduled";
                              const cardBg     = q3Planned ? "#b2e9d2" : q3Scheduled ? "#e8efff" : released ? "#ECECEC" : "#f5edca";
                              const cardFg     = q3Planned ? "#04824e" : q3Scheduled ? "#195dfa" : released ? "#666666" : "#5a5000";
                              const opBorderColor = q3Planned ? "#05a764" : q3Scheduled ? "#195dfa" : released ? "#666666" : "#c8b870";
                              // Vertical lane (priority). While this job is being dragged, the original
                              // stays put and fades into the background; a ghost shows where it'll land.
                              const isDraggingThis = opDrag && opDrag.woId === p.woId && opDrag.opId === p.opId;
                              const lane = laneOf[`${p.woId}:${p.opId}`] || 0;
                              const cardTop = 3 + lane * LANE_H;
                              return (
                                <div key={`${p.woId}-${p.opId}`}
                                  onMouseDown={e => startOpMove(e, p)}
                                  title={`${p.woId} ${p.label} · ${fmtDur(p.estHours)} · drag sideways to move, up/down to set priority${split ? ` (runs across ${p.segments.length} production windows)` : ""}`}
                                  style={{ position: "absolute", top: cardTop, left: 1 + (p.visFrac || 0) * hourColPx, width: wPx || "calc(100% - 2px)", height: LANE_H - 6,
                                    background: cardBg, color: cardFg, opacity: isDraggingThis ? 0.4 : 1,
                                    border: `1px solid ${opBorderColor}`,
                                    borderLeft: p.clippedLeft ? `3px dashed ${opBorderColor}` : `1px solid ${opBorderColor}`,
                                    borderRight: p.clippedRight ? `3px dashed ${opBorderColor}` : `1px solid ${opBorderColor}`,
                                    borderRadius: 0,
                                    padding: "0 8px", fontFamily: '"Inter", sans-serif', fontSize: 11, fontWeight: 500,
                                    overflow: "hidden", whiteSpace: "nowrap", zIndex: 3, cursor: opDrag ? "grabbing" : "grab",
                                    // While ANY drag is in flight (this card's mouse-drag or a panel native-drag),
                                    // the wide card must be click-through so the hour cells beneath receive the
                                    // move/drop events instead of pinning them to its original spot.
                                    pointerEvents: (dragging || opDrag) ? "none" : "auto",
                                    display: "flex", alignItems: "center", gap: 8, boxSizing: "border-box" }}>
                                  {p.clippedLeft && <span style={{ fontSize: 11, opacity: 0.6 }}>‹</span>}
                                  <span>{p.woId} -{p.label}</span>
                                  <span style={{ background: "#fff", border: `1px solid ${opBorderColor}`,
                                    padding: "1px 6px", fontSize: 10, fontWeight: 700, color: cardFg, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                    {fmtDur(p.estHours)}
                                  </span>
                                  {p.clippedRight && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: "auto" }}>›</span>}
                                </div>
                              );
                            })}
                          </td>
                        );
                      })
                    )}
                  </tr>
                  ];
                })
              ])}
            </tbody>
          </table>
        </div>

        </div>{/* ── end Body row ── */}
      </div>{/* ── end page content column ── */}

      {/* ── Plan OPs panel — full page height, sits beside the content column. Toggling
            "Plan OPs" animates its width so it slides in/out and the page resizes. ── */}
      <div style={{ width: panelOpen ? panelW : 0, flexShrink: 0, overflow: "hidden", position: "relative", transition: panelResizing ? "none" : "width 0.25s ease" }}>
          {/* Drag handle — resize the panel by dragging its left edge (fixed to the panel, doesn't scroll) */}
          <div onMouseDown={startPanelResize} title="Drag to resize"
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(112,83,234,0.25)")}
            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, cursor: "col-resize",
              zIndex: 10, background: "transparent", transition: "background 0.15s ease" }} />
          {/* Drop-zone cue: shown while dragging a placed job over the panel. pointerEvents:none
              so the mousemove/mouseup still reach the panel + bubble up to commit the unassign. */}
          {opDrag && opDragOverPanel && (
            <div style={{ position: "absolute", inset: 0, zIndex: 20, background: "rgba(112,83,234,0.08)",
              border: "2px dashed #7053ea", display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none", fontFamily: '"Inter", sans-serif' }}>
              <span style={{ background: "#7053ea", color: "#fff", padding: "8px 14px", borderRadius: 6,
                fontSize: 14, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>Drop to cancel planning</span>
            </div>
          )}
          <div
            onMouseMove={() => { if (opDrag && !opDragOverPanel) { setOpDragOverPanel(true); setOpDrag(prev => prev && { ...prev, previewMachine: null, previewStartH: null }); } }}
            onMouseLeave={() => { if (opDragOverPanel) setOpDragOverPanel(false); }}
            style={{ width: panelW, height: "100%", borderLeft: "1px solid #e5e5e5", background: "#fff", display: "flex", flexDirection: "column", overflowY: "auto" }}>
            <div style={{ padding: "13px 16px", borderBottom: "1px solid #e5e5e5", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "#fff", zIndex: 5 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>Plan OPs</span>
              <button onClick={() => setPanelOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#aaa" }}>✕</button>
            </div>
            <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", fontFamily: '"Inter", sans-serif',
              display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 13, lineHeight: "20px", color: "#181233", letterSpacing: "0.12px" }}>
                <span style={{ fontWeight: 700 }}>Filter: </span>
                <span style={{ fontWeight: 400 }}>{isQ3 ? PLAN_STATUSES.map(q3StatusLabel).join(", ") : "Not planned"}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: "20px", color: "#181233", letterSpacing: "0.12px" }}>
                <span style={{ fontWeight: 700 }}>Sort: </span>
                <span style={{ fontWeight: 400 }}>Due date</span>
              </div>
            </div>
            <div style={{ padding: 10, fontFamily: '"Inter", sans-serif' }}>
              {panelOps.length === 0 ? (
                <div style={{ textAlign: "center", color: "#bbb", fontSize: 12, marginTop: 32 }}>All operations planned ✓</div>
              ) : (() => {
                // Group ops by WO, then sort groups by due date — soonest (closest to today,
                // including overdue) on top; WOs without a due date fall to the bottom.
                const groups = [];
                const seen = {};
                panelOps.forEach(op => {
                  if (seen[op.woId] === undefined) { seen[op.woId] = groups.length; groups.push({ woId: op.woId, ops: [] }); }
                  groups[seen[op.woId]].ops.push(op);
                });
                const dueOf = (woId) => {
                  const d = workOrders.find(w => w.id === woId)?.dueDate;
                  return d ? new Date(d).getTime() : Infinity;
                };
                groups.sort((a, b) => dueOf(a.woId) - dueOf(b.woId));
                return groups.map(({ woId, ops }) => {
                  const wo = workOrders.find(w => w.id === woId);
                  if (!wo) return null;
                  return (
                    <div key={woId} style={{ background: "#f2f2f2", padding: 8, borderRadius: 3, marginBottom: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* WO header */}
                      <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <MIcon name="contract" size={16} color="#000" />
                          <span style={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000" }}>{wo.id}</span>
                        </div>
                        <span style={{ fontSize: 12, lineHeight: "18px", color: "#000", letterSpacing: "0.12px" }}>
                          <span style={{ fontWeight: 600 }}>Due: </span>
                          <span style={{ fontWeight: 400 }}>{wo.dueDate ? formatDateShort(wo.dueDate) : "—"}</span>
                        </span>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 12, lineHeight: "18px", color: "#000", letterSpacing: "0.12px" }}>
                            <span style={{ fontWeight: 600 }}>Product: </span>
                            <span style={{ fontWeight: 400 }}>{wo.product || "—"}</span>
                          </span>
                          <span style={{ fontSize: 12, lineHeight: "18px", color: "#000", letterSpacing: "0.12px" }}>
                            <span style={{ fontWeight: 600 }}>Qty:</span>
                            <span style={{ fontWeight: 400 }}>{` ${wo.targetQty ?? "—"}`}</span>
                          </span>
                        </div>
                      </div>

                      {/* OP cards */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {ops.map(op => {
                          const opNumber = op.label.replace(/^OP\s*/i, "");
                          return (
                            <div key={`${op.woId}-${op.id}`} draggable
                              onDragStart={(e) => {
                                const w = hourColPx > 0 ? Math.max(120, (op.estHours || 8) * hourColPx - 2) : 200;
                                const ghost = document.createElement("div");
                                ghost.style.cssText = `position:absolute;top:-1000px;left:-1000px;
                                  width:${w}px;height:${OPS_ROW_H - 6}px;
                                  background:#f5edca;color:#5a5000;
                                  border:1px solid #c8b870;
                                  padding:0 8px;font-family:'Inter',sans-serif;font-size:11px;font-weight:500;
                                  display:flex;align-items:center;gap:8px;
                                  box-sizing:border-box;white-space:nowrap;overflow:hidden;`;
                                ghost.innerHTML = `<span>${op.woId} -${op.label}</span>` +
                                  `<span style="background:#fff;border:1px solid #c8b870;padding:1px 6px;font-size:10px;font-weight:700;color:#5a5000">${fmtDur(op.estHours)}</span>`;
                                document.body.appendChild(ghost);
                                e.dataTransfer.setDragImage(ghost, 0, (OPS_ROW_H - 6) / 2);
                                setTimeout(() => ghost.remove(), 0);
                                onDragStartOp(op);
                              }}
                              onDragEnd={() => { setDragging(null); setHoveredSlot(null); }}
                              style={{ background: "#fff", border: "1px solid #d9d9d9", padding: 16, borderRadius: 4,
                                boxShadow: "0px 4px 2px rgba(0,0,0,0.05)",
                                position: "relative", display: "flex", flexDirection: "column", gap: 12,
                                cursor: "grab" }}>
                              {/* Row 1: OP pill + status */}
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ background: "#ececec", borderRadius: 33, padding: "4px 8px",
                                  display: "inline-flex", gap: 2, alignItems: "center", letterSpacing: "0.12px" }}>
                                  <span style={{ fontSize: 12, color: "#767676", lineHeight: "18px" }}>OP</span>
                                  <span style={{ fontSize: 12, color: "#000", fontWeight: 600, lineHeight: "18px" }}>{opNumber}</span>
                                </span>
                                {/* Q3: planning status is editable here so it can be set to Planned without dragging. */}
                                {isQ3
                                  ? <StatusDropdown value={op.planStatus} options={PLAN_STATUSES}
                                      onChange={(v) => onUpdateOp && onUpdateOp(op.woId, op.id, { planStatus: v })} />
                                  : <StatusBadge status={op.status} />}
                              </div>
                              {/* Row 2: est time + assigned machine(s) (reflects the Work orders
                                  page assignment). Falls back to the ideal cell when unassigned. */}
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ border: "1px solid rgba(0,0,0,0.3)", borderRadius: 2, padding: "4px 8px",
                                  fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "#000", whiteSpace: "nowrap", flexShrink: 0 }}>
                                  {fmtDur(op.estHours)}
                                </span>
                                {(() => {
                                  const assigned = machineAssignLabel(opMachines(op));
                                  return assigned ? (
                                    <span style={{ fontSize: 14, lineHeight: "20px", color: "#000", letterSpacing: "0.14px" }}>
                                      {assigned}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: 14, lineHeight: "20px", color: "#999", letterSpacing: "0.14px" }}>
                                      No machine assigned
                                    </span>
                                  );
                                })()}
                              </div>
                              {/* Drag handle */}
                              <span style={{ position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", pointerEvents: "none" }}>
                                <MIcon name="drag_indicator" size={16} color="#888" />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
      </div>{/* ── end Plan OPs panel ── */}

      {/* Method 1: Add production time modal */}
      {showAddModal && (
        <AddProductionTimeModal
          onClose={() => setShowAddModal(false)}
          onSave={handleModalSave}
        />
      )}

      {/* Add shift modal */}
      {showAddShiftModal && (
        <AddShiftModal
          initial={pendingShift ? {
            machines: [pendingShift.machine],
            startH: pendingShift.startH,
            endH: pendingShift.endH,
            dateKey: isoDate(pendingShift.date),
          } : null}
          onClose={() => { setShowAddShiftModal(false); setPendingShift(null); }}
          onSave={handleShiftSave}
        />
      )}

      {/* Shift popover (view + edit + delete) */}
      {shiftPopover && (
        <ShiftPopover
          date={shiftPopover.date}
          shift={shiftPopover.shift}
          anchorRect={shiftPopover.anchorRect}
          onClose={() => setShiftPopover(null)}
          onSave={(changes) => { updateShift(shiftPopover.shift, shiftPopover.machine, shiftPopover.date, changes); setShiftPopover(null); }}
          onDelete={() => { deleteShift(shiftPopover.shift, shiftPopover.machine, shiftPopover.date); setShiftPopover(null); }}
        />
      )}

      {/* Click-to-view / edit popover for an existing planned production time */}
      {winPopover && (() => {
        const w = winPopover.win;
        const dts = getWinDowntimes(w);
        const netH = (w.endH - w.startH) - getDeductibleH(w);
        return (
          <WindowPopover
            date={winPopover.date}
            win={w}
            anchorRect={winPopover.anchorRect}
            downtimes={dts}
            netH={netH}
            onClose={() => setWinPopover(null)}
            onSave={(s, e) => { updateWindow(winPopover.machine, winPopover.date, w, s, e); setWinPopover(null); }}
            onDelete={() => { deleteWindow(winPopover.machine, winPopover.date, w); setWinPopover(null); }}
            onAddDowntime={() => setAddDowntimeFor({ machine: winPopover.machine, date: winPopover.date, win: w, existing: null })}
            onEditDowntime={(dt) => setAddDowntimeFor({ machine: winPopover.machine, date: winPopover.date, win: w, existing: dt })}
            onDeleteDowntime={(dtId) => deleteDowntime(w, dtId)}
          />
        );
      })()}

      {/* Recurring-resize prompt (after dragging a handle on a recurring ppt) */}
      {recurResize && (
        <RecurringResizeModal
          machine={recurResize.machine}
          date={recurResize.date}
          win={recurResize.win}
          newStartH={recurResize.newStartH}
          newEndH={recurResize.newEndH}
          machineOptions={getMachinesForPpt(recurResize.win, recurResize.machine)}
          onClose={() => setRecurResize(null)}
          onApply={(scope, machines) => applyRecurringResize(scope, machines, recurResize.newStartH, recurResize.newEndH)}
        />
      )}

      {/* Add / edit downtime modal */}
      {addDowntimeFor && (() => {
        const { machine, date, win, existing } = addDowntimeFor;
        const netH = (win.endH - win.startH) - getDeductibleH(win);
        const machines = getMachinesForPpt(win, machine);
        return (
          <AddDowntimeModal
            machines={machines}
            win={win}
            currentNetH={netH}
            existing={existing}
            onClose={() => setAddDowntimeFor(null)}
            onSave={(dt, andAddNew) => {
              saveDowntime(machine, date, win, dt);
              if (!andAddNew) setAddDowntimeFor(null);
            }}
          />
        );
      })()}
    </div>
  );
}

// ─── SHOPFLOOR (OPERATOR TABLET) VIEW ─────────────────────────────────────────
// Q3-only. A tablet-framed operator dashboard. The Do Now / Do next cards are driven
// by the planner's schedule: any operation dragged onto the selected machine appears
// here, sorted into "now" (the simulated clock is inside its window) vs "next" (upcoming).
// The gauges/logs are static, matching the design; shift tabs, machine and clock are live.

// Cell → shopfloor location label (the prototype has no explicit location field).
const LOCATION_BY_CELL = { "Cell A": "Milling", "Cell B": "Turning" };

// Detailed operator job card used on the tablet's "All" tab. Mirrors the FourJaw
// operator layout: WO/OP header with due date + open button, planning status, the scheduled
// window, machine/location, product/qty and a run-time progress line.
function ShiftJobCard({ job, selected, onSelect, nowMs, onStart, onPause, onComplete, active }) {
  const { woId, op, product, targetQty, dueDate } = job;
  const opNo = op.label.replace(/^OP\s*/i, "");
  const location = LOCATION_BY_CELL[op.idealCell] || op.idealCell || "—";
  const estH = op.estHours || 0;
  const actualH = op.actualStart ? actualHours(op, nowMs) : 0;
  const pct = estH ? Math.round((actualH / estH) * 100) : 0;
  const actualLabel = actualH > 0 ? fmtDur(actualH) : "0m 0s";
  const key = `${woId}-${op.id}`;
  const done = op.floorStatus === "Done";
  const inProgress = op.floorStatus === "In progress"; // started (running or paused) → can be completed
  const row = { padding: "12px 16px", borderTop: "1px solid #f0f0f0", fontSize: 15, color: "#181233" };
  const lbl = { fontWeight: 700 };
  return (
    <div onClick={() => onSelect && onSelect(selected ? null : key)}
      style={{ border: selected ? "2px solid #7053ea" : "1px solid #e0e0e0", borderRadius: 8,
        background: "#fff", overflow: "hidden", cursor: "pointer" }}>
      {/* Header: WO / OP + due date + open button */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: 16 }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 16, color: "#181233" }}><span style={lbl}>Work Order No.</span> {woId}</span>
          <span style={{ fontSize: 16, color: "#181233" }}><span style={lbl}>Operation No.</span> {opNo}</span>
        </div>
        <span style={{ fontSize: 14, color: "#181233", whiteSpace: "nowrap" }}><span style={lbl}>Due:</span> {dueDate ? formatDateShort(dueDate) : "—"}</span>
      </div>
      {/* Planning status + scheduled window + machine / location */}
      <div style={{ ...row, display: "flex", flexDirection: "column", gap: 8 }}>
        <span><Q3StatusPill status={op.planStatus} small /></span>
        <span><span style={lbl}>Scheduled start:</span> {op.plannedStart ? formatDateTime(op.plannedStart) : "-"}</span>
        <span><span style={lbl}>Scheduled end:</span> {op.plannedEnd ? formatDateTime(op.plannedEnd) : "-"}</span>
        <div style={{ display: "flex", gap: 32 }}>
          <span style={{ flex: 1 }}><span style={lbl}>Machine:</span> {op.machine || "—"}</span>
          <span style={{ flex: 1 }}><span style={lbl}>Location:</span> {location}</span>
        </div>
      </div>
      {/* Product / target */}
      <div style={{ ...row, display: "flex", gap: 32 }}>
        <span style={{ flex: 1 }}><span style={lbl}>Product No.</span> {product}</span>
        <span style={{ flex: 1 }}><span style={lbl}>Target Qty.</span> {targetQty ?? "—"}</span>
      </div>
      {/* Run time */}
      <div style={row}>
        <span style={lbl}>Run time:</span> {actualLabel} / {fmtDur(estH)} - {pct}%
      </div>
      {/* Start / pause / done controls — same behaviour as the other tabs' cards */}
      <div style={{ ...row, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }} onClick={(e) => e.stopPropagation()}>
        {inProgress && (
          <button title="Complete job" onClick={() => onComplete && onComplete(woId, op.id)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px",
              border: "1px solid #19b36b", background: "#fff", borderRadius: 6, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", color: "#0f8f5c", whiteSpace: "nowrap" }}>
            <MIcon name="check" size={18} color="#0f8f5c" weight={700} /> Complete
          </button>
        )}
        <button title={done ? "Done" : active ? "Pause" : "Start"}
          onClick={() => { if (done) return; active ? (onPause && onPause(woId, op.id)) : (onStart && onStart(woId, op.id)); }}
          disabled={done}
          style={{ width: 56, height: 56, borderRadius: "50%",
            background: done ? "#b2e9d2" : active ? "#f0a23b" : "#19b36b", border: "none",
            cursor: done ? "default" : "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
          <MIcon name={done ? "check" : active ? "pause" : "play_arrow"} size={32} color="#fff" weight={700} />
        </button>
      </div>
    </div>
  );
}

// Combined "In progress" container shown on the tablet Dashboard for each running job.
// Left column: job header, live run time and progress, OP Complete / Stop. Right column
// (only when `showQty`): the production-quantity panel. Production counting is activated
// separately from the job — until the operator presses Start (`prodStarted` false) the right
// column shows a prompt to begin counting; once started it shows the live count panel. When
// `showQty` is false the left column spans the full width. Mirrors the Q3 "Q3 — explore" design.
// A single column-width status strip for the In progress card header. Green when `active`
// (In progress), neutral grey otherwise (e.g. production count Not started). `divider` draws
// the left border that aligns with the body's column divider.
function StatusStrip({ label, active, divider }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: active ? "#e3f3e9" : "#f2f2f2",
      borderLeft: divider ? "1px solid #f0f0f0" : "none",
      padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: active ? "#19b36b" : "#b0b0b0" }} />
      <span style={{ fontSize: 14, fontWeight: 700, color: "#181233" }}>{label}</span>
    </div>
  );
}

function InProgressCard({ job, showQty, prodStarted, onStartProd, onComplete, onStop }) {
  const { woId, op, product, targetQty, elapsedMs } = job;
  const opNo = op.label.replace(/^OP\s*/i, "");
  const estH = op.estHours || 0;
  const total = Math.max(0, Math.floor(elapsedMs / 1000));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const elapsedLabel = `${h} h ${m} m ${s} s`;
  const pct = estH ? Math.min(100, Math.round((elapsedMs / 3600000 / estH) * 100)) : 0;
  const target = targetQty ?? 0;

  const Bar = ({ value, color }) => (
    <div style={{ height: 8, background: "#ececec", borderRadius: 4, overflow: "hidden", marginTop: 12 }}>
      <div style={{ width: `${value}%`, height: "100%", background: color, borderRadius: 4 }} />
    </div>
  );
  const actionBtn = (extra) => ({ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px",
    border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", cursor: "pointer",
    fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "#181233", ...extra });

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden" }}>
      {/* Status header — one strip per column so the job and its production count read as
          independent states (the count can be "Not started" while the job is "In progress"). */}
      <div style={{ display: "flex" }}>
        <StatusStrip label="In progress" active />
        {showQty && <StatusStrip label={prodStarted ? "In progress" : "Not started"} active={prodStarted} divider />}
      </div>
      <div style={{ display: "flex" }}>
        {/* Left: job info + run time + controls */}
        <div style={{ flex: 1, minWidth: 0, padding: 16, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MIcon name="description" size={20} color="#181233" />
            <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>{woId}</span>
            <span style={{ background: "#ececec", borderRadius: 100, padding: "2px 10px", fontSize: 13, fontWeight: 700, color: "#555" }}>OP {opNo}</span>
            <MIcon name="info" size={20} color="#888" />
          </div>
          <div style={{ marginTop: 12 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: "#181233", fontVariantNumeric: "tabular-nums" }}>{elapsedLabel}</span>
            <span style={{ fontSize: 15, color: "#888" }}> / {fmtDur(estH)}</span>
          </div>
          <Bar value={pct} color="#7a7a7a" />
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button onClick={() => onComplete && onComplete(woId, op.id)} style={actionBtn()}>
              <MIcon name="check" size={18} color="#19b36b" weight={700} /> OP Complete
            </button>
            <button onClick={() => onStop && onStop(woId, op.id)} style={actionBtn()}>
              <span style={{ width: 13, height: 13, background: "#181233", borderRadius: 2 }} /> Stop
            </button>
          </div>
        </div>
        {/* Right: production quantity — only when part counting is enabled for this machine.
            Activated separately from the job: until Start is pressed we show a prompt. */}
        {showQty && (
          <div style={{ flex: 1, minWidth: 0, padding: 16, borderLeft: "1px solid #f0f0f0", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MIcon name="deployed_code" size={20} color="#181233" />
              <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Production Qty.</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#7053ea" }}>{product}</span>
            </div>
            {prodStarted ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#dff3e6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <MIcon name="thumb_up" size={15} color="#19b36b" weight={700} />
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Good 0</span>
                    <span style={{ fontSize: 15, color: "#888" }}>/ {target.toLocaleString()}</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#fde3e6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <MIcon name="thumb_down" size={15} color="#d24a57" weight={700} />
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Scrap 0</span>
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Quality 0%</span>
                </div>
                <Bar value={0} color="#7053ea" />
                <div style={{ marginTop: 16 }}>
                  <button style={actionBtn({ border: "1px solid #7053ea", color: "#7053ea" })}>
                    <MIcon name="add" size={18} color="#7053ea" weight={700} /> Add Qty
                  </button>
                </div>
              </>
            ) : (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 14, color: "#888" }}>Production counting not started.</span>
                <button onClick={() => onStartProd && onStartProd(woId, op.id)}
                  style={actionBtn({ border: "1px solid #7053ea", color: "#7053ea" })}>
                  <MIcon name="play_arrow" size={18} color="#7053ea" weight={700} /> Start
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact "Up next" strip shown beneath the In progress card(s) so the operator can see the next
// job coming up on this machine while one is running. A single horizontal row, not the full card.
function UpNextStrip({ job, onStart, onDetails }) {
  const { woId, op, product, targetQty } = job;
  const opNo = op.label.replace(/^OP\s*/i, "");
  const estH = op.estHours || 0;
  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, background: "#fff",
      display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", flexWrap: "wrap" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#777", letterSpacing: "0.4px", textTransform: "uppercase" }}>Up next</span>
      <MIcon name="description" size={18} color="#181233" />
      <span style={{ fontSize: 15, fontWeight: 700, color: "#181233" }}>{woId}</span>
      <span style={{ background: "#ececec", borderRadius: 100, padding: "2px 10px", fontSize: 12, fontWeight: 700, color: "#555" }}>OP {opNo}</span>
      <span style={{ fontSize: 14, color: "#181233" }}><strong>Product:</strong> {product}</span>
      <span style={{ fontSize: 14, color: "#181233" }}><strong>Target:</strong> {targetQty ?? "—"}</span>
      <span style={{ fontSize: 14, color: "#181233" }}><strong>Est. run time:</strong> {fmtDur(estH)}</span>
      <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
        <button onClick={() => onDetails && onDetails(job)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", border: "1px solid #d9d9d9",
            borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "#181233" }}>Details</button>
        <button onClick={() => onStart && onStart(woId, op.id)} title="Start job"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", border: "none",
            borderRadius: 8, background: "#19b36b", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: "#fff" }}>
          <MIcon name="play_arrow" size={18} color="#fff" weight={700} /> Start
        </button>
      </div>
    </div>
  );
}

// To-do job card for the Dashboard carousel (the "Do first / Do next" queue). Compact summary
// with a big Start button; pressing it starts the job (which then shows as an In progress card).
function JobToDoCard({ job, onStart, onDetails }) {
  const { woId, op, product, targetQty } = job;
  const opNo = op.label.replace(/^OP\s*/i, "");
  const estH = op.estHours || 0;
  const status = op.floorStatus || "Not started";
  return (
    <div style={{ flex: "0 0 360px", height: "100%", border: "1px solid #d9d9d9", borderRadius: 10, background: "#fff", padding: 16,
      display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
      {/* Header: WO + OP badge + floor status */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <MIcon name="description" size={20} color="#181233" />
        <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>{woId}</span>
        <span style={{ background: "#ececec", borderRadius: 100, padding: "2px 10px", fontSize: 13, fontWeight: 700, color: "#555" }}>OP {opNo}</span>
        <span style={{ marginLeft: "auto", background: "#ececec", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#555", whiteSpace: "nowrap" }}>{status}</span>
      </div>
      {/* Body: details + start */}
      <div style={{ display: "flex", alignItems: "stretch", gap: 12, marginTop: 12, flex: 1 }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#181233" }}>{op.planStatus}</span>
          <div style={{ marginTop: 8, fontSize: 14, color: "#181233", display: "flex", gap: 18, flexWrap: "wrap" }}>
            <span><strong>Product:</strong> {product}</span>
            <span><strong>Target:</strong> {targetQty ?? "—"}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: "#181233" }}><strong>Est. run time:</strong> {fmtDur(estH)}</div>
          <div style={{ marginTop: "auto", paddingTop: 16 }}>
            <button
              style={{ padding: "10px 22px", border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff",
                cursor: "default", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "#181233" }}>Details</button>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button onClick={() => onStart && onStart(woId, op.id)} title="Start job"
            style={{ width: 88, height: 88, borderRadius: "50%", background: "#19b36b", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 3px 8px rgba(0,0,0,0.18)" }}>
            <MIcon name="play_arrow" size={44} color="#fff" weight={700} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Standalone production-count container for the Dashboard, shown when part counting is enabled
// but no job is running on this machine — either jobs are queued (not started) or none are
// assigned. Counting can be started on its own here, independent of any job: until `started` it
// shows a grey "Not started" strip + Start button; once started it turns green with the live panel.
function StandaloneProdCountCard({ started, onStart }) {
  const Bar = ({ value, color }) => (
    <div style={{ height: 8, background: "#ececec", borderRadius: 4, overflow: "hidden", marginTop: 12 }}>
      <div style={{ width: `${value}%`, height: "100%", background: color, borderRadius: 4 }} />
    </div>
  );
  const btn = (extra) => ({ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px",
    border: "1px solid #7053ea", borderRadius: 8, background: "#fff", cursor: "pointer",
    fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "#7053ea", ...extra });
  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden" }}>
      <StatusStrip label={started ? "In progress" : "Not started"} active={started} />
      <div style={{ padding: 16, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MIcon name="deployed_code" size={20} color="#181233" />
          <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Production Qty.</span>
        </div>
        {started ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#dff3e6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MIcon name="thumb_up" size={15} color="#19b36b" weight={700} />
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Good 0</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#fde3e6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MIcon name="thumb_down" size={15} color="#d24a57" weight={700} />
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Scrap 0</span>
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>Quality 0%</span>
            </div>
            <Bar value={0} color="#7053ea" />
            <div style={{ marginTop: 16 }}>
              <button style={btn()}>
                <MIcon name="add" size={18} color="#7053ea" weight={700} /> Add Qty
              </button>
            </div>
          </>
        ) : (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
            <span style={{ fontSize: 14, color: "#888" }}>Production counting not started.</span>
            <button onClick={() => onStart && onStart()} style={btn()}>
              <MIcon name="play_arrow" size={18} color="#7053ea" weight={700} /> Start
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ShopfloorEmptyCard({ label }) {
  return (
    <div style={{ border: "1px dashed #d9d9d9", borderRadius: 10, padding: 24, background: "#fafafa",
      width: "100%", boxSizing: "border-box", minHeight: 132, display: "flex", alignItems: "center", justifyContent: "center",
      color: "#aaa", fontSize: 13, textAlign: "center" }}>
      {label}
    </div>
  );
}

// Static semicircular uptime gauge approximating the design.
function ShopfloorGauge() {
  const cx = 180, cy = 175, r = 140;
  const polar = (deg) => { const a = deg * Math.PI / 180; return [cx + r * Math.cos(a), cy - r * Math.sin(a)]; };
  const arc = (a0, a1) => {
    const [x0, y0] = polar(a0); const [x1, y1] = polar(a1);
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
    const sweep = a0 > a1 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
  };
  const needleDeg = 128;
  const [nx, ny] = polar(needleDeg);
  return (
    <svg viewBox="0 0 360 210" style={{ width: "100%", maxWidth: 360 }}>
      {/* gray track */}
      <path d={arc(180, 0)} fill="none" stroke="#ececec" strokeWidth={22} strokeLinecap="butt" />
      {/* blue set-up segment then green uptime segment, from the left */}
      <path d={arc(180, 152)} fill="none" stroke="#2f54c9" strokeWidth={22} />
      <path d={arc(152, 128)} fill="none" stroke="#37C58B" strokeWidth={22} />
      {/* needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#181233" strokeWidth={3} />
      <circle cx={nx} cy={ny} r={11} fill="#fff" stroke="#181233" strokeWidth={2} />
      {/* center labels */}
      <text x={cx} y={cy - 30} textAnchor="middle" fontSize="26" fontWeight="700" fill="#181233" fontFamily="Inter, sans-serif">1hr 00m</text>
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="14" fontWeight="600" fill="#37C58B" fontFamily="Inter, sans-serif">Uptime</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="12" fill="#888" fontFamily="Inter, sans-serif">Set Up</text>
    </svg>
  );
}

function ShopfloorPage({ workOrders, onUpdateOp, activeJobs, setActiveJobs, prodCountingEnabled = true, prodCountingMachines }) {
  // The jobs currently running on this tablet — an operator can run several at once (e.g. tending
  // multiple machines). Each entry is { woId, opId, startedAt(ms) }. Both running and paused jobs
  // have floorStatus "In progress"; `activeJobs` holds only the live (running) ones, which drive
  // the top-right "N in progress" card + per-job timers. Pausing removes that one but leaves status.
  // State is lifted to App so running jobs survive navigating away from and back to this page.
  const isActive = (woId, opId) => activeJobs.some(a => a.woId === woId && a.opId === opId);
  const [, tick] = useState(0); // 1s heartbeat to re-render the running timers
  useEffect(() => {
    if (!activeJobs.length) return;
    const id = setInterval(() => tick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeJobs.length]);
  // Press Start → job goes In progress, moves onto THIS tablet's machine, and joins the set of
  // active (running) jobs. We stamp the real start time once (a resume after pause keeps the
  // original), which drives the actual start datetime + actual run time on the Work Orders page.
  const startJob = (woId, opId) => {
    const ts = Date.now();
    const wo = workOrders.find(w => w.id === woId);
    const op = wo && wo.operations.find(o => o.id === opId);
    onUpdateOp && onUpdateOp(woId, opId, {
      floorStatus: "In progress",
      machine, machines: [machine],
      actualStart: (op && op.actualStart) || new Date(ts).toISOString(),
    });
    setActiveJobs(prev => isActive(woId, opId) ? prev : [...prev, { woId, opId, startedAt: ts }]);
  };
  // Press Pause → stop that job's live timer / drop it from the active set. Status stays In progress.
  const pauseJob = (woId, opId) => setActiveJobs(prev => prev.filter(a => !(a.woId === woId && a.opId === opId)));
  // Press Complete → job is Done; stamp the real end time (shown as the actual production end on the
  // Work Orders page) and drop it from the active set so its timer stops.
  const completeJob = (woId, opId) => {
    onUpdateOp && onUpdateOp(woId, opId, { floorStatus: "Done", actualEnd: new Date(Date.now()).toISOString() });
    setActiveJobs(prev => prev.filter(a => !(a.woId === woId && a.opId === opId)));
  };
  // Production counting is activated separately from the job: starting a job no longer starts its
  // count. The operator presses Start on the production panel to begin counting. We track which jobs
  // have had their count activated by key (woId-opId).
  // Production counting runs independently of jobs and is tracked per machine. It can be started
  // on its own (standalone card, before/without a job) or from a running job's panel — both flip
  // the same per-machine state, so the count stays active across starting and completing jobs.
  const [prodCountMachines, setProdCountMachines] = useState([]);
  const startProdCount = () =>
    setProdCountMachines(prev => prev.includes(machine) ? prev : [...prev, machine]);
  // Simulated "now" — scrub it to watch jobs move between Do Now and Do next.
  const [now, setNow] = useState(() => { const d = new Date(); d.setMinutes(0, 0, 0); return d; });
  const [machine, setMachine] = useState("Machine 1");
  const [shiftTab, setShiftTab] = useState("THIS SHIFT");
  const [navTab, setNavTab] = useState("Dashboard");
  const [jobSearch, setJobSearch] = useState("");
  // Jobs page is filtered via its own tabs — "All" (the detailed operator view) first, then
  // the floor statuses.
  const [jobStatusTab, setJobStatusTab] = useState("All");
  // "All" view: hide jobs not on the selected machine unless this is checked.
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [selectedJobKey, setSelectedJobKey] = useState(null);
  // Top-bar job search popup — opens from any tab; reuses the Jobs page search behaviour.
  const [searchOpen, setSearchOpen] = useState(false);
  const [topSearch, setTopSearch] = useState("");

  const SHIFT_TABS = ["THIS SHIFT", "LAST 60 MINS", "LAST NIGHT SHIFT", "LAST DAY SHIFT"];
  // "Jobs" stays a navigable view (reached via a card's Details button) but is hidden from the
  // tab bar; "Others" is surfaced as "Actions".
  const NAV_TABS = ["Dashboard", "Actions"];

  // Every operation in the factory shows up as a job, ordered to surface the planner's intent:
  //   1. Jobs on THIS machine come first.
  //   2. Planned jobs are prioritised to the top (the manager releases them together).
  //      When several are Planned, the one whose start time is closest to "now" leads.
  //   3. Everything else (Not planned and — as a soft "info may change" indicator — In planning)
  //      shares one default order: by OP number (OP10 → OP20 → OP30) regardless of WO, then by
  //      the WO due date so the more urgent of two same-OP jobs comes first.
  const nowT = now.getTime();
  const opNum = (op) => { const n = parseInt(String(op.label).replace(/\D/g, ""), 10); return Number.isFinite(n) ? n : 0; };
  const isPlanned = (op) => op.planStatus === "Planned";
  const jobs = workOrders.flatMap(wo => (wo.operations || []).map(op => {
    const hasSeg = op.segments?.length;
    const start = hasSeg ? new Date(op.segments[0].start) : null;
    const end = hasSeg ? new Date(op.segments[op.segments.length - 1].end) : null;
    return {
      woId: wo.id, op, product: wo.product, targetQty: wo.targetQty, dueDate: wo.dueDate,
      start, end,
      assignedHere: op.machine === machine,
      onNow: !!(start && end && op.machine === machine && start.getTime() <= nowT && nowT < end.getTime()),
    };
  }));
  const dueT = (j) => j.dueDate ? new Date(j.dueDate).getTime() : Infinity;
  jobs.sort((a, b) => {
    // 1. Current machine first.
    if (a.assignedHere !== b.assignedHere) return a.assignedHere ? -1 : 1;
    // 2. Planned jobs rise to the top.
    const pa = isPlanned(a.op), pb = isPlanned(b.op);
    if (pa !== pb) return pa ? -1 : 1;
    // 3. Multiple Planned: start time closest to now first.
    if (pa && pb) {
      const da = a.start ? Math.abs(a.start.getTime() - nowT) : Infinity;
      const db = b.start ? Math.abs(b.start.getTime() - nowT) : Infinity;
      return (da - db) || (opNum(a.op) - opNum(b.op));
    }
    // 4. Not planned / In planning: OP number, then WO due date.
    return (opNum(a.op) - opNum(b.op)) || (dueT(a) - dueT(b));
  });

  // Job search — by WO or OP number. Shared by the Jobs tab search and the top-bar search popup.
  const matchJobs = (query) => {
    const qq = query.trim().toLowerCase();
    if (!qq) return [];
    const d = qq.replace(/\D/g, "");
    return jobs.filter(j =>
      j.woId.toLowerCase().includes(qq) ||
      j.op.label.toLowerCase().includes(qq) ||
      (d && j.op.label.replace(/\D/g, "").includes(d))
    );
  };
  const q = jobSearch.trim().toLowerCase();
  const searchResults = matchJobs(jobSearch);
  const topResults = matchJobs(topSearch);

  const timeLabel = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  // Running jobs (started, not paused) — each renders an "In progress" container on the Dashboard,
  // pairing the job's run time with its production-quantity panel.
  const runningJobs = activeJobs.map(a => {
    const wo = workOrders.find(w => w.id === a.woId);
    const op = wo && wo.operations.find(o => o.id === a.opId);
    return op ? { woId: a.woId, op, product: wo.product, targetQty: wo.targetQty, elapsedMs: Date.now() - a.startedAt } : null;
  }).filter(Boolean);

  const navTabStyle = (active, isFirst) => ({
    padding: "0 24px", border: "none", borderLeft: isFirst ? "none" : "2px solid #7053ea", cursor: "pointer",
    background: active ? "#7053ea" : "#fff", color: active ? "#fff" : "#7053ea",
    fontFamily: '"Inter", sans-serif', fontSize: 16, fontWeight: 600,
  });
  const shiftTabStyle = (active) => ({
    padding: "10px 16px", border: "none", cursor: "pointer", whiteSpace: "nowrap",
    background: active ? "#7053ea" : "#f0f0f0", color: active ? "#fff" : "#555",
    fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: "0.4px",
  });
  const breakdownRow = (color, icon, label, value) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 22, height: 22, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MIcon name={icon} size={14} color="#fff" weight={700} />
        </span>
        <span style={{ fontSize: 13, color: "#555", lineHeight: "16px" }}>{label}</span>
      </div>
      <span style={{ fontSize: 16, fontWeight: 700, color: "#181233" }}>{value}</span>
    </div>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", background: "#e9e9ee", padding: 20, fontFamily: '"Inter", sans-serif', boxSizing: "border-box" }}>
      {/* Prototype clock control — sits outside the device frame */}
      <div style={{ maxWidth: 1180, margin: "0 auto 14px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        background: "#181233", color: "#fff", borderRadius: 10, padding: "10px 16px" }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "#b9b4d6" }}>Simulated time</span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button title="Previous day"
            onClick={() => { const nd = new Date(now); nd.setDate(nd.getDate() - 1); setNow(nd); }}
            style={{ width: 30, height: 30, borderRadius: 6, border: "none", background: "#2b2350", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MIcon name="chevron_left" size={20} color="#fff" />
          </button>
          <input type="date" value={isoDate(now)}
            onChange={(e) => { const [y, m, d] = e.target.value.split("-").map(Number); const nd = new Date(now); nd.setFullYear(y, m - 1, d); setNow(nd); }}
            style={{ font: "inherit", padding: "5px 8px", border: "none", borderRadius: 6 }} />
          <button title="Next day"
            onClick={() => { const nd = new Date(now); nd.setDate(nd.getDate() + 1); setNow(nd); }}
            style={{ width: 30, height: 30, borderRadius: 6, border: "none", background: "#2b2350", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MIcon name="chevron_right" size={20} color="#fff" />
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 240 }}>
          <input type="range" min={0} max={23} value={now.getHours()}
            onChange={(e) => { const nd = new Date(now); nd.setHours(Number(e.target.value), 0, 0, 0); setNow(nd); }}
            style={{ flex: 1 }} />
          <span style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 64 }}>{timeLabel}</span>
        </div>
        <span style={{ fontSize: 12, color: "#b9b4d6" }}>Drag to move "now" across the planned schedule</span>
      </div>

      {/* Device frame — fixed 4:3 landscape tablet shape; content scrolls inside the bezel. */}
      <div style={{ maxWidth: 1180, width: "100%", height: 750, margin: "0 auto", background: "#fff", borderRadius: 18,
        border: "10px solid #181233", boxShadow: "0 12px 40px rgba(0,0,0,0.25)", overflow: "hidden", display: "flex", boxSizing: "border-box", position: "relative" }}>

        {/* Main column */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", position: "relative" }}>
          {/* Top bar: globe + nav tabs */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16 }}>
            <button title="Language" style={{ width: 48, height: 48, boxSizing: "border-box", borderRadius: 10, border: "2px solid #7053ea",
              background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="language" size={24} color="#7053ea" />
            </button>
            <div style={{ display: "flex", height: 48, boxSizing: "border-box", border: "2px solid #7053ea", borderRadius: 10, overflow: "hidden" }}>
              {NAV_TABS.map((t, i) => (
                <button key={t} onClick={() => { if (t !== "Actions") setNavTab(t); }} style={navTabStyle(navTab === t, i === 0)}>{t}</button>
              ))}
            </div>
            {/* Job search — opens the search popup (same behaviour as the Jobs page search). */}
            <button onClick={() => setSearchOpen(true)} title="Search jobs"
              style={{ display: "flex", alignItems: "center", gap: 10, height: 48, padding: "0 16px", flex: 1, maxWidth: 480,
                border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", cursor: "pointer",
                fontFamily: "inherit", fontSize: 15, color: "#888", textAlign: "left" }}>
              <MIcon name="search" size={20} color="#888" />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Search for Work Order, Operation, Product or Location</span>
            </button>
          </div>

          {/* ── Dashboard job area, above the tab content ──
              • Running jobs render an "In progress" container (run time + production quantity,
                hidden when part counting is off for this machine in Settings).
              • Jobs assigned to / runnable on this machine that aren't running or done show as a
                horizontally-scrollable "Do first / Do next" carousel — pressing Start moves a job
                into the In progress state above. */}
          {(() => {
            if (navTab !== "Dashboard") return null;
            const prodQtyOn = prodCountingEnabled && (!prodCountingMachines || prodCountingMachines.has(machine));
            const prodCountStarted = prodCountMachines.includes(machine);
            const openDetails = (j) => { setSelectedJobKey(`${j.woId}-${j.op.id}`); setNavTab("Jobs"); };
            // While a job is in progress, show the In progress container(s) and hide the full
            // queue — but keep a compact "Up next" peek so the operator still sees what's coming.
            if (runningJobs.length) {
              const runningKeys = new Set(runningJobs.map(rj => `${rj.woId}-${rj.op.id}`));
              const upNext = jobs.find(j => j.assignedHere && j.op.floorStatus !== "Done" && !runningKeys.has(`${j.woId}-${j.op.id}`));
              return (
                <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
                  {runningJobs.map(rj => (
                    <InProgressCard key={`${rj.woId}-${rj.op.id}`} job={rj} showQty={prodQtyOn}
                      prodStarted={prodCountStarted}
                      onStartProd={startProdCount}
                      onComplete={completeJob} onStop={pauseJob} />
                  ))}
                  {upNext && <UpNextStrip job={upNext} onStart={startJob} onDetails={openDetails} />}
                </div>
              );
            }
            // Otherwise show the "Do first / Do next" queue of jobs assigned to this machine.
            const todo = jobs.filter(j => j.assignedHere && j.op.floorStatus !== "Done");
            // No jobs to do: when part counting is on, still surface the production-count panel
            // so the operator can start counting on its own; otherwise show nothing.
            if (!todo.length) return prodQtyOn
              ? <div style={{ padding: "4px 16px 16px" }}>
                  <StandaloneProdCountCard started={prodCountStarted} onStart={startProdCount} />
                </div>
              : null;
            return (
              <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ overflowX: "auto", paddingBottom: 4 }}>
                  {/* align-items: stretch + height:100% cards keeps every card the same height */}
                  <div style={{ display: "flex", gap: 16, alignItems: "stretch", width: "max-content" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do first</span>
                      <div style={{ flex: 1 }}><JobToDoCard job={todo[0]} onStart={startJob} onDetails={openDetails} /></div>
                    </div>
                    {todo.length > 1 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do next</span>
                        <div style={{ display: "flex", gap: 16, flex: 1 }}>
                          {todo.slice(1).map(j => (
                            <JobToDoCard key={`${j.woId}-${j.op.id}`} job={j} onStart={startJob} onDetails={openDetails} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Jobs are queued but none started — production counting can still be started on its own. */}
                {prodQtyOn && <StandaloneProdCountCard started={prodCountStarted} onStart={startProdCount} />}
              </div>
            );
          })()}

          {/* Tabs row — operation-status tabs on the Jobs page, shift tabs elsewhere; + machine selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px 12px" }}>
            {navTab === "Jobs" ? (
              <div style={{ display: "flex", gap: 8 }}>
                {["All", ...FLOOR_STATUSES].map(s => {
                  const count = s === "All"
                    ? jobs.length
                    : jobs.filter(j => j.op.floorStatus === s).length;
                  const active = jobStatusTab === s;
                  return (
                    <button key={s} onClick={() => setJobStatusTab(s)}
                      style={{ padding: "10px 16px", borderRadius: 6, cursor: "pointer",
                        border: active ? "none" : "1px solid #ececec",
                        background: active ? "#7053ea" : "#fff", color: active ? "#fff" : "#555",
                        fontFamily: '"Inter", sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: "0.4px",
                        textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {s}
                      <span style={{ background: active ? "#fff" : "#ececec", color: active ? "#7053ea" : "#555",
                        borderRadius: 4, padding: "0 8px", fontSize: 12, fontWeight: 700, lineHeight: "20px" }}>{count}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <button style={{ ...shiftTabStyle(false), padding: "10px 10px" }}>«</button>
                <div style={{ display: "flex", overflow: "hidden", borderRadius: 4 }}>
                  {SHIFT_TABS.map(t => (
                    <button key={t} onClick={() => setShiftTab(t)} style={shiftTabStyle(shiftTab === t)}>{t}</button>
                  ))}
                </div>
                <button style={{ ...shiftTabStyle(false), padding: "10px 10px" }}>»</button>
              </>
            )}
            <div style={{ marginLeft: "auto" }}>
              <select value={machine} onChange={(e) => setMachine(e.target.value)}
                style={{ padding: "10px 14px", border: "1px solid #d9d9d9", borderRadius: 6, fontFamily: "inherit",
                  fontSize: 14, fontWeight: 600, color: "#181233", background: "#fff", cursor: "pointer" }}>
                {MACHINES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* ── JOBS tab: detailed operator cards (Do first / Do next), ordered by planner
                intent (see sort). Every tab uses the same cards — only the filter differs:
                "All" shows everything; the status tabs filter by floor status. ── */}
          {navTab === "Jobs" && (() => {
            const base = q ? searchResults
              : jobStatusTab === "All" ? jobs
              : jobs.filter(j => j.op.floorStatus === jobStatusTab);
            // Default to this machine's jobs; the checkbox reveals jobs on other machines too.
            const list = (showUnassigned || q) ? base : base.filter(j => j.assignedHere);
            return (
              <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Search + machine filter live in the top-bar search popup now. */}
                {list.length ? (
                  <>
                    {/* Do first — always the top job in the sorted order */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do first</span>
                      <ShiftJobCard job={list[0]} nowMs={nowT}
                        selected={selectedJobKey === `${list[0].woId}-${list[0].op.id}`} onSelect={setSelectedJobKey}
                        onStart={startJob} onPause={pauseJob} onComplete={completeJob} active={isActive(list[0].woId, list[0].op.id)} />
                    </div>
                    {list.length > 1 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do next</span>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {list.slice(1).map(j => (
                            <ShiftJobCard key={`${j.woId}-${j.op.id}`} job={j} nowMs={nowT}
                              selected={selectedJobKey === `${j.woId}-${j.op.id}`} onSelect={setSelectedJobKey}
                              onStart={startJob} onPause={pauseJob} onComplete={completeJob} active={isActive(j.woId, j.op.id)} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : <ShopfloorEmptyCard label={
                      q ? "No jobs match your search"
                      : showUnassigned ? "No jobs" : "No jobs assigned to this machine"} />}
              </div>
            );
          })()}

          {navTab === "Actions" && (
            <div style={{ padding: 48, color: "#aaa", fontSize: 14, textAlign: "center" }}>Nothing here yet.</div>
          )}

          {/* ── DASHBOARD tab ── */}
          {navTab === "Dashboard" && (
          <>
          {/* Bottom: utilisation + gauge + breakdown */}
          <div style={{ display: "flex", gap: 16, padding: "0 16px 20px", alignItems: "stretch" }}>
            {/* Utilisation */}
            <div style={{ flex: "0 0 150px", border: "1px solid #d9d9d9", borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#181233", alignSelf: "flex-start" }}>Utilisation</span>
              <span style={{ fontSize: 26, fontWeight: 700, color: "#7053ea", alignSelf: "flex-start" }}>85%</span>
              <div style={{ width: 26, flex: 1, minHeight: 200, background: "#ececec", borderRadius: 13, position: "relative", overflow: "hidden", marginTop: 6 }}>
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "85%", background: "#7053ea", borderRadius: 13 }} />
              </div>
            </div>
            {/* Gauge */}
            <div style={{ flex: 1, minWidth: 0, border: "1px solid #d9d9d9", borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", justifyContent: "center" }}>
                <button style={{ width: 36, height: 36, borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer" }}>«</button>
                <ShopfloorGauge />
                <button style={{ width: 36, height: 36, borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer" }}>»</button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: 420, fontSize: 12, color: "#888" }}>
                <span>09:00<br />{isoDate(now).split("-").reverse().join("/")}</span>
                <span style={{ color: "#888" }}>Started 13:00</span>
                <span style={{ textAlign: "right" }}>21:00<br />{isoDate(now).split("-").reverse().join("/")}</span>
              </div>
              <button style={{ marginTop: 4, padding: "10px 18px", border: "1px solid #7053ea", borderRadius: 8, background: "#fff",
                color: "#7053ea", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "inherit" }}>
                <MIcon name="play_circle" size={18} color="#7053ea" /> Start Auto-Labeller
              </button>
            </div>
            {/* Breakdown */}
            <div style={{ flex: "0 0 210px", border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden" }}>
              {breakdownRow("#37C58B", "arrow_upward", "Uptime", "0m 0s")}
              {breakdownRow("#2f54c9", "remove", "Productive Downtime", "0m 0s")}
              {breakdownRow("#CE464F", "arrow_downward", "Unproductive Downtime", "4h 55m")}
              {breakdownRow("#181233", "close", "Offline", "0m 0s")}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Right log panel */}
        <div style={{ flex: "0 0 300px", minHeight: 0, overflowY: "auto", borderLeft: "1px solid #ececec", background: "#fff", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <MIcon name="chevron_right" size={24} color="#888" />
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#181233", fontVariantNumeric: "tabular-nums" }}>{timeLabel}</div>
              <div style={{ fontSize: 13, color: "#888" }}>Logs from this shift</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: 20 }}>
            <span style={{ width: 120, height: 120, borderRadius: "50%", background: "#e6f6ed", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MIcon name="check_circle" size={72} color="#19b36b" />
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#181233", textAlign: "center" }}>All events have been logged!</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <span style={{ width: 40, height: 40, borderRadius: "50%", background: "#ececec", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <MIcon name="flag" size={20} color="#888" />
            </span>
            <div style={{ flex: 1, border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 12px", position: "relative" }}>
              <span style={{ position: "absolute", top: -9, left: 10, background: "#fff", padding: "0 4px", fontSize: 10, color: "#888" }}>09:00</span>
              <span style={{ fontSize: 14, color: "#181233" }}>Downtime 1</span>
            </div>
          </div>
        </div>

        {/* Job search popup — overlays the whole tablet (incl. the right log panel). Same
            search behaviour, machine filter, and start-able job cards as the Jobs page. */}
        {searchOpen && (
          <div onClick={() => setSearchOpen(false)}
            style={{ position: "absolute", inset: 0, zIndex: 80, background: "rgba(24,18,51,0.28)",
              display: "flex", justifyContent: "center", padding: 16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: "100%", maxWidth: 960, maxHeight: "100%", background: "#ededed", borderRadius: 12,
                boxShadow: "0 12px 40px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Search field + machine filter toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, padding: 16 }}>
                <input autoFocus value={topSearch} onChange={e => setTopSearch(e.target.value)}
                  onKeyDown={e => e.key === "Escape" && setSearchOpen(false)}
                  placeholder="Search for Work Order, Operation, Product or Location"
                  style={{ flex: 1, height: 48, padding: "0 16px", border: "1px solid #d9d9d9", borderRadius: 8,
                    boxSizing: "border-box", fontFamily: "inherit", fontSize: 15, color: "#000", outline: "none", background: "#fff" }} />
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: "#181233",
                  cursor: "pointer", maxWidth: 210, lineHeight: "20px" }}>
                  <input type="checkbox" checked={showUnassigned} onChange={e => setShowUnassigned(e.target.checked)}
                    style={{ width: 22, height: 22, flexShrink: 0, accentColor: "#7053ea", cursor: "pointer" }} />
                  Show jobs that is not assigned to this machine
                </label>
              </div>
              {/* Results — Do first / Do next, same cards as the Jobs page */}
              <div style={{ overflowY: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
                {!topSearch.trim() ? (
                  <div style={{ padding: 32, textAlign: "center", color: "#888", fontSize: 14 }}>Start typing to search jobs</div>
                ) : (() => {
                  const list = showUnassigned ? topResults : topResults.filter(j => j.assignedHere);
                  if (!list.length) return <ShopfloorEmptyCard label="No jobs match your search" />;
                  return (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do first</span>
                        <ShiftJobCard job={list[0]} nowMs={nowT}
                          selected={selectedJobKey === `${list[0].woId}-${list[0].op.id}`} onSelect={setSelectedJobKey}
                          onStart={startJob} onPause={pauseJob} onComplete={completeJob} active={isActive(list[0].woId, list[0].op.id)} />
                      </div>
                      {list.length > 1 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: "#777" }}>Do next</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {list.slice(1).map(j => (
                              <ShiftJobCard key={`${j.woId}-${j.op.id}`} job={j} nowMs={nowT}
                                selected={selectedJobKey === `${j.woId}-${j.op.id}`} onSelect={setSelectedJobKey}
                                onStart={startJob} onPause={pauseJob} onComplete={completeJob} active={isActive(j.woId, j.op.id)} />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DASHBOARD STUB ───────────────────────────────────────────────────────────
function DashboardPage() {
  return <div style={{ padding: 32, fontFamily: "sans-serif", color: "#888" }}><h2 style={{ color: "#333" }}>Dashboards</h2><p>Coming soon.</p></div>;
}

// ─── NAV + ROOT ───────────────────────────────────────────────────────────────
const SIDE_NAV_ICON_STYLE = {
  fontFamily: '"Material Symbols Sharp"',
  fontWeight: 500,
  fontStyle: "normal",
  fontSize: 24,
  lineHeight: 1,
  display: "inline-block",
  fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24",
  userSelect: "none",
  WebkitFontFeatureSettings: '"liga"',
  fontFeatureSettings: '"liga"',
};
const TOP_NAV = [
  { id: "dashboards", icon: "team_dashboard",      label: "Dashboards" },
  { id: "workorders", icon: "contract",            label: "Work orders" },
  { id: "planning",   icon: "calendar_clock",      label: "Planning" },
  { id: "reports",    icon: "heap_snapshot_large", label: "Reports" },
];
const BOTTOM_NAV = [
  { id: "settings", icon: "settings",       label: "Settings" },
  { id: "account",  icon: "account_circle", label: "Account" },
  { id: "help",     icon: "help",           label: "Help" },
];
// Side-menu items that are intentionally inert in the prototype (clicking does nothing).
const DISABLED_NAV = ["dashboards", "reports", "account", "help"];

function SideMenuItem({ item, active, onClick }) {
  return (
    <button onClick={onClick}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = "#2b2b2b"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
      style={{
        width: 70, height: 70,
        background: active ? "#2b2b2b" : "transparent",
        border: "none", cursor: "pointer", padding: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
        color: active ? "#f2f2f2" : "#b9b9b9",
        fontFamily: '"Inter", sans-serif',
        transition: "background 0.12s",
      }}>
      <span style={SIDE_NAV_ICON_STYLE}>{item.icon}</span>
      <span style={{ fontSize: 10, lineHeight: "14px", letterSpacing: 0.1, fontWeight: 500, whiteSpace: "nowrap" }}>{item.label}</span>
    </button>
  );
}

function PlaceholderPage({ title }) {
  return <div style={{ padding: 32, fontFamily: "sans-serif", color: "#888" }}><h2 style={{ color: "#333" }}>{title}</h2><p>Coming soon.</p></div>;
}

// ─── SETTINGS ────────────────────────────────────────────────────────────────
// Settings hub + the "Production / Part Counting" sub-page that toggles whether the
// operator tablet shows the Production quantity panel, per machine.
const setsEqual = (a, b) => a.size === b.size && [...a].every(x => b.has(x));

function SettingsPage({ prodCountingEnabled, prodCountingMachines, onSaveProdCounting }) {
  const [sub, setSub] = useState("home");

  if (sub === "production-counting") {
    return <ProductionCountingPage enabled={prodCountingEnabled} machines={prodCountingMachines}
      onBack={() => setSub("home")} onSave={onSaveProdCounting} />;
  }

  const CardIcon = ({ name }) => (
    <span style={{ width: 40, height: 40, borderRadius: 10, background: "#ece8fb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <MIcon name={name} size={22} color="#181233" />
    </span>
  );
  const Card = ({ icon, title, children }) => (
    <div style={{ background: "#fff", borderRadius: 12, padding: 24, minHeight: 170 }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          {icon && <CardIcon name={icon} />}
          <span style={{ fontSize: 19, fontWeight: 700, color: "#181233" }}>{title}</span>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
    </div>
  );
  const LinkRow = ({ label, onClick, external, badge }) => (
    <button onClick={onClick}
      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
        display: "inline-flex", alignItems: "center", gap: 8, fontFamily: '"Inter", sans-serif',
        fontSize: 16, color: "#181233" }}>
      {label}
      {external && <MIcon name="open_in_new" size={17} color="#181233" />}
      {badge && <span style={{ background: "#1f7a4d", color: "#fff", fontSize: 12, fontWeight: 700, borderRadius: 100, padding: "2px 10px" }}>{badge}</span>}
    </button>
  );

  return (
    <div style={{ flex: 1, overflow: "auto", background: "#ededed", padding: "28px 36px", fontFamily: '"Inter", sans-serif' }}>
      <h1 style={{ fontSize: 26, fontWeight: 700, color: "#181233", margin: "0 0 24px" }}>Settings</h1>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24, maxWidth: 1200 }}>
        <Card icon="precision_manufacturing" title="System properties">
          <LinkRow label="Downtime reasons" />
          <LinkRow label="Production / part counting" badge="New" onClick={() => setSub("production-counting")} />
          <LinkRow label="Alert notifications" external />
        </Card>
        <Card icon="groups" title="Workplace">
          <LinkRow label="General" external />
          <LinkRow label="Team members & access" external />
        </Card>
        <Card icon="bolt" title="Asset connectivity">
          <LinkRow label="Assets" external />
          <LinkRow label="MachineLinks" external />
          <LinkRow label="Tablets" external />
        </Card>
        <Card>
          <LinkRow label="Go to operator view" external />
          <LinkRow label="Go to classic energy dashboards" external />
          <LinkRow label="Go to classic work list" external />
        </Card>
      </div>
    </div>
  );
}

function ProductionCountingPage({ enabled, machines, onBack, onSave }) {
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [draft, setDraft] = useState(() => new Set(machines));

  const dirty = draftEnabled !== enabled || !setsEqual(draft, machines);
  const canSave = dirty && (!draftEnabled || draft.size > 0);

  const toggleMachine = m => setDraft(prev => { const next = new Set(prev); next.has(m) ? next.delete(m) : next.add(m); return next; });
  const toggleCell = ms => setDraft(prev => {
    const all = ms.every(m => prev.has(m));
    const next = new Set(prev);
    ms.forEach(m => all ? next.delete(m) : next.add(m));
    return next;
  });
  const discard = () => { setDraftEnabled(enabled); setDraft(new Set(machines)); };

  const Box = ({ on, dim }) => (
    <span style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${on ? "#7053ea" : "#c4c4c4"}`,
      background: on ? "#7053ea" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, opacity: dim ? 0.4 : 1 }}>
      {on && <MIcon name="check" size={14} color="#fff" weight={700} />}
    </span>
  );
  const rowStyle = (indent, dim) => ({ display: "flex", alignItems: "center", gap: 12, width: "fit-content", textAlign: "left",
    background: "transparent", border: "none", padding: `8px 0 8px ${indent}px`, cursor: dim ? "default" : "pointer",
    fontFamily: '"Inter", sans-serif', color: dim ? "#aaa" : "#181233" });

  return (
    <div style={{ flex: 1, overflow: "auto", background: "#ededed", padding: "28px 36px", fontFamily: '"Inter", sans-serif' }}>
      <button onClick={onBack}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
          fontFamily: '"Inter", sans-serif', fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 6 }}>
        <MIcon name="chevron_left" size={20} color="#555" /> Settings
      </button>
      <h1 style={{ fontSize: 26, fontWeight: 700, color: "#181233", margin: "0 0 8px" }}>Production / Part Counting</h1>
      <p style={{ fontSize: 15, color: "#555", margin: "0 0 22px", maxWidth: 900 }}>
        Enable production or part quantity logging for selected machines, allowing operators to log counts directly from connected tablets.
      </p>

      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 1200 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            {/* Toggle switch */}
            <button onClick={() => setDraftEnabled(v => !v)} aria-label="Enable production / part quantity logging"
              style={{ width: 52, height: 30, borderRadius: 100, border: "none", cursor: "pointer", flexShrink: 0, padding: 0,
                background: draftEnabled ? "#7053ea" : "#c4c4c4", position: "relative", transition: "background 0.15s" }}>
              <span style={{ position: "absolute", top: 3, left: draftEnabled ? 25 : 3, width: 24, height: 24, borderRadius: "50%",
                background: "#fff", transition: "left 0.15s" }} />
            </button>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#181233" }}>Enable production / part quantity logging</div>
              <div style={{ fontSize: 13, color: "#555", marginTop: 6, maxWidth: 780 }}>
                <strong>Note:</strong> Select at least one machine. Turning off production logging will end any active counts. Changes may take up to one hour to apply on tablets.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <button onClick={discard} disabled={!dirty}
              style={{ padding: "10px 18px", borderRadius: 8, border: "1px solid #d9d9d9", background: "#fff",
                fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, color: dirty ? "#181233" : "#bbb",
                cursor: dirty ? "pointer" : "default" }}>Discard changes</button>
            <button onClick={() => canSave && onSave(draftEnabled, [...draft])} disabled={!canSave}
              style={{ padding: "10px 22px", borderRadius: 8, border: "none",
                background: canSave ? "#7053ea" : "#ececec", color: canSave ? "#fff" : "#bbb",
                fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 600, cursor: canSave ? "pointer" : "default" }}>Save</button>
          </div>
        </div>

        {/* Machine list grouped by cell */}
        <div style={{ marginTop: 28, opacity: draftEnabled ? 1 : 0.55, pointerEvents: draftEnabled ? "auto" : "none" }}>
          {Object.entries(MACHINES_BY_CELL).map(([cell, ms]) => {
            const allOn = ms.every(m => draft.has(m));
            return (
              <div key={cell} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#181233" }}>{cell}</span>
                  <MIcon name="arrow_drop_up" size={20} color="#888" />
                </div>
                {ms.length > 1 && (
                  <button onClick={() => toggleCell(ms)} style={rowStyle(2)}>
                    <Box on={allOn} /><span style={{ fontSize: 15 }}>Select All</span>
                  </button>
                )}
                {ms.map(m => (
                  <button key={m} onClick={() => toggleMachine(m)} style={rowStyle(2)}>
                    <Box on={draft.has(m)} /><span style={{ fontSize: 15 }}>{m}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── FLOW SWITCHER ───────────────────────────────────────────────────────────
// Top bar that lets the user flip between the Q3 view (/q3) and the
// Vision view (/). Both flows share the same App state.
function FlowSwitcher({ flow, onChange }) {
  const tabStyle = (active) => ({
    height: 28, padding: "0 14px", borderRadius: 100,
    border: "1px solid " + (active ? "#7053ea" : "transparent"),
    background: active ? "#fff" : "transparent",
    color: active ? "#181233" : "#cfcfd6",
    fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
  });
  return (
    <div style={{ height: 40, background: "#181233", display: "flex", alignItems: "center",
      padding: "0 12px", gap: 10, flexShrink: 0, borderBottom: "1px solid #000" }}>
      <span style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, fontWeight: 600,
        color: "#8a86a3", letterSpacing: "0.5px", textTransform: "uppercase" }}>Flow</span>
      <div style={{ display: "flex", gap: 4, background: "#26203f", borderRadius: 100, padding: 3 }}>
        <button onClick={() => onChange("q3")} style={tabStyle(flow === "q3")}>Q3 view</button>
        <button onClick={() => onChange("vision")} style={tabStyle(flow === "vision")}>Vision view</button>
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const flow = location.pathname.startsWith("/q3") ? "q3" : "vision";

  const [page, setPage] = useState("planning");
  const [workOrders, setWorkOrders] = useState(INITIAL_WORK_ORDERS);
  const [prodWindows, setProdWindows] = useState(() => Object.fromEntries(MACHINES.map(m => [m, {}])));
  // downtimes are stored keyed by the PPT window id, so recurring PPTs naturally propagate downtimes
  // across every instance that shares that id (default windows for a machine share `default-<machine>`).
  const [downtimes, setDowntimes] = useState({});
  // shifts: { [machine]: { [dateKey]: [{ id, startH, endH, category, recurring? }] } }
  const [shifts, setShifts] = useState({});
  // Jobs the operator has running on the tablet — lifted here so they survive leaving the
  // Shopfloor page and coming back. Each entry is { woId, opId, startedAt(ms) }.
  const [activeJobs, setActiveJobs] = useState([]);
  // Production / part counting (Settings → System properties). When logging is enabled, the
  // tablet shows the "Production quantity" panel for machines that are selected here. Lifted to
  // App so the Settings page controls it and the operator tablet reads it.
  const [prodCountingEnabled, setProdCountingEnabled] = useState(true);
  const [prodCountingMachines, setProdCountingMachines] = useState(() => new Set(MACHINES));

  const isQ3 = flow === "q3";
  // Q3 flow adds an operator "Shopfloor" tablet view, sitting just above Settings.
  const bottomNav = isQ3 ? [{ id: "shopfloor", icon: "tablet_mac", label: "Shopfloor" }, ...BOTTOM_NAV] : BOTTOM_NAV;

  function handleAssignOp(woId, opId, machine, segments) {
    setWorkOrders(prev => prev.map(wo => wo.id !== woId ? wo : {
      ...wo, operations: wo.operations.map(op => {
        if (op.id !== opId) return op;
        const plannedStart = segments[0]?.start ?? null;
        const plannedEnd   = segments[segments.length - 1]?.end ?? null;
        return { ...op, machine, segments, plannedStart, plannedEnd, status: "Planned", planStatus: "In planning" };
      })
    }));
  }
  function handleUnassignOp(woId, opId) {
    setWorkOrders(prev => prev.map(wo => wo.id !== woId ? wo : {
      ...wo, operations: wo.operations.map(op => op.id !== opId ? op : { ...op, machine: null, plannedStart: null, plannedEnd: null, segments: [], status: "Not planned", planStatus: "Not planned" })
    }));
  }
  // Q3 work order page: edit an operation's planning/shopfloor status (or any op field) directly.
  // Any edit stamps the modified time on both the op and its parent work order.
  function handleUpdateOp(woId, opId, changes) {
    const now = new Date().toISOString();
    setWorkOrders(prev => prev.map(wo => wo.id !== woId ? wo : {
      ...wo, lastModified: now,
      operations: wo.operations.map(op => {
        if (op.id !== opId) return op;
        // Capture the CSV baseline for est run time on the first edit, so we can flag overrides.
        const baseline = ("estHours" in changes && op.csvEstHours == null) ? { csvEstHours: op.estHours } : {};
        return { ...op, ...baseline, ...changes, lastModified: now };
      })
    }));
  }
  function handleUpdateWo(woId, changes) {
    const now = new Date().toISOString();
    setWorkOrders(prev => prev.map(wo => wo.id === woId ? { ...wo, ...changes, lastModified: now } : wo));
  }
  // Import work orders from a CSV (append/merge): a new woId is added; an existing woId has its
  // scalar fields refreshed and its operations merged by opId — new ops are appended, matching ops
  // get their CSV-sourced fields updated while their planning/shopfloor state is preserved.
  function handleImportWorkOrders(imported, woPresent = []) {
    setWorkOrders(prev => {
      const map = new Map(prev.map(wo => [wo.id, wo]));
      const order = prev.map(wo => wo.id);
      for (const imp of imported) {
        const existing = map.get(imp.id);
        if (!existing) {
          map.set(imp.id, {
            id: imp.id,
            product: imp.product ?? "", description: imp.description ?? "",
            dueDate: imp.dueDate ?? "", targetQty: imp.targetQty ?? 0,
            operations: imp.operations.map(newOperationFromCsv),
          });
          order.push(imp.id);
          continue;
        }
        // Update only the WO scalar fields the CSV actually carried.
        const woUpdates = {};
        for (const f of woPresent) if (imp[f] !== undefined) woUpdates[f] = imp[f];
        const opMap = new Map(existing.operations.map(op => [op.id, op]));
        const opOrder = existing.operations.map(op => op.id);
        for (const csvOp of imp.operations) {
          if (opMap.has(csvOp.id)) {
            // Refresh only the fields this CSV row carried (est time always; machines/schedule
            // when present). Planning state the CSV didn't specify is left untouched.
            opMap.set(csvOp.id, { ...opMap.get(csvOp.id), ...csvOpFields(csvOp) });
          } else {
            opMap.set(csvOp.id, newOperationFromCsv(csvOp));
            opOrder.push(csvOp.id);
          }
        }
        map.set(imp.id, { ...existing, ...woUpdates, operations: opOrder.map(id => opMap.get(id)) });
      }
      return order.map(id => map.get(id));
    });
  }
  function handleReleaseToShopfloor() {
    setWorkOrders(prev => prev.map(wo => ({
      ...wo, operations: wo.operations.map(op => op.status === "Planned" ? { ...op, status: "Not started" } : op)
    })));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f8f8f8" }}>
      <FlowSwitcher flow={flow} onChange={(f) => navigate(f === "q3" ? "/q3" : "/")} />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div style={{ width: 70, background: "#000", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
          {/* Company logo (FourJaw mark), centred in the 60px header */}
          <div style={{ height: 60, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="19.78" height="24" viewBox="0 0 19.7754 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="FourJaw">
              <path d="M9.8877 24H5.18359V15.2842L5.86914 14.5908H9.8877V24ZM19.7754 19.7754H15.0713V15.7764L15.7607 15.0713H19.7754V19.7754ZM14.5918 13.8857L13.8955 14.5918H9.88867V5.18457H14.5918V13.8857ZM4.7041 0V13.4229L4.00781 14.1123H0V0H4.7041Z" fill="#fff" />
            </svg>
          </div>
          {/* Top nav — these items are intentionally non-functional in the prototype. */}
          {TOP_NAV.map(item => (
            <SideMenuItem key={item.id} item={item} active={page === item.id}
              onClick={() => { if (!DISABLED_NAV.includes(item.id)) setPage(item.id); }} />
          ))}
          {/* Spacer pushes the bottom group down */}
          <div style={{ flex: 1 }} />
          {/* Bottom nav */}
          {bottomNav.map(item => (
            <SideMenuItem key={item.id} item={item} active={page === item.id}
              onClick={() => { if (!DISABLED_NAV.includes(item.id)) setPage(item.id); }} />
          ))}
        </div>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {page === "dashboards" && <DashboardPage />}
          {page === "workorders" && (isQ3
            ? <WorkOrdersPageQ3 workOrders={workOrders} onUpdateWo={handleUpdateWo} onUpdateOp={handleUpdateOp} onImport={handleImportWorkOrders} />
            : <WorkOrdersPage workOrders={workOrders} onUpdateWo={handleUpdateWo} onImport={handleImportWorkOrders} />)}
          {page === "shopfloor" && <ShopfloorPage workOrders={workOrders} onUpdateOp={handleUpdateOp} activeJobs={activeJobs} setActiveJobs={setActiveJobs} prodCountingEnabled={prodCountingEnabled} prodCountingMachines={prodCountingMachines} />}
          {page === "planning" && <PlanningPage flow={flow} workOrders={workOrders} onAssignOp={handleAssignOp} onUnassignOp={handleUnassignOp} onReleaseToShopfloor={handleReleaseToShopfloor} onUpdateOp={handleUpdateOp} prodWindows={prodWindows} setProdWindows={setProdWindows} downtimes={downtimes} setDowntimes={setDowntimes} shifts={shifts} setShifts={setShifts} />}
          {page === "reports"  && <PlaceholderPage title="Reports" />}
          {page === "settings" && <SettingsPage prodCountingEnabled={prodCountingEnabled} prodCountingMachines={prodCountingMachines}
            onSaveProdCounting={(enabled, machines) => { setProdCountingEnabled(enabled); setProdCountingMachines(new Set(machines)); }} />}
          {page === "account"  && <PlaceholderPage title="Account" />}
          {page === "help"     && <PlaceholderPage title="Help" />}
        </div>
      </div>
    </div>
  );
}
